import { addSerialNumberToCsv } from "../lib/csv";
import { formatDateValue, formatReportValue } from "../lib/dateFormat";
import DateInput from "../components/DateInput";
import { createPortal } from "react-dom";
import React from "react";
import QuickAccessPanel from "../components/QuickAccessPanel";
import { FavoriteButton, FavoriteAnalyticsShell } from "../components/AnalyticsFavorites";
import analyticsCatalogue from "../lib/analyticsSearchCatalogue.json";
import { useAnalyticsSearchTarget } from "../lib/analyticsSearch";
import { Fragment, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import WidgetRenderer, { WidgetLoadingState } from "../components/WidgetRenderer";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { useAuth } from "../lib/auth";
import { isRestricted, CEO_TAB_INFO } from "../lib/screens";
import { normalizeFilterSearch } from "../lib/filterSearch";
import { projectRows, isFieldNotAvailable } from "../lib/rowMapping";
import { api } from "../lib/api";
import { MasterSearchPanel } from "./MasterSearchDetail";
import { downloadCsv } from "../lib/csv";
import { setAskAiContext, ceoFiltersToAskAiContext, openAskAi } from "../lib/askAiContext";
import {
  Crown,
  ShoppingCart,
  Factory,
  Warehouse,
  Truck,
  DollarSign,
  Search,
  Brain,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  FlaskConical,
  Loader2,
  Eye,
  Rocket,
  LayoutDashboard,
  Download,
  X
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

const analyticsById = Object.fromEntries(analyticsCatalogue.map(entry => [entry.id, entry]));
export const CeoSalesFilterContext = React.createContext(null);

const TABS = [
  { id: "quick-access", label: "Quick Access" },
  { id: "overview", label: "CEO Overview" },
  { id: "buyer", label: "Buyer & Sales" },
  { id: "purchase", label: "Purchase / Procurement" },
  { id: "commercial", label: "Commercial" },
  { id: "po", label: "PO Fulfilment" },
  { id: "store", label: "Store / Cold Store" },
  { id: "production", label: "Production" },
  { id: "shipment", label: "Shipment / Dispatch" },
  { id: "qc", label: "QC" },
  { id: "comparisons", label: "Comparisons" },
  // Temporarily hide Master Search from CEO Command Center navigation.
  // { id: "master", label: "Master Search" },
  { id: "ai", label: "AI Ask" },
];

const CEO_AI_EXAMPLES = [
  "Create a CEO dashboard for buyer wise sales, stock availability, PO fulfilment, production received quantity, shipment trend, and QC table.",
  "Create a dashboard showing buyer wise sales, destination wise sales, grade wise sales, and invoice level details.",
  "Create a stock availability dashboard by cold store, grade, production date, and barcode stock register.",
  "Create a production dashboard showing plant wise received quantity, finished variety freezing quantity, VA quantity, and yield details.",
  "Create a shipment dashboard showing shipment trend, stock-out by cold store, shipment details, and PO linked dispatch quantity.",
  "Create a QC dashboard showing sample number, sample location, product type, testing lab, date, and linked invoice IDs.",
  "Compare sales value, ordered MCS, production quantity, stock availability, and shipment quantity for the selected period.",
];

export const DS = {
  sales: "ds-camaroo-qa-sales",
  shipmentSales: "ds-camaroo-qa-shipment-sales",
  msSales: "ds-camaroo-qa-ms-sales",
  msSalesPoTrace: "ds-camaroo-qa-ms-sales-po-trace",
  msPurchasePreprocessing: "ds-camaroo-qa-ms-purchase-preprocessing",
  salesInvoices: "ds-camaroo-qa-sales-invoices",
  consignees: "ds-camaroo-qa-consignees",
  totalSalesUsd: "ds-camaroo-qa-total-sales-usd",
  payments: "ds-camaroo-qa-payments",
  po: "ds-camaroo-qa-po",
  stock: "ds-camaroo-qa-stock",
  preprocessing: "ds-camaroo-qa-preprocessing",
  msPreprocessing: "ds-camaroo-qa-ms-preprocessing",
  msStore: "ds-camaroo-qa-ms-store",
  shipment: "ds-camaroo-qa-shipment",
  shipmentOut: "ds-camaroo-qa-shipment-out",
  movement: "ds-camaroo-qa-movement",
  qc: "ds-camaroo-qa-qc-lab",
  rmPurchase: "ds-camaroo-qa-rm-purchase",
  rmArrival: "ds-camaroo-qa-rm-arrivals",
  vaPlant: "ds-camaroo-qa-va-plant",
  vaInput: "ds-camaroo-qa-va-input-erp",
  vaTransfer: "ds-camaroo-qa-va-transfer-erp",
  soakingPlant: "ds-camaroo-qa-soaking-plant",
  freezingType: "ds-camaroo-qa-freezing-type",
  deheading: "ds-camaroo-qa-deheading",
  deheadingOverall: "ds-camaroo-qa-deheading-overall",
  dailyProduction: "ds-camaroo-qa-daily-production",
  inwardReceived: "ds-camaroo-qa-inward-received",
  packingType: "ds-camaroo-qa-packing-type",
  dummyStock: "ds-camaroo-qa-dummy-stock",
  orders: "ds-camaroo-qa-orders",
  realised: "ds-camaroo-qa-realised",
  realisedCy: "ds-camaroo-qa-realised-cy",
};

export const DATE_FIELD_BY_DATASET = {
  [DS.sales]: "InvoiceDate",
  [DS.shipmentSales]: "InvoiceDate",
  [DS.msSales]: "InvoiceDate",
  [DS.msSalesPoTrace]: "InvoiceDate",
  [DS.msPurchasePreprocessing]: "PurchaseDate",
  [DS.salesInvoices]: "InvoiceDate",
  [DS.totalSalesUsd]: "InvoiceDate",
  [DS.realised]: "InvoiceDate",
  [DS.po]: "PoDate",
  [DS.orders]: "PoDate",
  [DS.payments]: "InvoiceDate",
  [DS.preprocessing]: "ArrivalDate",
  [DS.msPreprocessing]: "ArrivalDate",
  [DS.msStore]: "PoDate",
  [DS.shipment]: "ShipmentDate",
  [DS.shipmentOut]: "ShipmentDate",
  [DS.movement]: "TransferDate",
  [DS.qc]: "AnalysisDate",
  [DS.rmPurchase]: "PurchaseDate",
  [DS.rmArrival]: "PurchaseDate",
  [DS.vaPlant]: "ProcessingDate",
  [DS.vaInput]: "ArrivalDate",
  [DS.vaTransfer]: "ArrivalDate",
  [DS.soakingPlant]: "SoakingDate",
  [DS.freezingType]: "ProductionDate",
  // Deheading in the Process tab is reported by the arrival cohort, so the
  // global date range uses the linked raw-material Arrival Date.
  [DS.deheading]: "ArrivalDate",
  [DS.deheadingOverall]: "ArrivalDate",
  [DS.dailyProduction]: "ProductionDate",
  [DS.inwardReceived]: "ProductionDate",
  [DS.packingType]: "ProductionDate",
};

export const FILTER_FIELDS_BY_DATASET = {
  [DS.shipmentSales]: {
    buyer: "BuyerName", consignee: "ConsigneeName", country: "DestinationCountry",
    grade: "Grade", variety: "VarietyShortName", brand: "BrandName", species: "Species",
    productType: "ProductType", treatment: "Treatment", freezingType: "FreezingType",
    exporter: "ExporterID",
  },
  [DS.sales]: {
    buyer: "BuyerName",
    exporter: "ExporterID",
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    variety: "VarietyShortName",
    brand: "BrandName",
    species: "Species",
    productType: "ProductType",
    treatment: "Treatment",
  },
  [DS.msSales]: {
    buyer: "BuyerName",
    exporter: "ExporterID",
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    variety: "VarietyShortName",
    brand: "BrandName",
    species: "Species",
    productType: "ProductType",
    treatment: "Treatment",
  },
  [DS.msSalesPoTrace]: {
    buyer: "BuyerName",
    exporter: "ExporterID",
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    variety: "VarietyShortName",
    brand: "BrandName",
    species: "Species",
    productType: "ProductType",
    treatment: "Treatment",
  },
  [DS.msPreprocessing]: { plant: "PlantName" },
  [DS.msPurchasePreprocessing]: { plant: "ReceivingPlantName" },
  [DS.msStore]: { plant: "PlantName" },
  [DS.po]: {
    buyer: "BuyerName",
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    variety: "Variety",
    species: "Species",
    productType: "ProductType",
    brand: "Brand",
    treatment: "Treatment",
    freezingType: "FreezingType",
  },
  // Cold stores belong to a plant, so a plant filter matches its store name
  // through PLANT_ALIASES.
  [DS.stock]: {
    grade: "Grade",
    variety: "Variety",
    coldStore: "ColdStore",
    plant: "Plant",
    treatment: "Treatment",
  },
  [DS.preprocessing]: {
    plant: "ProductionPlant",
    variety: "FinishedVarieties",
  },
  [DS.inwardReceived]: {
    grade: "Grade",
    variety: "Variety",
    productType: "ProductType",
    coldStore: "ColdStore",
    plant: "Plant",
  },
  [DS.shipment]: {
    grade: "Grade",
    coldStore: "ColdStore",
    plant: "ColdStore",
    treatment: "Treatment",
  },
  [DS.shipmentOut]: {
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    coldStore: "ColdStore",
    plant: "ColdStore",
    treatment: "Treatment",
  },
  [DS.orders]: {
    consignee: "ConsigneeName",
    country: "DestinationCountry",
    grade: "Grade",
    variety: "Variety",
    brand: "BrandName",
  },
  [DS.payments]: {
    buyer: "BuyerName",
  },
  [DS.realised]: {
    consignee: "ConsigneeName",
    country: "DestinationCountry",
  },
  [DS.realisedCy]: {
    consignee: "ConsigneeName",
    country: "DestinationCountry",
  },
  [DS.consignees]: {
    consignee: "ConsigneeName",
  },
  [DS.movement]: {
    plant: "FromPlant",
  },
  [DS.qc]: {
    plant: "Plant",
  },
  [DS.rmArrival]: {
    plant: "Centre",
    variety: "Variety",
  },
  [DS.rmPurchase]: {
    plant: "ProcessingPlant",
    variety: "Variety",
  },
  // VA / soaking use their own variety names (RPDTO, CPDTO...); the global
  // Variety filter matches their base product (PDTO) instead.
  [DS.vaPlant]: {
    plant: "Plant",
    grade: "Grade",
    variety: "ProductVariety",
  },
  [DS.vaInput]: {
    plant: "Plant",
  },
  [DS.vaTransfer]: {
    plant: "ArrivalPlant",
  },
  [DS.soakingPlant]: {
    plant: "Plant",
    grade: "Grade",
    variety: "ProductVariety",
  },
  [DS.freezingType]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
    freezingType: "FreezingType",
  },
  [DS.deheading]: {
    plant: "Plant",
  },
  [DS.deheadingOverall]: {
    plant: "Plant",
  },
  [DS.dailyProduction]: {
    plant: "Plant",
    brand: "BrandName",
    grade: "Grade",
    variety: "Variety",
    species: "Species",
    productType: "TypeProduct",
    treatment: "Treatment",
    freezingType: "FreezingType",
  },
  [DS.packingType]: {
    plant: "Plant",
    grade: "Grade",
    variety: "Variety",
  },
  [DS.dummyStock]: {
    plant: "Plant",
    grade: "Grade",
    buyer: "BuyerName",
    consignee: "ConsigneeName",
  },
};

function currentFinancialYearRange() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    from: `${year}-04-01`,
    to: `${year + 1}-03-31`,
  };
}

function currentCalendarYearRange() {
  const year = new Date().getFullYear();
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

function previousCalendarYearRange(offset = 1) {
  const year = new Date().getFullYear() - offset;
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

// Plant name -> other ERP spellings of the same plant (cold-store name, plant
// code). Filled from /ceo/filter-options so a plant filter matches datasets
// keyed by station name ("DFL UNIT-4 (PND)") and by store name ("DFL U4").
let PLANT_ALIASES = {};
export function setCeoPlantAliases(aliases) { PLANT_ALIASES = aliases || {}; }

const PROCESS_PLANT_DATASETS = new Set([
  DS.rmArrival, DS.rmPurchase,
  DS.preprocessing, DS.msPreprocessing, DS.msStore, DS.vaPlant, DS.soakingPlant, DS.freezingType,
  DS.deheading, DS.deheadingOverall, DS.dailyProduction, DS.packingType,
  DS.dummyStock, DS.movement,
]);

function resolveProcessPlant(datasetId, filters = {}) {
  const plants = filterValues(filters?.plant);
  if (plants.length || !PROCESS_PLANT_DATASETS.has(datasetId)) return filterSelectionOutput(plants);
  const coldStores = filterValues(filters?.coldStore);
  if (!coldStores.length) return "";
  return filterSelectionOutput([...new Set(coldStores.flatMap((coldStore) => {
    const match = Object.entries(PLANT_ALIASES).find(([plant, aliases]) =>
      plant === coldStore || (Array.isArray(aliases) && aliases.includes(coldStore))
    );
    return match ? [match[0]] : [];
  }))]);
}

const CEO_GLOBAL_FILTER_KEYS = [
  "exporter", "buyer", "consignee", "country", "grade", "variety", "species",
  "productType", "treatment", "freezingType", "brand", "coldStore", "plant",
];

function filterValues(value) {
  const values = Array.isArray(value) ? value : value == null || value === "" ? [] : [value];
  return [...new Set(values.map((item) => String(item ?? "").trim()).filter(Boolean))];
}

function filterSelectionOutput(values) {
  const normalized = filterValues(values);
  // Migrate any multi-select values saved by older versions to one value.
  return normalized[0] || "";
}

function normalizeCeoGlobalFilters(filters = {}) {
  const normalized = { ...filters };
  CEO_GLOBAL_FILTER_KEYS.forEach((key) => { normalized[key] = filterSelectionOutput(filters[key]); });
  return normalized;
}

function SearchableYearDropdown({ value, options, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [position, setPosition] = React.useState(null);
  const wrapperRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const years = [...new Set((options || []).map(String))].sort().reverse();
  const filtered = years.filter((year) => year.includes(search.trim()));

  React.useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setPosition({ top: rect.bottom + 3, left: rect.left, width: rect.width });
    };
    const outside = (event) => {
      if (wrapperRef.current?.contains(event.target) || event.target.closest?.(".variety-combobox-options")) return;
      setOpen(false);
      setSearch("");
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", outside);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", outside);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="variety-combobox w-[108px]">
      <input
        ref={inputRef}
        type="text"
        className="variety-combobox-input rounded-lg border px-3 py-1.5 text-[11px] font-semibold"
        style={{ borderColor: "var(--c-border)", background: "var(--c-surface)", color: "var(--c-text)" }}
        role="combobox"
        aria-label="Dummy carton usage year"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="Search year"
        value={open ? search : value}
        onFocus={() => setOpen(true)}
        onClick={() => {
          if (!open) setSearch("");
          setOpen(true);
        }}
        onChange={(event) => {
          setSearch(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
            setSearch("");
          } else if (event.key === "Enter" && filtered.length) {
            event.preventDefault();
            onChange(filtered[0]);
            setOpen(false);
            setSearch("");
          }
        }}
      />
      {open && position && createPortal(
        <div
          className="variety-combobox-options"
          role="listbox"
          style={{ position: "fixed", top: position.top, left: position.left, width: position.width, zIndex: 2147483647 }}
        >
          {filtered.map((year) => (
            <button
              key={year}
              type="button"
              role="option"
              aria-selected={year === value}
              className="variety-combobox-option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(year);
                setSearch("");
                setOpen(false);
              }}
            >
              {year}
            </button>
          ))}
          {!filtered.length && <div className="variety-combobox-empty">No years found</div>}
        </div>,
        document.body
      )}
    </div>
  );
}

export const CEO_FILTERS_KEY = "ceo-command-center-filters";

const DATE_FIELD_BY_WIDGET = {
  "ceo-purchase-lot-count": "ArrivalDate",
};

export function createCeoFilters(widget, filters) {
  const datasetId = widget?.dataset_id;
  const mapped = [];
  const dateField = DATE_FIELD_BY_WIDGET[widget?.id] || DATE_FIELD_BY_DATASET[datasetId];

  if (dateField && (filters.dateFrom || filters.dateTo)) {
    mapped.push({
      field: dateField,
      op: "date_range",
      value: {
        from: filters.dateFrom || undefined,
        to: filters.dateTo || undefined,
      },
    });
  }

  const fieldMap = FILTER_FIELDS_BY_DATASET[datasetId] || {};
  const effectivePlant = datasetId === DS.inwardReceived && filterValues(filters.coldStore).length
    ? ""
    : resolveProcessPlant(datasetId, filters);

  [
    ["buyer", filters.buyer],
    ["exporter", filters.exporter],
    ["consignee", filters.consignee],
    ["country", filters.country],
    ["grade", filters.grade],
    ["variety", filters.variety],
    ["species", filters.species],
    ["productType", filters.productType],
    ["treatment", filters.treatment],
    ["freezingType", filters.freezingType],
    ["brand", filters.brand],
    ["coldStore", filters.coldStore],
    ["plant", effectivePlant],
  ].forEach(([key, value]) => {
    const field = fieldMap[key];
    const selectedValues = key === "plant" && datasetId
      ? filterValues(effectivePlant)
      : filterValues(value);
    if (field && selectedValues.length) {
      const values = key === "plant"
        ? [...new Set(selectedValues.flatMap((plant) => [plant, ...(PLANT_ALIASES[plant] || [])]))]
        : key === "variety" && datasetId === DS.shipmentSales
          ? [...new Set(selectedValues.flatMap((variety) => /^C(HLSO|PD|PDTO|PUD)$/i.test(variety)
            ? [variety, variety.slice(1)] : [variety]))]
        : selectedValues;
      mapped.push({ field, op: "in", value: values });
    }
  });

  return mapped;
}

const CHART_TYPES = ["bar", "line", "pie", "donut"];
const CHART_CATEGORY_LIMIT = 200; // every category is returned; the renderer scales/scrolls

function makeWidget(id, type, title, dataset_id, extra = {}) {
  const shipmentSales = dataset_id === DS.sales && (
    extra.measure === "LineValueUSD" || extra.selected_columns?.includes("LineValueUSD")
  );
  const widget = {
    id,
    type,
    title,
    dataset_id: shipmentSales ? DS.shipmentSales : dataset_id,
    limit: 20,
    filters: [],
    ...extra,
  };
  // A chart is only capped when it is explicitly a "Top N" chart.
  if (CHART_TYPES.includes(type) && !/\btop\s*-?\s*\d+/i.test(title)) {
    widget.limit = Math.max(Number(widget.limit) || 0, CHART_CATEGORY_LIMIT);
  }
  // Trends plot the latest dates of the selected period in date order (the
  // renderer rolls long spans up to months). Without a dimension sort the
  // server keeps the highest-VALUE dates, which left irregular gaps.
  if (type === "line") {
    widget.limit = 1000; // server cap for grouped queries
    widget.sort = { by: "dimension", dir: "desc" };
  }
  if (shipmentSales && type !== "table") widget.limit = 10000;
  if (shipmentSales && type === "contribution-list") {
    widget.include_non_positive = true;
    widget.exclude_unassigned = false;
  }
  return widget;
}

export const W = {
  purchaseWeight: { id: "ceo-parity-rm_purchase_weight", type: "ceo-parity-kpi", title: "RM Purchase Weight", metric: "rm_purchase_weight", filters: [] },
  purchaseProductionWeight: makeWidget("ceo-purchase-production-weight", "kpi", "RM Production Weight", DS.rmPurchase, {
    measure: "ProductionWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  purchaseLotCount: makeWidget("ceo-purchase-lot-count", "kpi", "RM Lots", DS.rmPurchase, {
    measure: "LotRecordID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  purchaseSupplierWeight: makeWidget("ceo-purchase-supplier-weight", "bar", "Supplier-wise RM Purchase", DS.rmArrival, {
    dimension: "SupplierLabel",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 10000,
    orientation: "vertical",
  }),
  purchaseCentreWeight: makeWidget("ceo-purchase-centre-weight", "bar", "Centre-wise RM Purchase", DS.rmArrival, {
    dimension: "Centre",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 15,
  }),
  purchaseStationWeight: makeWidget("ceo-purchase-station-weight", "bar", "Purchase Station-wise RM Purchase", DS.rmArrival, {
    dimension: "PurchaseStationLabel",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 15,
    orientation: "vertical",
  }),

  purchaseTrend: makeWidget("ceo-purchase-trend", "line", "RM Purchase Weight Trend", DS.rmArrival, {
    dimension: "PurchaseDate",
    measure: "PurchaseWeight",
    aggregation: "sum",
    limit: 60,
    sort: { by: "dimension", dir: "desc" },
  }),
  purchaseLotStatus: makeWidget("ceo-purchase-lot-status", "donut", "RM Lot Status", DS.rmPurchase, {
    dimension: "LotStatus",
    measure: "LotRecordID",
    aggregation: "count_distinct",
    limit: 10,
  }),
  rmRequirementGap: {
    id: "ceo-rm-requirement-gap",
    type: "ceo-rm-requirement-gap",
    title: "RM Requirement & Procurement Gap",
    limit: 1,
    filters: [],
  },
  crossModuleLineage: {
    id: "ceo-cross-module-lineage",
    type: "ceo-client-closure-status",
    title: "Cross-Module Record Lineage",
    endpoint: "/ai/ceo-chat/cross-module-lineage",
    limit: 1,
    filters: [],
  },
  documentationStatus: {
    id: "ceo-documentation-status",
    type: "ceo-client-closure-status",
    title: "Order Documentation Completion Status",
    endpoint: "/ai/ceo-chat/documentation-status",
    limit: 1,
    filters: [],
  },

  purchaseTable: makeWidget("ceo-purchase-table", "table", "Raw Material Purchase Details", DS.rmPurchase, {
    selected_columns: [
      "LotNumber",
      "PurchaseDate",
      "SupplierName",
      "SupplierType",
      "SupplierCentre",
      "PurchaseStation",
      "Centre",
      "PurchaseCount",
      "PurchaseWeight",
      "ProductionWeight",
      "LotStatus"
    ],
    limit: 100,
  }),

  commercialSalesUsd: { id: "ceo-parity-total_sales_usd", type: "ceo-parity-kpi", title: "Commercial Sales USD", metric: "total_sales_usd", filters: [] },
  commercialSalesKgs: { id: "ceo-parity-commercial-sales-gross-kgs", type: "ceo-parity-kpi", title: "Commercial Sales Gross KGS", metric: "total_sales_gross_kgs", filters: [] },
  commercialUtilizedValue: makeWidget("ceo-commercial-utilized-value", "kpi", "Utilized Collection Value", DS.payments, { measure: "UtilizedValue", aggregation: "sum", number_format: { decimals: 2 } }),
  commercialBankCharges: makeWidget("ceo-commercial-bank-charges", "kpi", "Bank Charges", DS.payments, { measure: "BankCharges", aggregation: "sum", number_format: { decimals: 2 } }),
  commercialBuyerSales: makeWidget("ceo-commercial-buyer-sales", "bar", "Buyer-wise Commercial Sales", DS.sales, { dimension: "ConsigneeName", measure: "LineValueUSD", aggregation: "sum", limit: 10, orientation: "vertical", axis_labels: { x: "Buyer", y: "Sales Value (USD)" } }),
  commercialDestinationSales: makeWidget("ceo-commercial-destination-sales", "bar", "Destination-wise Commercial Sales", DS.sales, { dimension: "DestinationCountry", measure: "LineValueUSD", aggregation: "sum", limit: 10, orientation: "vertical" }),
  commercialCollectionRate: makeWidget("ceo-commercial-collection-rate", "bar", "Collection Rate by Invoice", DS.payments, {
    dimension: "InvNumFull",
    measure: "CollectionRate",
    aggregation: "avg",
       orientation: "vertical",
    limit: 10000,
    axis_labels: {
      x: "Invoice Number",
      y: "Collection Rate (%)",
    },
  }),
  commercialVarietyAnalysis: {
    id: "ceo-commercial-variety-analysis",
    type: "ceo-variety-analysis",
    title: "Variety Analysis",
    limit: 10,
    filters: [],
  },
  commercialAntidumping: {
    id: "ceo-antidumping",
    type: "ceo-antidumping",
    title: "Antidumping — Annual Sales Contribution",
    limit: 10,
    filters: [],
  },
  annualCountryContribution: {
    id: "ceo-annual-country-contribution",
    type: "ceo-annual-country-contribution",
    title: "Annual % Contribution — USA, China, Russia & Belgium",
    limit: 10,
    filters: [],
  },
  commercialPaymentsReceivedPending: {id:"ceo-payments-received-pending",type:"ceo-payments-received-pending",title:"Payments Received / Pending",dataset_id:DS.payments,limit:1,filters:[]},
  commercialPendingShipments: { id: "ceo-commercial-pending-shipments", type: "ceo-shipment-pending-risk", title: "Pending Shipments", dataset_id: DS.po, limit: 1, filters: [] },
  commercialTable: makeWidget("ceo-commercial-table", "table", "Commercial Sales Details", DS.sales, { selected_columns: ["InvoiceNumber", "InvoiceDate", "BuyerName", "BuyerCountry", "PoNumber", "PoDate", "BrandName", "Grade", "Variety", "MCS", "InvoiceGrossKGS", "PriceNative", "Currency", "QtyUnit", "Quantity", "LineValueNative", "LineValueUSD", "DestinationCountry", "ExporterName", "ShipmentDate"], limit: 100 }),
  commercialPaymentTable: makeWidget("ceo-commercial-payment-table", "table", "Payment & Collection Details", DS.payments, { selected_columns: ["PoID", "InvNumFull", "PoNo", "PoDate", "CollectionRate", "DueDate", "ETAPOD", "BLDate", "BankCharges", "UtilizedValue", "FirstCollectionDate", "LastCollectionDate", "LastUtilizedDate", "SpotRate"], limit: 100 }),
  totalSalesUsd: { id: "ceo-parity-total_sales_usd", type: "ceo-parity-kpi", title: "Total Sales USD", metric: "total_sales_usd", filters: [] },
  totalSalesKgs: { id: "ceo-parity-total-sales-gross-kgs", type: "ceo-parity-kpi", title: "Total Sales Gross KGS", metric: "total_sales_gross_kgs", filters: [] },
  orderedMcs: { id: "ceo-parity-ordered_mcs", type: "ceo-parity-kpi", title: "Ordered MCS", metric: "ordered_mcs", filters: [] },
  dispatchedMcs: { id: "ceo-parity-dispatched_mcs", type: "ceo-parity-kpi", title: "Dispatched MCS", metric: "dispatched_mcs", filters: [] },
  availableMcs: {
    id: "ceo-parity-available-stock-mcs",
    type: "ceo-parity-kpi",
    title: "Available Stock MCS",
    metric: "available_stock_mcs",
    filters: [],
  },
  availableKgs: {
    id: "ceo-parity-available-stock-kgs",
    type: "ceo-parity-kpi",
    title: "Available Stock KGS",
    metric: "available_stock_kgs",
    filters: [],
  },
  receivedQty: { id: "ceo-parity-received_qty", type: "ceo-parity-kpi", title: "Production Received Qty (MCS)", metric: "received_qty", filters: [] },
  deheadingReceivedWeight: {
    id: "ceo-parity-deheading-input-weight",
    type: "ceo-parity-kpi",
    title: "Deheading Input Weight",
    metric: "deheading_input_weight",
    filters: [],
  },
  deheadingOutputWeight: {
    id: "ceo-parity-weight-after-deheading",
    type: "ceo-parity-kpi",
    title: "Weight After Deheading",
    metric: "weight_after_deheading",
    filters: [],
  },
  deheadingOutputUnit1: {
    id: "ceo-parity-deheading-output-unit1",
    type: "ceo-parity-kpi",
    title: "Deheading Output Unit 1",
    metric: "deheading_output_unit1",
    filters: [],
  },
  looseShellWeight: makeWidget("ceo-loose-shell-weight", "kpi", "Loose Shell Weight", DS.deheading, {
    measure: "LooseShellWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  deheadingByPlant: makeWidget("ceo-deheading-by-plant", "bar", "Deheading Output by Plant", DS.deheadingOverall, {
    dimension: "Plant",
    measure: "WeightAfterDeheading",
    aggregation: "sum",
    limit: 12,
  }),
  deheadingTable: makeWidget("ceo-deheading-table", "table", "ERP Deheading Overall Details", DS.deheadingOverall, {
    selected_columns: ["TrackID", "ArrivalDate", "ProcessingDate", "LotNumber", "Plant", "PurchaseWeight", "WeightAfterDeheading"],
    limit: 100,
  }),

  vaInputWeight: makeWidget("ceo-va-input-weight", "kpi", "VA Input (HL Weight)", DS.vaPlant, {
    measure: "VAInputWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),

  vaTransferWeight: makeWidget("ceo-va-transfer-weight", "kpi", "Sent VA / VA Transfer", DS.vaTransfer, {
    measure: "VATransferWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  vaOutputWeight: makeWidget("ceo-va-output-weight", "kpi", "VA Output Weight", DS.vaPlant, {
    measure: "VAOutputWeight",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  vaPercentByPlant: makeWidget(
    "ceo-va-percent-by-plant",
    "ceo-va-percent-by-plant",
    "Value Addition",
    DS.vaPlant,
    {}
  ),
  vaByPlant: makeWidget("ceo-va-by-plant", "bar", "Value Addition Output by Plant", DS.vaPlant, {
    dimension: "Plant",
    measure: "VAOutputWeight",
    aggregation: "sum",
    limit: 12,
  }),
  vaByVariety: makeWidget("ceo-va-by-variety", "bar", "Value Addition Output by Variety", DS.vaPlant, {
    dimension: "Variety",
    measure: "VAOutputWeight",
    aggregation: "sum",
    limit: 15,
  }),
  vaTable: makeWidget("ceo-va-table", "table", "Value Addition Processing Details", DS.vaPlant, {
    selected_columns: [
      "ArrivalDate",
      "BatchNumber",
      "TrackCode",
      "Plant",
      "ProcessingDate",
      "Shift",
      "Variety",
      "Grade",
      "VAInputWeight",
      "HLCount",
      "VAOutputWeight",
      "VAYieldPct",
      "Remarks",
    ],
    limit: 100,
  }),

  soakInQty: makeWidget("ceo-soak-in-qty", "kpi", "Soaking Input Qty", DS.soakingPlant, {
    measure: "SoakInQty",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  soakOutQty: makeWidget("ceo-soak-out-qty", "kpi", "Soaking Output Qty", DS.soakingPlant, {
    measure: "SoakOutQty",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  soakingByPlant: makeWidget("ceo-soaking-by-plant", "bar", "Soaking Input by Plant", DS.soakingPlant, {
    dimension: "Plant",
    measure: "SoakInQty",
    aggregation: "sum",
    limit: 12,
  }),
  soakingByTreatment: makeWidget("ceo-soaking-by-treatment", "bar", "Soaking Input by Treatment", DS.soakingPlant, {
    dimension: "SoakingTreatment",
    measure: "SoakInQty",
    aggregation: "sum",
    limit: 12,
  }),
  soakingPickupByItemPlant: makeWidget(
    "ceo-soaking-pickup-item-plant",
    "ceo-soaking-pickup-item-plant",
    "Soaking Pickup — Least to Highest by Item & Plant",

    DS.soakingPlant,
    {}
  ),
  soakingTable: makeWidget("ceo-soaking-table", "table", "Soaking Processing Details", DS.soakingPlant, {
    selected_columns: [
      "SoakingRecordID",
      "TrackID",
      "SoakingDate",
      "Plant",
      "Grade",
      "Variety",
      "SoakingTreatment",
      "SoakInQty",
      "SoakOutQty",
    ],
    limit: 100,
  }),

  freezingProductionWeight: { id: "ceo-parity-freezing_weight", type: "ceo-parity-kpi", title: "Freezing Production Weight", metric: "freezing_weight", filters: [] },
  freezingRejectionWeight: { id: "ceo-parity-freezing-rejection-weight", type: "ceo-parity-kpi", title: "Freezing Rejection Weight", metric: "freezing_rejection_weight", filters: [] },
  freezingByType: makeWidget("ceo-freezing-by-type", "bar", "Production by Freezing Type", DS.freezingType, {
    dimension: "FreezingType",
    measure: "ProductionWeight",
    aggregation: "sum",
    limit: 12,
  }),
  freezingByPlant: makeWidget("ceo-freezing-by-plant", "bar", "Freezing Production by Plant", DS.freezingType, {
    dimension: "Plant",
    measure: "ProductionWeight",
    aggregation: "sum",
    limit: 12,
  }),
  freezingTable: makeWidget("ceo-freezing-table", "table", "Freezing Processing Details", DS.freezingType, {
    selected_columns: [
      "FreezingRecordID",
      "TrackID",
      "ProductionDate",
      "Plant",
      "FreezingType",
      "Grade",
      "TypeOfMaterial",
      "Variety",
      "ShiftID",
      "StartTime",
      "EndTime",
      "ProductionWeight",
      "RejectionWeight",
    ],
    limit: 100,
  }),

  dailyActualMcs: { id: "ceo-parity-packed_mcs", type: "ceo-parity-kpi", title: "Daily Production MCS", metric: "packed_mcs", filters: [] },
  dailyActualKgs: makeWidget("ceo-daily-actual-kgs", "kpi", "Daily Production KGS", DS.dailyProduction, {
    measure: "ActualKGS",
    aggregation: "sum",
    number_format: { decimals: 0 },
  }),
  productionByPlantValidated: makeWidget("ceo-production-by-plant-validated", "bar", "Daily Production by Plant", DS.dailyProduction, {
    dimension: "Plant",
    measure: "ActualMCS",
    aggregation: "sum",
    limit: 12,
  }),
  productionByFreezingType: makeWidget("ceo-production-by-freezing-type", "bar", "Daily Production MCS by Freezing Type", DS.dailyProduction, {
    dimension: "FreezingType",
    measure: "ActualMCS",
    aggregation: "sum",
    limit: 12,
  }),
  dailyProductionTable: makeWidget("ceo-daily-production-table", "table", "Daily Production & Packing Details", DS.dailyProduction, {
    selected_columns: [
      "ProductionRecordID",
      "ProductionDate",
      "Plant",
      "Grade",
      "Variety",
      "TypeProduct",
      "FreezingType",
      "PoID",
      "ProductionType",
      "ActualMCS",
      "AvailableMCS",
      "ActualKGS",
      "AvailableKGS",
    ],
    limit: 100,
  }),

  dummyMcs: makeWidget("ceo-dummy-mcs", "kpi", "Dummy Packed MCS", DS.packingType, {
    measure: "DummyMCS",
    aggregation: "sum",
    // Keep fractional cartons from the Inward Report visible (for example,
    // 2 loose bags / 16 slabs = 0.125 MCS), just as they appear in the
    // matching detailed report.
    number_format: { decimals: 3 },
  }),
  masterMcs: makeWidget("ceo-master-mcs", "kpi", "Master Packed MCS", DS.packingType, {
    measure: "MasterMCS",
    aggregation: "sum",
    number_format: { decimals: 3 },
  }),
  packingTypeDistribution: makeWidget("ceo-packing-type-distribution", "donut", "Dummy vs Master Packing", DS.packingType, {
    dimension: "PackingType",
    measure: "PackedMCS",
    aggregation: "sum",
    limit: 6,
  }),
  dummyCartonUsageByPeriod: {
    id: "ceo-dummy-carton-usage-by-period",
    type: "ceo-dummy-carton-usage-by-period",
    title: "Dummy Carton Usage by Plant — Week / Month",
    dataset_id: DS.packingType,
    limit: 12,
    filters: [],
  },
  packingByPlant: makeWidget("ceo-packing-by-plant", "bar", "Packed MCS by Plant", DS.packingType, {
    dimension: "Plant",
    measure: "InwardMCS",
    aggregation: "sum",
    limit: 12,
  }),
  packingTable: makeWidget("ceo-packing-table", "table", "Dummy / Master Packing Details", DS.packingType, {
    selected_columns: [
      "ProductionRecordID",
      "ProductionDate",
      "PackingType",
      "Plant",
      "Grade",
      "Variety",
      "FreezingType",
      "PoID",
      "ActualMCS",
      "AvailableMCS",
      "ActualKGS",
      "DummyMCS",
      "MasterMCS",
    ],
    limit: 100,
  }),
  shipmentOutMcs: { id: "ceo-parity-shipment_out_mcs", type: "ceo-parity-kpi", title: "Shipment Out MCS", metric: "shipment_out_mcs", filters: [] },
  shipmentOutKgs: { id: "ceo-parity-shipment_out_kgs", type: "ceo-parity-kpi", title: "Shipment Out KGS", metric: "shipment_out_kgs", filters: [] },

  shipmentDispatchStatus: {
    id: "ceo-shipment-dispatch-status",
    type: "ceo-shipment-dispatch-status",
    title: "Shipment / Dispatch Operational Status",
    limit: 500,
    filters: [],
  },

  shipmentPendingRisk: {
    id: "ceo-shipment-pending-risk",
    type: "ceo-shipment-pending-risk",
    title: "Pending Shipments",
    limit: 1,
    filters: [],
  },

  onTimeDeliveries: {
    id: "ceo-on-time-deliveries",
    type: "ceo-on-time-deliveries",
    title: "On-time Deliveries",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  earlyShipments: {
    id: "ceo-early-shipments",
    type: "ceo-early-shipments",
    title: "Early Shipments",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  delayedShipments: {
    id: "ceo-delayed-shipments",
    type: "ceo-delayed-shipments",
    title: "Delayed Shipments",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  onWaterShipments: {
    id: "ceo-on-water-shipments",
    type: "ceo-on-water-shipments",
    title: "On-water Shipments",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  fdaReleaseHold: {
    id: "ceo-fda-release-hold",
    type: "ceo-fda-release-hold",
    title: "FDA Release / Hold",
    dataset_id: DS.shipment,
    limit: 1,
    filters: [],
  },

  salesMcs: { id: "ceo-parity-sales_mcs", type: "ceo-parity-kpi", title: "Sales MCS", metric: "sales_mcs", filters: [] },
  salesInvoiceCount: { id: "ceo-parity-invoices", type: "ceo-parity-kpi", title: "Invoices", metric: "invoices", filters: [] },
  salesConsigneeCount: makeWidget("ceo-sales-consignee-count", "kpi", "Consignees", DS.sales, {
    measure: "ConsigneeID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  dashboardCustomerSales: makeWidget("ceo-dashboard-customer-sales", "bar", "Consignee / Buyer-wise Sales Value", DS.sales, {
    dimension: "ConsigneeName",
    display_dimension: "ConsigneeCode",
    number_format: { prefix: "$", decimals: 0 },
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
    orientation: "buyer-sales",
    axis_labels: {
      x: "Consignee",
      y: "Sales Value (USD)",
    },
  }),
  buyerSales: makeWidget("ceo-buyer-sales", "contribution-list", "Buyer Sales Contribution", DS.sales, {
    dimension: "BuyerName",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 100,
    exclude_unassigned: true,
    entity_label: "Buyer",
  }),
  consigneeSales: makeWidget("ceo-consignee-sales", "contribution-list", "Consignee Sales Contribution", DS.sales, {
    dimension: "ConsigneeName",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 120,
    exclude_unassigned: true,
    entity_label: "Consignee",
  }),
  destinationSales: makeWidget("ceo-destination-sales", "bar", "Destination-wise Sales", DS.sales, {
    dimension: "DestinationCountry",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
  }),
  gradeSales: makeWidget("ceo-grade-sales", "bar", "Grade-wise Sales Contribution", DS.sales, {
    dimension: "Grade",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
  }),
  brandSales: makeWidget("ceo-brand-sales", "bar", "Brand-wise Sales Value", DS.sales, {
    dimension: "BrandName",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 12,
  }),
  varietySales: makeWidget("ceo-variety-sales", "bar", "Variety-wise Sales Value", DS.sales, {
    dimension: "Variety",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 16,
  }),
  salesYearComparison: makeWidget("ceo-sales-year-comparison", "bar", "Year-wise Sales Value", DS.sales, {
    dimension: "FinYear",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 10,
    sort: { by: "dimension", dir: "asc" },
  }),
  salesAnnualContribution: makeWidget(
    "ceo-sales-annual-contribution",
    "sales-annual-contribution",
    "Annual Sales Contribution %",
    DS.sales,
    {
      dimension: "SalesYear",
      measure: "LineValueUSD",
      aggregation: "sum",
      limit: 10,
      sort: { by: "dimension", dir: "asc" },
      number_format: { prefix: "$", decimals: 0 },
    }
  ),
  salesTable: makeWidget("ceo-sales-table", "table", "Invoice / Customer / Sales Details", DS.sales, {
    selected_columns: [
      "InvoiceNumber",
      "InvoiceDate",
      "PoNumber",
      "PoDate",
      "ClientName",
      "ConsigneeCountry",
      "ConsigneeName",
      "BrandName",
      "Grade",
      "Variety",
      "VarietyShortName",
      "Species",
      "ProductType",
      "Treatment",
      "PackingStyle",
      "MCS",
      "LineGrossKGS",
      "InvoiceGrossKGS",
      "PriceNative",
      "Currency",
      "QtyUnit",
      "Quantity",
      "ExporterName",
      "SaleInvoiceLineNumber",
      "LineValueUSD",
      "DestinationCountry",
      "ContainerNo",
      "ShipmentDate",
      "ShippingBillNo",
      "ShippingBillDate",
    ],
    limit: 100,
  }),

  poBrand: makeWidget("ceo-po-brand", "bar", "PO Ordered MCS by Brand", DS.po, {
    dimension: "Brand",
    measure: "OrderedMCS",
    aggregation: "sum",
    limit: 10,
  }),
  poStatus: makeWidget("ceo-po-status", "donut", "PO Status Distribution", DS.po, {
    dimension: "PoStatus",
    measure: "PoID",
    aggregation: "count_distinct",
    limit: 8,
  }),
  poTable: makeWidget("ceo-po-table", "table", "PO Fulfilment Details", DS.po, {
    selected_columns: ["PoNumber", "PoDate", "PoStatus", "Grade", "Variety", "Brand", "DestinationCountry", "OrderedMCS", "RevisedMCS", "DispatchedMCS", "Price"],
    limit: 100,
  }),

  poFulfilmentRisk: {
    id: "ceo-po-fulfilment-risk",
    type: "ceo-po-fulfilment-risk",
    title: "Current Running PO Coverage & Risk",
    limit: 500,
    filters: [],
  },

  poCriticalRisk: {
    id: "ceo-po-critical-risk",
    type: "ceo-po-critical-risk",
    title: "Critical POs <50%",
    limit: 1,
    filters: [],
  },

  poWarningRisk: {
    id: "ceo-po-warning-risk",
    type: "ceo-po-warning-risk",
    title: "Warning POs 50–<90%",
    limit: 1,
    filters: [],
  },

  coldStoreStock: makeWidget("ceo-coldstore-stock", "bar", "Cold Store-wise Available Stock", DS.stock, {
    dimension: "ColdStore",
    measure: "StockMCS",
    aggregation: "sum",
    orientation: "vertical",
    limit: 10,
  }),
  gradeStock: makeWidget("ceo-grade-stock", "bar", "Grade-wise Available Stock", DS.stock, {
    dimension: "Grade",
    measure: "StockMCS",
    aggregation: "sum",
    orientation: "vertical",
    limit: 10,
  }),
  stockAgeing: makeWidget("ceo-stock-ageing", "line", "Stock by Production Date", DS.stock, {
    dimension: "ProductionDate",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 30,
  }),
  stockTable: makeWidget("ceo-stock-table", "table", "Stock Register", DS.stock, {
    selected_columns: ["Barcode", "ProductionDate", "ActualMCS", "AvailableMCS", "StockMCS", "ActualKGS", "AvailableKGS", "Grade", "Variety", "PackingStyle", "Plant", "ColdStore", "RackLocation", "PoID"],
    limit: 100,
  }),
  stockTransfersByPlant: makeWidget("ceo-stock-transfers-by-plant", "bar", "MCS Moved by Plant — Least to Highest", DS.movement, {
    dimension: "FromPlant",
    measure: "MovedMCS",
    aggregation: "sum",
    limit: 12,
    orientation: "vertical",
    sort: { dir: "asc" },
  }),

  dummyStockMcs: makeWidget("ceo-dummy-stock-mcs", "kpi", "PO-linked Stock MCS", DS.dummyStock, {
    measure: "AvailableMCS",
    aggregation: "sum",
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    number_format: { decimals: 0 },
  }),
  dummyStockKgs: makeWidget("ceo-dummy-stock-kgs", "kpi", "PO-linked Stock KGS", DS.dummyStock, {
    measure: "AvailableKGS",
    aggregation: "sum",
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    number_format: { decimals: 0 },
  }),
  poPendingMcsStore: makeWidget("ceo-store-po-pending-mcs", "kpi", "Linked PO Pending MCS", DS.dummyStock, {
    measure: "PoPendingMCSOnce",
    aggregation: "sum",
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    number_format: { decimals: 0 },
  }),
  stockByPackingType: makeWidget("ceo-store-packing-type", "donut", "Stock by Packing Type", DS.dummyStock, {
    dimension: "PackingType",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 8,
  }),
  stockReadyLabAwaiting: makeWidget("ceo-stock-ready-lab-awaiting", "table", "Stock-ready Contracts — Lab Sample Awaiting", DS.dummyStock, {
    selected_columns: [
      "PoNo",
      "ConsigneeName",
      "Plant",
      "Grade",
      "AvailableMCS",
      "AvailableKGS",
      "LabSampleStatus",
    ],
    filters: [
      { field: "IsPoLinked", op: "eq", value: 1 },
      { field: "LabSampleStatus", op: "eq", value: "Awaiting" },
    ],
    limit: 100,
  }),
  stockByContractState: makeWidget("ceo-store-contract-state", "donut", "Stock by Contract State", DS.dummyStock, {
    dimension: "ContractState",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 8,
  }),
  stockByPlant: makeWidget("ceo-store-plant", "bar", "PO-linked Stock by Plant", DS.dummyStock, {
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    dimension: "Plant",
    measure: "AvailableMCS",
    aggregation: "sum",
    orientation: "vertical",
    limit: 12,
  }),
  stockByBuyer: makeWidget("ceo-store-buyer", "bar", "PO-linked Stock by Consignee", DS.dummyStock, {
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    dimension: "ConsigneeName",
    exclude_unassigned: true,
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 15,
    orientation: "vertical",
  }),
  stockByPo: makeWidget("ceo-store-po", "bar", "Stock by PO", DS.dummyStock, {
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    dimension: "PoNo",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 15,
    orientation: "vertical",
  }),
  poLinkedStockTable: makeWidget("ceo-po-linked-stock-table", "table", "PO / Contract-linked Stock Details", DS.dummyStock, {
    filters: [{ field: "IsPoLinked", op: "eq", value: 1 }],
    selected_columns: [
      "ProductionDate",
      "PackingType",
      "Plant",
      "Grade",
      "PoNo",
      "ConsigneeName",
      "ContractState",
      "AvailableMCS",
      "AvailableKGS",
      "PoPendingMCS",
    ],
    limit: 100,
  }),

  plantProduction: makeWidget("ceo-plant-production", "bar", "Plant-wise Received Qty", DS.inwardReceived, {
    dimension: "Plant",
    measure: "ReceivedMCS",
    aggregation: "sum",
    limit: 10,
  }),
  freezingVariety: makeWidget("ceo-freezing-variety", "bar", "Finished Variety-wise Freezing Qty", DS.preprocessing, {
    dimension: "FinishedVarieties",
    measure: "FreezingQty",
    aggregation: "sum",
    limit: 10,
  }),
  vaVariety: makeWidget("ceo-va-variety", "bar", "VA Variety-wise Qty", DS.preprocessing, {
    dimension: "VAVarieties",
    measure: "VAQty",
    aggregation: "sum",
    limit: 10000,
  }),
  productionTable: makeWidget("ceo-production-table", "table", "Production / Preprocessing Details", DS.inwardReceived, {
    selected_columns: ["ProductionRecordID", "ProductionDate", "Plant", "ColdStore", "PackingType", "Grade", "Variety", "ProductType", "PackingStyle", "ReceivedMCS"],
    limit: 100,
  }),
  productionYearComparison: {
    id: "ceo-production-year-comparison",
    type: "ceo-production-year-comparison",
    title: "2–3 Year Capacity & Production Comparison",
    dataset_id: DS.preprocessing,
    limit: 3,
    filters: [],
  },

  shipmentByStore: makeWidget("ceo-shipment-store", "bar", "Shipment Out by Cold Store", DS.shipmentOut, {
    dimension: "ColdStore",
    measure: "ShipmentOutMcs",
    aggregation: "sum",
    limit: 10,
  }),
  shipmentTrend: makeWidget("ceo-shipment-trend", "line", "Shipment Trend", DS.shipmentOut, {
    dimension: "ShipmentDate",
    measure: "ShipmentOutMcs",
    aggregation: "sum",
    limit: 30,
    sort: { by: "dimension", dir: "desc" },
  }),
  salesTrend: makeWidget("ceo-sales-trend", "line", "Sales Value Trend", DS.sales, {
    dimension: "InvoiceDate",
    measure: "LineValueUSD",
    aggregation: "sum",
    limit: 60,
    sort: { by: "dimension", dir: "desc" },
  }),
  poTrend: makeWidget("ceo-po-trend", "line", "PO Ordered MCS Trend", DS.po, {
    dimension: "PoDate",
    measure: "OrderedMCS",
    aggregation: "sum",
    limit: 60,
  }),
  productionTrend: makeWidget("ceo-production-trend", "line", "Production Received Qty Trend", DS.inwardReceived, {
    dimension: "ProductionDate",
    measure: "ReceivedMCS",
    aggregation: "sum",
    limit: 60,
    sort: { by: "dimension", dir: "desc" },
  }),
  stockTrend: makeWidget("ceo-stock-trend", "line", "Available Stock Trend by Production Date", DS.stock, {
    dimension: "ProductionDate",
    measure: "AvailableMCS",
    aggregation: "sum",
    limit: 60,
  }),
  priceComparisonOverYears: {
    id: "ceo-price-comparison-over-years",
    type: "ceo-price-comparison-over-years",
    title: "Price Comparison Over Years",
    dataset_id: DS.po,
    limit: 10,
    filters: [],
  },
  buyerOrderAnalysis: {
    id: "ceo-buyer-order-analysis",
    type: "ceo-buyer-order-analysis",
    title: "Buyer Total Orders vs Confirmed Orders",
    dataset_id: DS.po,
    limit: 10,
    filters: [],
  },

  shipmentTable: makeWidget("ceo-shipment-table", "table", "Shipment Dispatch Details", DS.shipmentOut, {
    selected_columns: [
      "ShipmentDate",
      "PoNo",
      "ConsigneeName",
      "DestinationCountry",
      "Grade",
      "BatchCode",
      "ColdStore",
      "RackLocation",
      "OutMCS",
      "OutActualKGS",
      "LooseBags"
    ],
    limit: 100,
  }),

  // Total QC Tests follows the ERP QC/LAB rule: one QcRmAbReport row = one test.
  // Clear/detected cards below remain lot-level outcome KPIs.
  qcTotalTests: makeWidget("ceo-qc-total-tests", "kpi", "Antibiotic Test Records", DS.qc, {
    measure: "QcRecordID",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  qcClearTests: makeWidget("ceo-qc-clear-tests", "kpi", "Clear Lots", DS.qc, {
    measure: "ClearLotKey",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  qcDetectedTests: makeWidget("ceo-qc-detected-tests", "kpi", "Detected / Failed Lots", DS.qc, {
    measure: "DetectedLotKey",
    aggregation: "count_distinct",
    number_format: { decimals: 0 },
  }),
  qcResultDistribution: makeWidget("ceo-qc-result-distribution", "donut", "QC Result Distribution", DS.qc, {
    dimension: "Result",
    measure: "SampleKey",
    aggregation: "count_distinct",
    limit: 12,
  }),
  qcDetectedByPlant: makeWidget("ceo-qc-detected-by-plant", "bar", "Detected / Failed by Plant", DS.qc, {
    dimension: "Plant",
    measure: "DetectedSampleKey",
    aggregation: "count_distinct",
    limit: 12,
  }),
  qcDetectedByCentre: makeWidget("ceo-qc-detected-by-centre", "bar", "Detected / Failed by Centre", DS.qc, {
    dimension: "Centre",
    measure: "DetectedSampleKey",
    aggregation: "count_distinct",
    limit: 15,
  }),
  qcParameterVolume: makeWidget("ceo-qc-parameter-volume", "bar", "Parameter-wise Test Volume", DS.qc, {
    dimension: "Parameter",
    measure: "SampleKey",
    aggregation: "count_distinct",
    limit: 15,
  }),
  qcExternalResult: makeWidget("ceo-qc-external-result", "donut", "External Lab Result Status", DS.qc, {
    dimension: "ExternalResult",
    measure: "SampleKey",
    aggregation: "count_distinct",
    limit: 12,
  }),
  farmCertificationSummary: {
    id: "ceo-farm-certification-summary",
    type: "ceo-farm-audit-summary",
    title: "Farm Certification Summary",
    limit: 1,
    filters: [],
  },
  farmAuditSchedule: {
    id: "ceo-farm-audit-schedule",
    type: "ceo-farm-audit-schedule",
    title: "Plant Audit Schedule & Certificate Renewals",
    limit: 1,
    filters: [],
  },
  qcTable: makeWidget("ceo-qc-table", "table", "QC / Antibiotic Test Details", DS.qc, {
    selected_columns: [
      "QcRecordID",
      "AnalysisDate",
      "ReportDate",
      "RMLotId",
      "LotNo",
      "Centre",
      "Plant",
      "Result",
      "Parameter",
      "Sulphite",
      "AOZ",
      "SEM",
      "CAP",
      "AHD",
      "AMOZ",
      "AbStatus",
      "TestedBy",
      "AntibioticDetected",
      "TestClear",
    ],
    limit: 100,
  }),
};


function ExecutiveAttentionQueue({ filters }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shipmentNeedsPeriod, setShipmentNeedsPeriod] = useState(false);
  const [attentionExpanded, setAttentionExpanded] = useState(false);

  const attentionRows = attentionExpanded ? rows : rows.slice(0, 5);

  const {
    dateFrom,
    dateTo,
    buyer,
    consignee,
    country,
    grade,
    variety,
    brand,
    coldStore,
    plant,
  } = filters || {};

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setShipmentNeedsPeriod(false);

      try {
        const filterContext = {
          dateFrom,
          dateTo,
          buyer,
          consignee,
          country,
          grade,
          variety,
          brand,
          coldStore,
          plant,
        };

        const qcWidget = {
          ...W.qcTable,
          limit: 50,
          filters: [
            ...(W.qcTable.filters || []),
            {
              field: "AntibioticDetected",
              op: "gt",
              value: 0,
            },
          ],
        };

        const requests = [
          api.post("/ai/ceo-chat/po-fulfilment-risk", {
            from_date: dateFrom || undefined,
            to_date: dateTo || undefined,
            buyer: buyer || undefined,
            consignee: consignee || undefined,
            country: country || undefined,
            plant: plant || undefined,
          }),
          api.post(
            "/widget/data",
            {
              widget: qcWidget,
              filters: createCeoFilters(qcWidget, filterContext),
            },
            { timeout: 35000 }
          ),
        ];

        const hasShipmentPeriod = Boolean(dateFrom && dateTo);

        if (hasShipmentPeriod) {
          requests.push(
            api.post("/ai/ceo-chat/shipment-dispatch-status", {
              from_date: dateFrom,
              to_date: dateTo,
              buyer: buyer || undefined,
              consignee: consignee || undefined,
              country: country || undefined,
            })
          );
        }

        const results = await Promise.allSettled(requests);

        if (cancelled) return;

        const combined = [];

        // ----------------------------------------------------
        // Critical PO queue
        // ----------------------------------------------------
        const poResult =
          results[0].status === "fulfilled"
            ? results[0].value?.data ?? results[0].value
            : null;

        if (poResult?.ok && Array.isArray(poResult?.rows)) {
          poResult.rows
            .filter((row) => String(row?.RiskBand || "").toUpperCase() === "RED")
            .slice(0, 10)
            .forEach((row) => {
              combined.push({
                priority: "Critical",
                rank: 1,
                area: "PO",
                reference: row?.PoNo || row?.PoID || "—",
                issue: "PO coverage below 50%",
                context: [
                  row?.ConsigneeName,
                  row?.CountryName,
                  row?.CoveragePct != null ? `${row.CoveragePct}% coverage` : null,
                ].filter(Boolean).join(" · "),
                status: "Action Required",
              });
            });
        }

        // ----------------------------------------------------
        // QC detected / failed queue
        // ----------------------------------------------------
        const qcResult =
          results[1].status === "fulfilled"
            ? results[1].value?.data ?? results[1].value
            : null;

        const qcColumns = Array.isArray(qcResult?.columns) ? qcResult.columns : [];
        const qcRows = Array.isArray(qcResult?.rows) ? qcResult.rows : [];

        qcRows.slice(0, 10).forEach((raw) => {
          const row = {};
          qcColumns.forEach((column, index) => {
            row[column] = raw?.[index];
          });

          combined.push({
            priority: "Critical",
            rank: 1,
            area: "QA",
            reference: row?.LotNo || row?.QcRecordID || "—",
            issue: "Antibiotic detected / failed",
            context: [
              row?.Plant ? `Plant ${row.Plant}` : null,
              row?.Centre ? `Centre ${row.Centre}` : null,
              row?.Parameter,
              row?.Result || row?.AbStatus,
            ].filter(Boolean).join(" · "),
            status: "QA Attention",
          });
        });

        // ----------------------------------------------------
        // Shipment / dispatch queue
        // ----------------------------------------------------
        if (hasShipmentPeriod) {
          const shipmentResult =
            results[2]?.status === "fulfilled"
              ? results[2].value?.data ?? results[2].value
              : null;

          if (shipmentResult?.ok) {
            (shipmentResult.stockout_pending_rows || [])
              .slice(0, 8)
              .forEach((row) => {
                combined.push({
                  priority: "High",
                  rank: 2,
                  area: "Dispatch",
                  reference: row?.PoNo || row?.PoID || "—",
                  issue: "Stock-out recorded; Shipment Out pending",
                  context: [
                    row?.ConsigneeName,
                    row?.DestinationCountry,
                    row?.StockOutDate ? `Stock Out ${formatDateValue(row.StockOutDate)}` : null,
                  ].filter(Boolean).join(" · "),
                  status: "Dispatch Pending",
                });
              });

            (shipmentResult.pending_rows || [])
              .slice(0, 8)
              .forEach((row) => {
                combined.push({
                  priority: "Warning",
                  rank: 3,
                  area: "Shipment",
                  reference: row?.PoNo || row?.PoID || "—",
                  issue: "Shipment due; StockOutDate not recorded",
                  context: [
                    row?.ConsigneeName,
                    row?.DestinationCountry,
                    row?.ShipByDate ? `Ship By ${formatDateValue(row.ShipByDate)}` : null,
                  ].filter(Boolean).join(" · "),
                  status: "Pending Shipment",
                });
              });
          }
        } else {
          setShipmentNeedsPeriod(true);
        }

        combined.sort((a, b) => a.rank - b.rank);

        setRows(combined.slice(0, 20));

        const successfulSources = results.filter(
          (result) => result.status === "fulfilled"
        ).length;

        if (successfulSources === 0) {
          setError("Validated exception sources could not be loaded.");
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Unable to load executive attention queue."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    dateFrom,
    dateTo,
    buyer,
    consignee,
    country,
    grade,
    variety,
    brand,
    coldStore,
    plant,
  ]);

  const priorityStyle = (priority) => {
    if (priority === "Critical") {
      return { background: "#fee2e2", color: "#991b1b" };
    }
    if (priority === "High") {
      return { background: "#ffedd5", color: "#9a3412" };
    }
    return { background: "#fef3c7", color: "#92400e" };
  };

  return (
    <div className="ceo-attention-queue">
      <div className="ceo-attention-summary">
        <div>
          <strong>{rows.length}</strong>
          <span> priority records surfaced</span>
        </div>
        <div className="ceo-attention-legend">
          <span>PO</span>
          <span>QA</span>
          <span>Dispatch</span>
          <span>Shipment</span>
        </div>
      </div>

      {shipmentNeedsPeriod && (
        <div className="ceo-attention-period-note">
          Select both From Date and To Date to include shipment and dispatch exceptions.
        </div>
      )}

      {loading ? (
        <div className="ceo-attention-state">Loading exceptions...</div>
      ) : error && rows.length === 0 ? (
        <div className="ceo-attention-state">{error}</div>
      ) : rows.length === 0 ? (
        <div className="ceo-attention-state">
          No exceptions found for the current filters.
        </div>
      ) : (
        <div className="ceo-attention-table-wrap">
          <div className="flex justify-end pb-2">
            <button
              type="button"
              onClick={() =>
                downloadCsv(
                  "ceo-executive-attention.csv",
                  ["Priority", "Area", "Reference", "Issue", "Business Context", "Status"],
                  attentionRows.map((row) => [
                    row.priority,
                    row.area,
                    row.reference,
                    row.issue,
                    row.context || "—",
                    row.status,
                  ])
                )
              }
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
              style={{ borderColor: "var(--c-border)", background: "#fff" }}
              title="Download all executive attention rows as CSV"
            >
              Download CSV
            </button>
          </div>


          <table className="ceo-attention-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Area</th>
                <th>Reference</th>
                <th>Issue</th>
                <th>Business Context</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {attentionRows.map((row, index) => (
                <tr key={`${row.area}-${row.reference}-${index}`}>
                  <td>
                    <span
                      className="ceo-attention-priority"
                      style={priorityStyle(row.priority)}
                    >
                      {row.priority}
                    </span>
                  </td>
                  <td className="font-semibold">{row.area}</td>
                  <td className="font-semibold">{row.reference}</td>
                  <td>{row.issue}</td>
                  <td>{row.context || "—"}</td>
                  <td>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {rows.length > 5 && (
            <div className="flex justify-center py-3">
              <button
                type="button"
                onClick={() => setAttentionExpanded((current) => !current)}
                className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                style={{
                  borderColor: "var(--c-border)",
                  background: "#fff",
                }}
              >
                {attentionExpanded
                  ? "Collapse"
                  : `Expand · Show all ${rows.length.toLocaleString()} rows`}
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}


function SalesAnnualContribution({ widget, filters, onDrillDown }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const queryWidget = {
          ...widget,
          type: "bar",
          title: "Year-wise Sales Value",
        };

        const response = await api.post(
          "/widget/data",
          {
            widget: queryWidget,
            filters: createCeoFilters(queryWidget, filters || {}),
          },
          {
            signal: controller.signal,
            timeout: 35000,
          }
        );

        if (!cancelled) {
          setData(response?.data ?? null);
        }
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Unable to calculate annual Sales contribution."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch (_) {
        // noop
      }
    };
  }, [widget, filters]);

  if (loading) {
    return <WidgetLoadingState />;
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center px-4 text-center text-sm text-red-700">
        {error}
      </div>
    );
  }

  const rawRows = Array.isArray(data?.rows) ? data.rows : [];

  const mappedRows = rawRows
    .filter((row) => {
      const label = row?.label;
      return (
        label !== null &&
        label !== undefined &&
        String(label).trim() !== "" &&
        String(label).trim() !== "—" &&
        String(label).trim().toLowerCase() !== "unassigned"
      );
    })
    .map((row) => ({
      year: String(row.label),
      salesValue: Number(row.value || 0),
    }))
    .filter((row) => Number.isFinite(row.salesValue));

  const unresolvedValue = rawRows
    .filter((row) => {
      const label = row?.label;
      return (
        label === null ||
        label === undefined ||
        String(label).trim() === "" ||
        String(label).trim() === "—" ||
        String(label).trim().toLowerCase() === "unassigned"
      );
    })
    .reduce((sum, row) => sum + Number(row?.value || 0), 0);

  const mappedTotal = mappedRows.reduce(
    (sum, row) => sum + row.salesValue,
    0
  );

  const rows = mappedRows.map((row) => ({
    ...row,
    contributionPct:
      mappedTotal > 0 ? (row.salesValue / mappedTotal) * 100 : 0,
  }));

  if (!rows.length) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-500">
        No mapped annual Sales data for the selected filters.
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
          <BarChart
            data={rows}
            margin={{ top: 8, right: 18, left: 0, bottom: 8 }}
            onClick={(state) => {
              if (!onDrillDown) return;
              const row = rows.find((item) => String(item.year) === String(state?.activeLabel));
              if (!row?.year) return;
              onDrillDown({
                widget: { ...widget, type: "bar" },
                point: { label: row.year, value: row.salesValue },
              });
            }}
            style={{ cursor: onDrillDown ? "pointer" : "default" }}
          >
            <CartesianGrid
              strokeDasharray="2 3"
              stroke="#eef2f7"
              vertical={false}
            />

            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: "#374151" }}
            />

            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10.5, fill: "#6b7280" }}
              tickFormatter={(value) => `${Number(value).toFixed(0)}%`}
              width={48}
            />

            <Tooltip
              formatter={(value, name, entry) => {
                const row = entry?.payload || {};
                return [
                  `${Number(value || 0).toFixed(2)}% · $${Number(
                    row.salesValue || 0
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
                  "Contribution",
                ];
              }}
              labelFormatter={(label) => `Sales Year ${label}`}
            />

            <Bar
              dataKey="contributionPct"
              name="Annual Contribution %"
              fill="#2563eb"
              radius={[4, 4, 0, 0]}
              cursor={onDrillDown ? "pointer" : "default"}
              onClick={(entry) => {
                if (!onDrillDown) return;

                const row = entry?.payload || entry;
                if (!row?.year) return;

                onDrillDown({
                  widget: {
                    ...widget,
                    type: "bar",
                  },
                  point: {
                    label: row.year,
                    value: row.salesValue,
                  },
                });
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div
        className="text-[11px] leading-5 mt-2"
        style={{ color: "var(--c-text-muted)" }}
      >
        Contribution by sales year for the selected filters.
        {unresolvedValue > 0
          ? ` $${unresolvedValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} has no sales year and is not included in the percentages.`
          : ""}
      </div>
    </div>
  );
}

function WidgetCard({ widget, filters, compact = false, dummyCartonPeriod, setDummyCartonPeriod, reportColumnSelections, setReportColumnSelections }) {
  const nav = useNavigate();
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");
  const [soakingExpanded, setSoakingExpanded] = useState(false);
  const [poCoverageExpanded, setPoCoverageExpanded] = useState(false);
  const openWidgetDrillDown = async ({ widget: sourceWidget, point, appliedFilters }) => {
    if (!sourceWidget || sourceWidget.type === "table" || sourceWidget.type === "text") return;

    const dim = sourceWidget.group_by || sourceWidget.dimension;
    const clickedLabel = point?.label ?? point?.name;
    const mergedFilters = Array.isArray(appliedFilters)
      ? [...appliedFilters]
      : [...createCeoFilters(sourceWidget, filters || {})];

    if (dim && point?.range) {
      // time-series point (a day, month or year): its own range replaces the
      // page date filter on that field
      for (let i = mergedFilters.length - 1; i >= 0; i -= 1) {
        if (mergedFilters[i].field === dim && mergedFilters[i].op === "date_range") mergedFilters.splice(i, 1);
      }
      mergedFilters.push({ field: dim, op: "date_range", value: point.range });
    } else if (
      dim &&
      clickedLabel !== undefined &&
      clickedLabel !== null &&
      clickedLabel !== "" &&
      clickedLabel !== "Unassigned" &&
      clickedLabel !== "Others"
    ) {
      mergedFilters.push({
        field: dim,
        op: "in",
        value: [clickedLabel],
      });
    }

    const tableWidget = {
      ...sourceWidget,
      type: "table",
      selected_columns: undefined,
      limit: 2000,
      filters: mergedFilters,
    };

    setDrill({
      title: sourceWidget.title || sourceWidget.type,
      dimension: dim,
      clickedLabel,
      metricField: sourceWidget.measure || "",
      metricValue: point?.value,
      aggregation: sourceWidget.aggregation || "sum",
      payload: {
        widget: tableWidget,
        filters: mergedFilters,
      },
    });
    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);

    try {
      const r = await api.post(
        "/widget/data",
        { widget: tableWidget, filters: mergedFilters },
        { timeout: 35000 }
      );
      setDrillData(r.data);
    } catch (e) {
      setDrillError(e?.response?.data?.detail || e.message || "Unable to load detailed data.");
    } finally {
      setDrillLoading(false);
    }
  };

  const openValueAdditionDrillDown = async ({ title, metricField, metricValue, plant }) => {
    const effectiveFilters = plant ? { ...(filters || {}), plant } : (filters || {});
    const mergedFilters = [...createCeoFilters(W.vaTable, effectiveFilters)];
    const tableWidget = { ...W.vaTable, title, type: "table", limit: 2000, filters: mergedFilters };
    setDrill({
      title,
      dimension: plant ? "Plant" : "",
      clickedLabel: plant || "",
      metricField: metricField || "",
      metricValue,
      aggregation: metricField === "YieldPct" ? "ratio" : "sum",
      payload: { widget: tableWidget, filters: mergedFilters },
    });
    setDrillData(null); setDrillError(""); setDrillLoading(true);
    try {
      const r = await api.post("/widget/data", { widget: tableWidget, filters: mergedFilters }, { timeout: 35000 });
      setDrillData(r.data);
    } catch (e) {
      setDrillError(e?.response?.data?.detail || e.message || "Unable to load ERP Value Addition records.");
    } finally { setDrillLoading(false); }
  };

  // Year-based custom charts drill into the invoice-level sales lines.
  const openSalesYearDrill = (year, countries, title) => {
    if (!year) return;
    openWidgetDrillDown({
      widget: {
        id: `${widget.id}-drill`,
        type: "bar",
        title: title || `${widget.title} — ${year}`,
        dataset_id: widget.dataset_id || DS.sales,
        dimension: "InvoiceDate",
        measure: "LineValueUSD",
        aggregation: "sum",
        filters: countries?.length ? [{ field: "DestinationCountry", op: "in", value: countries }] : [],
      },
      point: { label: String(year), range: { from: `${year}-01-01`, to: `${year}-12-31` } },
    });
  };
  const ANTIDUMPING_COUNTRIES = ["U.S.A.", "CHINA", "RUSSIA", "BELGIUM"];
  const COUNTRY_NAMES = { USA: "U.S.A.", China: "CHINA", Russia: "RUSSIA", Belgium: "BELGIUM" };

  const openProcessStage = async (stage, row) => {
    const trackId = row?.ReceivingID;
    if (!trackId) return;

    const stageColumns = {
      grading: ["ReceivingID", "TrackCode", "GradingVarieties"],
      va: ["ReceivingID", "TrackCode", "VAVarieties"],
      finished: ["ReceivingID", "TrackCode", "FinishedVarieties", "FreezingQty"],
      conversion: ["ReceivingID", "TrackCode", "SizeConversionPaths"],
    };

    const stageLabels = {
      grading: "Grading Details",
      va: "Value Addition Details",
      finished: "Finished Product Details",
      conversion: "Head-on → Finished Product Size Conversion",
    };

    const sourceWidget = widget?.dataset_id === DS.preprocessing ? widget : null;
    if (!sourceWidget) return;

    const tableWidget = {
      ...sourceWidget,
      type: "table",
      selected_columns: stageColumns[stage],
      limit: 2000,
    };

    const processFilters = [{
      field: "ReceivingID",
      op: "in",
      value: [trackId],
    }];

    setDrill({
      title: `${stageLabels[stage]} — ${row?.TrackCode || trackId}`,
      dimension: "ReceivingID",
      clickedLabel: String(trackId),
      metricField: "",
      metricValue: null,
      aggregation: "sum",
      payload: {
        widget: tableWidget,
        filters: processFilters,
        directData: stage === "grading" || stage === "va" || stage === "finished" || stage === "conversion",
        processStage: stage,
      },
    });

    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);

    try {
      if (stage === "grading") {
        const r = await api.post(
          "/ai/ceo-chat/process/grading",
          { receiving_id: trackId },
          { timeout: 35000 }
        );
        setDrillData({
          columns: [
            "ReceivingID",
            "LotID",
            "GradingDate",
            "GradingBreakupID",
            "VarietyID",
            "Variety",
            "Species",
            "TypeProduct",
            "PackingGrade",
            "Treatment",
            "HLWeight",
            "HLCount",
            "NoCrates",
            "IsBlock",
          ],
          rows: r.data?.rows || [],
        });
      } else if (stage === "va") {
        const r = await api.post(
          "/ai/ceo-chat/process/va",
          { receiving_id: trackId },
          { timeout: 35000 }
        );
        setDrillData({
          columns: [
            "ReceivingID",
            "VLID",
            "GradingBreakupID",
            "VarietyID",
            "Variety",
            "Species",
            "TypeProduct",
            "SentWeight",
            "WtAftrVA",
            "ProcessingDate",
            "NoofCrates",
          ],
          rows: r.data?.rows || [],
        });
      } else if (stage === "finished") {
        const r = await api.post(
          "/ai/ceo-chat/process/finished",
          { receiving_id: trackId },
          { timeout: 35000 }
        );
        setDrillData({
          columns: [
            "ReceivingID",
            "GradingBreakupID",
            "VarietyID",
            "Variety",
            "Grade",
            "FreezingType",
            "ProductionWeight",
          ],
          rows: r.data?.rows || [],
        });
      } else if (stage === "conversion") {
        const r = await api.post(
          "/ai/ceo-chat/process/conversion",
          { receiving_id: trackId },
          { timeout: 35000 }
        );
        setDrillData({
          columns: [
            "ReceivingID",
            "GradingBreakupID",
            "HeadOnVarietyID",
            "HeadOnVariety",
            "HeadOnPackingGrade",
            "HeadOnHLCount",
            "FinishedVarietyID",
            "FinishedVariety",
            "FinishedGrade",
            "FreezingType",
            "ProductionWeight",
          ],
          rows: r.data?.rows || [],
        });
      } else {
        const r = await api.post(
          "/widget/data",
          { widget: tableWidget, filters: processFilters },
          { timeout: 35000 }
        );
        setDrillData(r.data);
      }
    } catch (e) {
      setDrillError(
        e?.response?.data?.detail ||
          e.message ||
          "Unable to load process stage details."
      );
    } finally {
      setDrillLoading(false);
    }
  };

  const [specialData, setSpecialData] = useState(null);
  const [specialLoading, setSpecialLoading] = useState(false);
  const [dummyCartonYear, setDummyCartonYear] = useState("");
  const dummyCartonAvailableYears = React.useMemo(() => {
    const responseYears = Array.isArray(specialData?.available_years)
      ? specialData.available_years
      : [];
    const periodYears = (specialData?.rows || [])
      .filter((row) => row.period_type === "month")
      .map((row) => String(row.period || "").slice(0, 4));
    return [...new Set([...responseYears, ...periodYears].map(String))]
      .filter((year) => /^\d{4}$/.test(year))
      .sort()
      .reverse();
  }, [specialData]);
  useEffect(() => {
    if (
      widget.type === "ceo-dummy-carton-usage-by-period" &&
      dummyCartonAvailableYears.length &&
      (!dummyCartonYear || !dummyCartonAvailableYears.includes(dummyCartonYear))
    ) {
      setDummyCartonYear(dummyCartonAvailableYears[0]);
    }
  }, [widget.type, dummyCartonAvailableYears, dummyCartonYear]);

  const shipmentPromptnessStatus =
    widget.type === "ceo-early-shipments"
      ? "early"
      : widget.type === "ceo-delayed-shipments"
        ? "delayed"
        : widget.type === "ceo-on-time-deliveries"
          ? "on time"
          : null;

  const isOnWaterShipmentKpi =
    widget.type === "ceo-on-water-shipments";

  const isFdaReleaseHoldKpi =
    widget.type === "ceo-fda-release-hold";

  const isPaymentsReceivedPendingKpi =
    widget.type === "ceo-payments-received-pending";

  const openShipmentPromptnessKpi = () => {
    if (!shipmentPromptnessStatus) return;

    window.dispatchEvent(
      new CustomEvent("ceo-shipment-promptness-drill", {
        detail: {
          status: shipmentPromptnessStatus,
          title: widget.title,
        },
      })
    );
  };

  const openOnWaterShipmentKpi = () => {
    if (!isOnWaterShipmentKpi) return;

    window.dispatchEvent(
      new CustomEvent("ceo-shipment-on-water-drill", {
        detail: {
          title: widget.title,
        },
      })
    );
  };

  const shipmentSpecialClickable =
    Boolean(shipmentPromptnessStatus) ||
    isOnWaterShipmentKpi ||
    isFdaReleaseHoldKpi ||
    isPaymentsReceivedPendingKpi;

  const openPendingShipmentsKpi = () => {
    const columns = [
      "PoID",
      "PoNo",
      "PoDate",
      "ShipByDate",
      "CustomerName",
      "DestinationCountry",
      "ProcessorID",
      "ProcessorName",
      "ShipmentState",
    ];
    const selectedColumns = [
      "PoNo",
      "PoDate",
      "ShipByDate",
      "CustomerName",
      "DestinationCountry",
      "ProcessorName",
      "ShipmentState",
    ];

    setDrill({
      title: "Pending Shipments",
      metricField: "",
      metricValue: specialData?.summary?.pending_shipments ?? 0,
      aggregation: "count",
      payload: {
        directData: true,
        widget: {
          id: "ceo-commercial-pending-shipments-details",
          type: "table",
          title: "Pending Shipments",
          selected_columns: selectedColumns,
        },
      },
    });
    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);

    api.post(
      "/ai/ceo-chat/pending-shipments/detail",
      {
        from_date: filters?.dateFrom || undefined,
        to_date: filters?.dateTo || undefined,
        buyer: filters?.buyer || undefined,
        consignee: filters?.consignee || undefined,
        country: filters?.country || undefined,
      },
      { timeout: 120000 }
    ).then((response) => {
      const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
      setDrillData({
        columns,
        rows: rows.map((row) => columns.map((column) => row?.[column] ?? null)),
      });
    }).catch((error) => {
      setDrillError(
        error?.response?.data?.detail ||
          error?.message ||
          "Pending Shipments details could not be loaded safely."
      );
    }).finally(() => {
      setDrillLoading(false);
    });
  };

  const openPoRiskKpi = (riskBand, title, count) => {
    const columns = [
      "PoNo",
      "PoDate",
      "InvNo",
      "InvDate",
      "ConsigneeName",
      "CountryName",
      "PoStatus",
      "OrderedMCS",
      "AvailableMCSEquivalent",
      "MasterAvailableMCSEquivalent",
      "DummyAvailableMCSEquivalent",
      "CoveragePct",
      "MasterCoveragePct",
      "DummyCoveragePct",
      "RiskBand",
      "MasterRiskBand",
      "DummyRiskBand",
    ];
    const riskRows = (specialData?.rows || []).filter(
      (row) => row?.RiskBand === riskBand
    );

    setDrill({
      title,
      widgetTitle: title,
      metricValue: count,
      metricField: "",
      aggregation: "count",
      payload: {
        directData: true,
        widget: {
          id: `ceo-po-${riskBand.toLowerCase()}-risk-details`,
          type: "table",
          title,
          selected_columns: columns,
        },
      },
    });
    setDrillData({
      columns,
      rows: riskRows.map((row) => columns.map((column) => row?.[column] ?? null)),
    });
    setDrillError("");
    setDrillLoading(false);
  };

  const openShipmentSpecialKpi = () => {
    if (isPaymentsReceivedPendingKpi) {
      const columns = [
        "PoID",
        "PoNo",
        "PoDate",
        "CustomerName",
        "DestinationCountry",
        "CInvNo",
        "CInvDate",
        "PaymentTerms",
        "PaymentType",
        "CollectionStatus",
        "DueDate",
        "TransportedDate",
        "PaymentState",
        "UtilizedValue",
      ];
      const selectedColumns = columns.filter((column) => column !== "PoID");

      setDrill({
        title: "Payments Received / Pending",
        widgetTitle: "Realised Value",
        metricField: "UtilizedValue",
        metricValue: specialData?.summary?.realized_value ?? 0,
        aggregation: "sum",
        payload: {
          directData: true,
          widget: {
            id: "ceo-payments-received-pending-details",
            type: "table",
            title: "Payments Received / Pending",
            selected_columns: selectedColumns,
          },
        },
      });
      setDrillData(null);
      setDrillError("");
      setDrillLoading(true);

      api.post(
        "/ai/ceo-chat/payments-received-pending/detail",
        {
          from_date: filters?.dateFrom || undefined,
          to_date: filters?.dateTo || undefined,
          status: "all",
        },
        { timeout: 120000 }
      ).then((response) => {
        const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        setDrillData({ columns, rows });
      }).catch((error) => {
        setDrillError(
          error?.response?.data?.detail ||
            error?.message ||
            "Payments Received / Pending details could not be loaded safely."
        );
      }).finally(() => {
        setDrillLoading(false);
      });
      return;
    }

    if (isFdaReleaseHoldKpi) {
      const params = new URLSearchParams({ scope: "fda", status: "all" });
      if (filters?.dateFrom) params.set("from", filters.dateFrom);
      if (filters?.dateTo) params.set("to", filters.dateTo);
      nav(`/ceo-command-center/fda-release-hold?${params.toString()}`);
      return;
    }

    if (isOnWaterShipmentKpi) {
      openOnWaterShipmentKpi();
      return;
    }

    openShipmentPromptnessKpi();
  };

  useEffect(() => {
    const specialTypes = [
      "ceo-on-time-deliveries",
      "ceo-early-shipments",
      "ceo-delayed-shipments",
      "ceo-on-water-shipments",
      "ceo-fda-release-hold",
      "ceo-price-comparison-over-years",
      "ceo-buyer-order-analysis",
      "ceo-commercial-pending-shipments","ceo-payments-received-pending","ceo-antidumping",
      "ceo-annual-country-contribution",
      "ceo-production-year-comparison",
      "ceo-rm-requirement-gap",
      "ceo-client-closure-status",
      "ceo-va-percent-by-plant",
      "ceo-soaking-pickup-item-plant",
      "ceo-po-fulfilment-risk",
      "ceo-po-critical-risk",
      "ceo-po-warning-risk",
      "ceo-shipment-dispatch-status",
      "ceo-shipment-pending-risk",
      "ceo-dummy-carton-usage-by-period",
      "ceo-farm-audit-summary",
      "ceo-farm-audit-schedule",
    ];

    if (!specialTypes.includes(widget.type)) return;

    let cancelled = false;

    const load = async () => {
      try {
        setSpecialLoading(true);

        const endpoint =
          [
            "ceo-on-time-deliveries",
            "ceo-early-shipments",
            "ceo-delayed-shipments",
            "ceo-on-water-shipments",
          ].includes(widget.type)
            ? "/ai/ceo-chat/shipment-performance"
            : widget.type === "ceo-fda-release-hold"
                ? "/ai/ceo-chat/fda-release-hold"
                : widget.type === "ceo-price-comparison-over-years"
                  ? "/ai/ceo-chat/price-comparison-over-years"
                  : widget.type === "ceo-buyer-order-analysis"
                    ? "/ai/ceo-chat/buyer-order-analysis"
                    : widget.type === "ceo-commercial-pending-shipments"
                      ? "/ai/ceo-chat/commercial-pending-shipments"
                    : widget.type === "ceo-payments-received-pending"
                      ? "/ai/ceo-chat/payments-received-pending"
                    : widget.type === "ceo-antidumping"
                      ? "/ai/ceo-chat/antidumping"
                    : widget.type === "ceo-annual-country-contribution"
                      ? "/ai/ceo-chat/annual-country-contribution"
                    : widget.type === "ceo-production-year-comparison"
                      ? "/ai/ceo-chat/capacity-comparison"
                    : widget.type === "ceo-dummy-carton-usage-by-period"
                      ? "/ai/ceo-chat/dummy-carton-usage-by-period"
                    : widget.type === "ceo-rm-requirement-gap"
                      ? "/ai/ceo-chat/rm-requirement-gap"
                    : widget.type === "ceo-client-closure-status"
                      ? widget.endpoint
                    : widget.type === "ceo-farm-audit-summary"
                      ? "/ai/ceo-chat/farm-audit-summary"
                    : widget.type === "ceo-farm-audit-schedule"
                      ? "/ai/ceo-chat/farm-audit-schedule"

                    : widget.type === "ceo-va-percent-by-plant"
                      ? "/ai/ceo-chat/va-percent-by-plant"

                    : widget.type === "ceo-soaking-pickup-item-plant"
                      ? "/ai/ceo-chat/soaking-pickup-item-plant"
                    : ["ceo-po-fulfilment-risk", "ceo-po-critical-risk", "ceo-po-warning-risk"].includes(widget.type)
                      ? "/ai/ceo-chat/po-fulfilment-risk"
                    : ["ceo-shipment-dispatch-status", "ceo-shipment-pending-risk"].includes(widget.type)
                      ? "/ai/ceo-chat/shipment-dispatch-status"
                      : "/ai/ceo-chat/on-time-deliveries";

        // Shipment / Dispatch stages no longer wait for a date range: with no
        // CEO date filter the backend reports the current financial year.
        // Global CEO date contract: all special endpoints receive the active period.
        const specialPlant = ["ceo-va-percent-by-plant", "ceo-soaking-pickup-item-plant"].includes(widget.type)
          ? resolveProcessPlant(DS.vaPlant, { plant: filters?.plant || "", coldStore: filters?.coldStore || "" })
          : (filters?.plant || "");
        const payload = widget.type === "ceo-dummy-carton-usage-by-period"
          ? {}
          : {
              from_date: filters?.dateFrom || undefined,
              to_date: filters?.dateTo || undefined,
              plant: specialPlant || undefined,
              cold_store: filters?.coldStore || undefined,
              buyer: filters?.buyer || undefined,
              consignee: filters?.consignee || undefined,
              country: filters?.country || undefined,
              brand: filters?.brand || undefined,
              grade: filters?.grade || undefined,
              variety: filters?.variety || undefined,
              species: filters?.species || undefined,
              product_type: filters?.productType || undefined,
              treatment: filters?.treatment || undefined,
              freezing_type: filters?.freezingType || undefined,
            };

        const response = await api.post(endpoint, payload);
        console.log("CEO SPECIAL API:", widget.type, response);
        const data = response?.data ?? response;
        console.log("CEO SPECIAL DATA:", widget.type, data);

        if (!cancelled) {
          setSpecialData(data);
        }
      } catch (error) {
        console.error(`Failed to load ${widget.title}:`, error);

        if (!cancelled) {
          setSpecialData(null);
        }
      } finally {
        if (!cancelled) {
          setSpecialLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [widget.type, widget.title, widget.endpoint, filters?.dateFrom, filters?.dateTo, filters?.plant, filters?.coldStore, filters?.buyer, filters?.consignee, filters?.country, filters?.brand, filters?.grade, filters?.variety, filters?.species, filters?.productType, filters?.treatment, filters?.freezingType]);

  const isSpecial =
    widget.type === "ceo-on-time-deliveries" ||
    widget.type === "ceo-early-shipments" ||
    widget.type === "ceo-delayed-shipments" ||
    widget.type === "ceo-on-water-shipments" ||
    widget.type === "ceo-fda-release-hold" ||
    widget.type === "ceo-price-comparison-over-years" ||
    widget.type === "ceo-buyer-order-analysis" ||
    widget.type === "ceo-commercial-pending-shipments" ||
    widget.type === "ceo-payments-received-pending" ||
    widget.type === "ceo-antidumping" ||
    widget.type === "ceo-annual-country-contribution" ||
    widget.type === "ceo-production-year-comparison" ||
    widget.type === "ceo-rm-requirement-gap" ||
    widget.type === "ceo-client-closure-status" ||
    widget.type === "ceo-va-percent-by-plant" ||
    widget.type === "ceo-soaking-pickup-item-plant" ||
    widget.type === "ceo-po-fulfilment-risk" ||
    widget.type === "ceo-po-critical-risk" ||
    widget.type === "ceo-po-warning-risk" ||
    widget.type === "ceo-shipment-dispatch-status" ||
    widget.type === "ceo-shipment-pending-risk" ||
    widget.type === "ceo-dummy-carton-usage-by-period" ||
    widget.type === "ceo-farm-audit-summary" ||
    widget.type === "ceo-farm-audit-schedule";

  const [tableExportData, setTableExportData] = useState(null);

  return (
    <div className={`ceo-live-widget ${compact ? "ceo-process-stage-card" : ""}`} data-analytics-target={widget.id || widget.target || widget.analyticsTarget}
                    data-analytics-target-id={widget.id || widget.target || widget.analyticsTarget}
                    data-analytics-target-aliases={[widget.target, widget.analyticsTarget, widget.id].filter(Boolean).join(" ")}>
      <div className={`ceo-live-widget-head ${compact ? "ceo-process-stage-head" : ""}`}>
        <div className="ceo-live-widget-title">{widget.title}</div>
        <FavoriteButton target={widget.id} />
        {widget.type === "table" &&
        tableExportData?.columns?.length &&
        tableExportData?.rows?.length ? (
          <button
            type="button"
            onClick={() =>
              downloadCsv(
                "ceo-command-center-table.csv",
                tableExportData.columns,
                tableExportData.rows
              )
            }
            className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold transition hover:bg-slate-50"
            style={{
              borderColor: "var(--c-border)",
              color: "var(--c-text)",
              background: "#fff",
            }}
            title="Download all table rows as CSV"
          >
            Download CSV
          </button>
        ) : null}
      </div>

      <div
        className={`ceo-live-widget-body ${
          ["bar", "line", "pie", "donut"].includes(widget.type)
            ? "ceo-chart-widget-body"
            : ""
        }`}
      >
        {widget.type === "ceo-po-critical-risk" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.ok && specialData?.summary ? (
            <button
              type="button"
              className="h-full w-full flex flex-col justify-between rounded-xl text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
              title="View critical running PO records"
              onClick={() => openPoRiskKpi(
                "RED",
                "Critical POs <50%",
                specialData.summary.red_pos ?? 0
              )}
            >
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#991b1b" }}>
                  Critical Running POs
                </div>
                <div className="text-3xl font-semibold mt-2" style={{ color: "#b91c1c" }}>
                  {specialData.summary.red_pos ?? 0}
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Coverage below 50% · {specialData.summary.running_pos ?? 0} running POs
              </div>
            </button>
          ) : (
            <div className="text-sm text-slate-500">PO risk could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-po-warning-risk" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.ok && specialData?.summary ? (
            <button
              type="button"
              className="h-full w-full flex flex-col justify-between rounded-xl text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
              title="View warning running PO records"
              onClick={() => openPoRiskKpi(
                "WARNING",
                "Warning POs 50–<90%",
                specialData.summary.warning_pos ?? 0
              )}
            >
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#92400e" }}>
                  Warning Running POs
                </div>
                <div className="text-3xl font-semibold mt-2" style={{ color: "#b45309" }}>
                  {specialData.summary.warning_pos ?? 0}
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Coverage 50–&lt;90% · {specialData.summary.green_pos ?? 0} ready at ≥90%
              </div>
            </button>
          ) : (
            <div className="text-sm text-slate-500">PO risk could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-shipment-pending-risk" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.needs_date_range ? (
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#92400e" }}>
                  Pending Shipments
                </div>
                <div className="text-2xl font-semibold mt-2" style={{ color: "#92400e" }}>
                  Select Period
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Choose From Date and To Date to load the dispatch queue.
              </div>
            </div>
          ) : specialData?.ok && specialData?.summary ? (
            <div
              className="h-full flex flex-col justify-between cursor-pointer rounded-xl transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 -m-2 p-2"
              role="button"
              tabIndex={0}
              title="View pending shipment records"
              onClick={openPendingShipmentsKpi}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPendingShipmentsKpi(); } }}
            >
              <div>
                <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: "#92400e" }}>
                  Pending Shipments
                </div>
                <div className="text-3xl font-semibold mt-2" style={{ color: "#b45309" }}>
                  {specialData.summary.pending_shipments ?? 0}
                </div>
              </div>
              <div className="text-[11px] leading-4 mt-3" style={{ color: "var(--c-text-muted)" }}>
                Ship-by date in selected period with no StockOutDate recorded · click to view
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Shipment status could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-shipment-dispatch-status" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.needs_date_range ? (
            <div
              className="rounded-xl border p-4"
              style={{
                background: "#fffbeb",
                borderColor: "#fde68a",
              }}
            >
              <div className="text-[13px] font-semibold" style={{ color: "#92400e" }}>
                Date range required
              </div>
              <div className="text-[12px] mt-1" style={{ color: "#78350f" }}>
                {specialData.message}
              </div>
            </div>
          ) : specialData?.ok && specialData?.summary ? (
            <div className="space-y-5">

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    style={{ background: "#dcfce7", color: "#166534" }}
                  >
                    Live ERP
                  </span>

                  <span
                    className="text-[11.5px]"
                    style={{ color: "var(--c-text-muted)" }}
                  >
                    {formatDateValue(specialData.date_range?.from_date) || "—"}
                    {" → "}
                    {formatDateValue(specialData.date_range?.to_date) || "—"}
                  </span>
                </div>

                <span
                  className="text-[11px]"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  Executive stage classification does not use raw OutStatus codes
                </span>
              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">

                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#fde68a", background: "#fffbeb" }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{ color: "#92400e" }}
                  >
                    Pending Shipments
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.pending_shipments ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "#78350f" }}
                  >
                    Ship-by date is in the selected period but StockOutDate is not recorded.
                  </div>
                </div>


                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#bfdbfe", background: "#eff6ff" }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{ color: "#1d4ed8" }}
                  >
                    Stock-out Recorded
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.stockout_candidates ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "#1e40af" }}
                  >
                    POs stocked out in the selected period.
                  </div>
                </div>


                <div
                  className="rounded-xl border p-4"
                  style={{
                    borderColor:
                      Number(specialData.summary.stockout_pending || 0) > 0
                        ? "#fecaca"
                        : "#bbf7d0",
                    background:
                      Number(specialData.summary.stockout_pending || 0) > 0
                        ? "#fef2f2"
                        : "#f0fdf4",
                  }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{
                      color:
                        Number(specialData.summary.stockout_pending || 0) > 0
                          ? "#991b1b"
                          : "#166534",
                    }}
                  >
                    Stock-out Pending
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.stockout_pending ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "var(--c-text-muted)" }}
                  >
                    Stock-out recorded but final Shipment Out transaction is not yet present.
                  </div>
                </div>


                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#bbf7d0", background: "#f0fdf4" }}
                >
                  <div
                    className="text-[10.5px] uppercase tracking-wide"
                    style={{ color: "#166534" }}
                  >
                    Shipment Completed
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {specialData.summary.completed_shipments ?? 0}
                  </div>
                  <div
                    className="text-[10.5px] mt-2 leading-4"
                    style={{ color: "#166534" }}
                  >
                    Shipment Out transaction exists with an actual OutDate.
                  </div>
                </div>

              </div>


              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

                <div
                  className="rounded-xl border overflow-hidden"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div
                    className="px-4 py-3 border-b"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-semibold">
                          Pending Shipment Queue
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          POs due in the selected period without a recorded StockOutDate.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          downloadCsv(
                            "ceo-pending-shipment-queue.csv",
                            ["PO", "PO Date", "Ship By", "Consignee", "Destination"],
                            (specialData.pending_rows || []).map((row) => [
                              row.PoNo || row.PoID,
                              row.PoDate || "—",
                              row.ShipByDate || "—",
                              row.ConsigneeName || "—",
                              row.DestinationCountry || "—",
                            ])
                          )
                        }

                        className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                        style={{ borderColor: "var(--c-border)", background: "#fff" }}
                        title="Download all pending shipment rows as CSV"
                      >
                        Download CSV
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-h-[360px]">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-white">



                        <tr
                          className="border-b"
                          style={{ borderColor: "var(--c-border)" }}
                        >
                          {[
                            "PO",
                            "PO Date",
                            "Ship By",
                            "Consignee",
                            "Destination",
                          ].map((h) => (
                            <th
                              key={h}
                              className="text-left px-3 py-2 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {(specialData.pending_rows || []).slice(0, 100).map((row) => (
                          <tr
                            key={row.PoID}
                            className="border-b"
                            style={{ borderColor: "var(--c-border)" }}
                          >
                            <td className="px-3 py-2 whitespace-nowrap font-medium">
                              {row.PoNo || row.PoID}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatDateValue(row.PoDate) || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatDateValue(row.ShipByDate) || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.ConsigneeName || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.DestinationCountry || "—"}
                            </td>
                          </tr>
                        ))}

                        {(specialData.pending_rows || []).length === 0 && (
                          <tr>
                            <td
                              colSpan="5"
                              className="px-4 py-8 text-center"
                              style={{ color: "var(--c-text-muted)" }}
                            >
                              No pending shipment POs found for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>


                <div
                  className="rounded-xl border overflow-hidden"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div
                    className="px-4 py-3 border-b"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[12px] font-semibold">
                          Stock-out Exception Queue
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          StockOutDate exists, but Shipment Out completion has not been recorded.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          downloadCsv(
                            "ceo-stockout-exception-queue.csv",
                            ["PO", "Ship By", "Stock Out", "Consignee", "Destination"],
                            (specialData.stockout_pending_rows || []).map((row) => [
                              row.PoNo || row.PoID,
                              row.ShipByDate || "—",
                              row.StockOutDate || "—",
                              row.ConsigneeName || "—",
                              row.DestinationCountry || "—",
                            ])
                          )
                        }

                        className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                        style={{ borderColor: "var(--c-border)", background: "#fff" }}
                        title="Download all stock-out exception rows as CSV"
                      >
                        Download CSV
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-h-[360px]">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-white">
                        <tr
                          className="border-b"
                          style={{ borderColor: "var(--c-border)" }}
                        >
                          {[
                            "PO",
                            "Ship By",
                            "Stock Out",
                            "Consignee",
                            "Destination",
                          ].map((h) => (
                            <th
                              key={h}
                              className="text-left px-3 py-2 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {(specialData.stockout_pending_rows || [])
                          .slice(0, 100)
                          .map((row) => (
                            <tr
                              key={row.PoID}
                              className="border-b"
                              style={{ borderColor: "var(--c-border)" }}
                            >
                              <td className="px-3 py-2 whitespace-nowrap font-medium">
                                {row.PoNo || row.PoID}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {formatDateValue(row.ShipByDate) || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {formatDateValue(row.StockOutDate) || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.ConsigneeName || "—"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {row.DestinationCountry || "—"}
                              </td>
                            </tr>
                          ))}

                        {(specialData.stockout_pending_rows || []).length === 0 && (
                          <tr>
                            <td
                              colSpan="5"
                              className="px-4 py-8 text-center"
                              style={{ color: "var(--c-text-muted)" }}
                            >
                              No stock-out exceptions found for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>



            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Shipment / Dispatch operational status could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-po-fulfilment-risk" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.ok && specialData?.summary ? (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{ background: "#dcfce7", color: "#166534" }}
                >
                  Live ERP
                </span>
                <span
                  className="text-[11.5px]"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  Current running orders · coverage counts master + dummy packed stock
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  ["Running POs", specialData.summary.running_pos],
                  ["Green ≥90% (all stock)", specialData.summary.green_pos],
                  ["Warning 50–89% (all stock)", specialData.summary.warning_pos],
                  ["Red <50% (all stock)", specialData.summary.red_pos],
                  ["Avg Coverage (all stock)", `${specialData.summary.avg_coverage_pct ?? 0}%`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border p-3"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div
                      className="text-[10.5px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      {label}
                    </div>
                    <div className="text-xl font-semibold mt-1">
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div className="text-[12px] font-semibold mb-3">
                    Coverage from MASTER-packed stock only
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.master_green}</div>
                      <div className="text-[10.5px] text-slate-500">Green</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.master_warning}</div>
                      <div className="text-[10.5px] text-slate-500">Warning</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.master_red}</div>
                      <div className="text-[10.5px] text-slate-500">Red</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.avg_master_pct}%</div>
                      <div className="text-[10.5px] text-slate-500">Average</div>
                    </div>
                  </div>
                </div>

                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div className="text-[12px] font-semibold mb-3">
                    Coverage from DUMMY-packed stock only
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.dummy_green}</div>
                      <div className="text-[10.5px] text-slate-500">Green</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.dummy_warning}</div>
                      <div className="text-[10.5px] text-slate-500">Warning</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.dummy_red}</div>
                      <div className="text-[10.5px] text-slate-500">Red</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{specialData.summary.avg_dummy_pct}%</div>
                      <div className="text-[10.5px] text-slate-500">Average</div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="rounded-xl border overflow-hidden"
                style={{ borderColor: "var(--c-border)" }}
              >
                <div className="px-4 py-3 border-b" style={{ borderColor: "var(--c-border)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[12px] font-semibold">
                        Lowest Coverage / Highest Risk Running POs
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                        Ordered MCS compared with available MCS-equivalent stock. Coverage may exceed 100% because the ERP does not cap over-coverage.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        downloadCsv(
                          "ceo-running-po-coverage-risk.csv",
                          [
                            "PO",
                            "PO Date",
                            "Consignee",
                            "Country",
                            "Ordered MCS",
                            "Available",
                            "Coverage",
                            "Master %",
                            "Dummy %",
                            "Risk",
                          ],
                          (specialData.rows || []).map((row) => [
                            row.PoNo || row.PoID,
                            row.PoDate || "—",
                            row.ConsigneeName || "—",
                            row.CountryName || "—",
                            Number(row.OrderedMCS || 0).toLocaleString(),
                            Number(row.AvailableMCSEquivalent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }),
                            `${row.CoveragePct}%`,
                            `${row.MasterCoveragePct}%`,
                            `${row.DummyCoveragePct}%`,
                            row.RiskBand || "—",
                          ])
                        )
                      }
                      className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                      style={{ borderColor: "var(--c-border)", background: "#fff" }}
                      title="Download all running PO coverage rows as CSV"
                    >
                      Download CSV
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-[11.5px]">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "var(--c-border)" }}>
                        {[
                          "PO",
                          "PO Date",
                          "Consignee",
                          "Country",
                          "Ordered MCS",
                          "Available",
                          "Coverage",
                          "Master %",
                          "Dummy %",
                          "Risk",
                        ].map((h) => (
                          <th key={h} className="text-left px-3 py-2 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(poCoverageExpanded
                        ? (specialData.rows || [])
                        : (specialData.rows || []).slice(0, 5)
                      ).map((row) => (
                        <tr
                          key={row.PoID}
                          className="border-b"
                          style={{ borderColor: "var(--c-border)" }}
                        >
                          <td className="px-3 py-2 whitespace-nowrap font-medium">
                            {row.PoNo || row.PoID}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDateValue(row.PoDate) || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.ConsigneeName || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.CountryName || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{Number(row.OrderedMCS || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{Number(row.AvailableMCSEquivalent || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-semibold">{row.CoveragePct}%</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.MasterCoveragePct}%</td>
                          <td className="px-3 py-2 whitespace-nowrap">{row.DummyCoveragePct}%</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className="px-2 py-1 rounded-full text-[10.5px] font-semibold"
                              style={{
                                background:
                                  row.RiskBand === "GREEN"
                                    ? "#dcfce7"
                                    : row.RiskBand === "WARNING"
                                      ? "#fef3c7"
                                      : "#fee2e2",
                                color:
                                  row.RiskBand === "GREEN"
                                    ? "#166534"
                                    : row.RiskBand === "WARNING"
                                      ? "#92400e"
                                      : "#991b1b",
                              }}
                            >
                              {row.RiskBand}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {(specialData.rows || []).length > 5 && (
                    <div className="flex justify-center py-3">
                      <button
                        type="button"
                        onClick={() => setPoCoverageExpanded((current) => !current)}
                        className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                        style={{
                          borderColor: "var(--c-border)",
                          background: "#fff",
                        }}
                      >
                        {poCoverageExpanded
                          ? "Collapse"
                          : `Expand · Show all ${(specialData.rows || []).length.toLocaleString()} rows`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Current running PO coverage could not be loaded safely.
            </div>
          )
         ) : widget.type === "ceo-client-closure-status" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData && !specialData.publishable ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500 text-center px-4">
              No data to show yet for this section.
            </div>
          ) : specialData ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{
                    background: specialData.publishable ? "#dcfce7" : "#fef3c7",
                    color: specialData.publishable ? "#166534" : "#92400e",
                  }}
                >
                  {specialData.publishable ? "Live ERP" : (specialData.validation_status || "Validation Pending")}
                </span>
                {specialData.formula_version ? (
                  <span className="text-xs text-slate-500">Version {specialData.formula_version}</span>
                ) : null}
              </div>
              <div className="text-sm text-slate-700">{specialData.message}</div>
              {specialData.publishable && Array.isArray(specialData.rows) && specialData.rows.length ? (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        {Object.keys(specialData.rows[0]).filter((key) => !specialData.rows.some((r) => r?.[key] !== null && typeof r?.[key] === "object")).slice(0, 10).map((key) => (
                          <th key={key} className="px-3 py-2 text-left">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {specialData.rows.slice(0, 25).map((row, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          {Object.keys(specialData.rows[0]).filter((key) => !specialData.rows.some((r) => r?.[key] !== null && typeof r?.[key] === "object")).slice(0, 10).map((key) => (
                            <td key={key} className="px-3 py-2 whitespace-nowrap">{String(formatReportValue(row?.[key], key) ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {!specialData.publishable && Array.isArray(specialData.source_tables) && specialData.source_tables.length ? (
                <div className="text-xs text-slate-500">Mapped ERP sources: {specialData.source_tables.join(", ")}</div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-slate-500">Closure mapping could not be loaded safely.</div>
          )
        ) : widget.type === "ceo-rm-requirement-gap" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{
                    background: specialData.publishable ? "#dcfce7" : "#fef3c7",
                    color: specialData.publishable ? "#166534" : "#92400e",
                  }}
                >
                  {specialData.publishable
                    ? "Approved"
                    : "Awaiting business approval"}
                </span>
              </div>

              {!specialData.publishable && (
                <div
                  className="rounded-xl border p-4"
                  style={{
                    background: "#fffbeb",
                    borderColor: "#fde68a",
                  }}
                >
                  <div className="text-[13px] font-semibold" style={{ color: "#92400e" }}>
                    RM requirement vs procurement gap is not available yet
                  </div>
                  <div className="text-[12px] mt-1 leading-5" style={{ color: "#78350f" }}>
                    This figure compares raw material needed for open orders with raw material
                    purchased. It will appear here once the calculation is approved by the business.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              RM requirement mapping is unavailable.
            </div>
          )
        ) : widget.type === "ceo-farm-audit-summary" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.ok && specialData?.data?.summary ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Total Farms", specialData.data.summary.total_farms],
                  ["BAP", specialData.data.summary.bap_farms],
                  ["ASC", specialData.data.summary.asc_farms],
                  ["Non-Certified", specialData.data.summary.non_certified_farms],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border px-3 py-2"
                    style={{
                      borderColor: "var(--c-border)",
                      background: "var(--c-surface)",
                    }}
                  >
                    <div
                      className="text-[10.5px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      {label}
                    </div>
                    <div className="text-2xl font-semibold mt-1">
                      {Number(value || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px]"
                style={{ color: "var(--c-text-muted)" }}
              >
                <span>
                  Non-BAP:{" "}
                  <span className="numeric">
                    {Number(specialData.data.summary.non_bap_farms || 0).toLocaleString()}
                  </span>
                </span>
                <span>
                  Unmarked:{" "}
                  <span className="numeric">
                    {Number(specialData.data.summary.unmarked_farms || 0).toLocaleString()}
                  </span>
                </span>
                {specialData.data.categories_are_non_exclusive && (
                  <span>
                    Certification categories are non-exclusive
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Farm certification summary could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-farm-audit-schedule" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.ok && specialData?.data ? (
            <div className="space-y-4">
              {(() => {
                const data = specialData.data;
                const summary = data.summary || {};
                const plantSummary = data.plant_summary || [];
                const rows = data.rows || [];

                const cards = [
                  ["Total", summary.total],
                  ["Valid", summary.valid],
                  ["Expired", summary.expired],
                  ["Renewal Due", summary.renewal_due],
                  ["Upcoming ≤90d", summary.upcoming_renewal],
                ];

                const exceptionRows = plantSummary.filter(
                  (row) =>
                    Number(row.expired || 0) > 0 ||
                    Number(row.renewal_due || 0) > 0 ||
                    Number(row.upcoming_renewal || 0) > 0
                );

                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {cards.map(([label, value]) => (
                        <div
                          key={label}
                          className="rounded-lg border px-3 py-2"
                          style={{
                            borderColor: "var(--c-border)",
                            background: "var(--c-surface)",
                          }}
                        >
                          <div
                            className="text-[10.5px] uppercase tracking-wide"
                            style={{ color: "var(--c-text-muted)" }}
                          >
                            {label}
                          </div>
                          <div className="text-2xl font-semibold mt-1">
                            {Number(value || 0).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      className="rounded-xl border overflow-hidden"
                      style={{ borderColor: "var(--c-border)" }}
                    >
                      <div
                        className="px-4 py-3 border-b"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <div className="text-[12px] font-semibold">
                          Plant Exceptions
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          Expired certificates and renewals requiring attention.
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr
                              className="border-b"
                              style={{ borderColor: "var(--c-border)" }}
                            >
                              {[
                                "Plant",
                                "Total",
                                "Valid",
                                "Expired",
                                "Renewal Due",
                                "Upcoming ≤90d",
                              ].map((label) => (
                                <th
                                  key={label}
                                  className="px-3 py-2 text-left font-semibold"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  {label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {exceptionRows.map((row, index) => (
                              <tr
                                key={`${row.plant || "plant"}-${index}`}
                                className="border-b"
                                style={{ borderColor: "var(--c-border)" }}
                              >
                                <td className="px-3 py-2 font-medium">
                                  {row.plant || "Unknown plant"}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.total || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.valid || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.expired || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.renewal_due || 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 numeric">
                                  {Number(row.upcoming_renewal || 0).toLocaleString()}
                                </td>
                              </tr>
                            ))}

                            {!exceptionRows.length && (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="px-3 py-4 text-center"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  No plant exceptions found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div
                      className="rounded-xl border overflow-hidden"
                      style={{ borderColor: "var(--c-border)" }}
                    >
                      <div
                        className="px-4 py-3 border-b"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <div className="text-[12px] font-semibold">
                          Audit / Certificate Drill-down
                        </div>
                        <div
                          className="text-[10.5px] mt-1"
                          style={{ color: "var(--c-text-muted)" }}
                        >
                          AuditDate · ValidUpto · RenewalDate · ERP status
                        </div>
                      </div>

                      <div className="max-h-[420px] overflow-auto">
                        <table className="w-full text-[11px]">
                          <thead className="sticky top-0 bg-white">
                            <tr
                              className="border-b"
                              style={{ borderColor: "var(--c-border)" }}
                            >
                              {[
                                "Plant",
                                "Certificate",
                                "Certification Body",
                                "Audit Date",
                                "Valid Upto",
                                "Renewal Date",
                                "Status",
                                "Renewal Status",
                              ].map((label) => (
                                <th
                                  key={label}
                                  className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  {label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, index) => (
                              <tr
                                key={`${row.farm_audit_id || "audit"}-${index}`}
                                className="border-b"
                                style={{ borderColor: "var(--c-border)" }}
                              >
                                <td className="px-3 py-2 font-medium whitespace-nowrap">
                                  {row.plant || "Unknown plant"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.certificate_no || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.certification_body || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {formatDateValue(row.audit_date) || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.valid_upto || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {formatDateValue(row.renewal_date) || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap font-semibold">
                                  {row.status || "—"}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {row.renewal_status || "—"}
                                </td>
                              </tr>
                            ))}

                            {!rows.length && (
                              <tr>
                                <td
                                  colSpan={8}
                                  className="px-3 py-4 text-center"
                                  style={{ color: "var(--c-text-muted)" }}
                                >
                                  No audit/certificate records available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div
                      className="flex flex-wrap gap-x-4 gap-y-1 text-[10.5px]"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      <span>
                        Renewal window: {Number(data.renewal_window_days || 90)} days
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Plant audit schedule and certificate renewals could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-va-percent-by-plant" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.ok && specialData?.data?.rows?.length ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold" style={{ color: "var(--c-text)" }}>
                    ERP Value Addition Report
                  </div>
                  <div className="text-[10.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
                    Period: {specialData.data.period || "all available data"} · Yield = VA Output / HL Input × 100
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { label: "HL Input", value: `${Number(specialData.data.overall_denominator || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg`, metricField: "VAInputWeight", metricValue: specialData.data.overall_denominator, favorite: W.vaInputWeight.id },
                  { label: "VA Output", value: `${Number(specialData.data.overall_numerator || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg`, metricField: "VAOutputWeight", metricValue: specialData.data.overall_numerator, favorite: W.vaOutputWeight.id },
                  { label: "Overall Yield", value: specialData.data.overall_percent != null ? `${Number(specialData.data.overall_percent).toFixed(2)}%` : "—", metricField: "YieldPct", metricValue: specialData.data.overall_percent, favorite: W.vaPercentByPlant.id },
                ].map((item) => (
                  <div key={item.label} role="button" tabIndex={0} data-analytics-target={item.favorite} title="Click to view ERP Value Addition records"
                    onClick={() => openValueAdditionDrillDown({ title: `${item.label} — ERP Value Addition Records`, metricField: item.metricField, metricValue: item.metricValue })}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openValueAdditionDrillDown({ title: `${item.label} — ERP Value Addition Records`, metricField: item.metricField, metricValue: item.metricValue }); } }}
                    className="cursor-pointer rounded-xl border border-slate-200 p-3 transition hover:border-slate-400 hover:shadow-md">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{item.label}</div>
                      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}><FavoriteButton target={item.favorite} /></span>
                    </div>
                    <div className="mt-1 text-xl font-semibold text-slate-900">{item.value}</div>
                    <div className="mt-1 text-[10px] text-slate-400">View supporting ERP records</div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {specialData.data.rows.map((row, index) => {
                  const percent = Number(row.percent);
                  const width = Math.max(0, Math.min(percent, 100));

                  return (
                    <div
                      key={`${row.plant || "plant"}-${index}`}
                      className="space-y-1 cursor-pointer rounded-md hover:bg-slate-50"
                      title="View VA records for this plant"
                      onClick={() =>
                        row.plant &&
                        openValueAdditionDrillDown({
                          title: `Value Addition — ${row.plant}`,
                          metricField: "YieldPct",
                          metricValue: row.percent,
                          plant: row.plant,
                        })
                      }
                    >
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="font-medium">
                          {row.plant || "Unknown plant"}
                        </span>
                        <span className="numeric font-semibold">
                          {Number.isFinite(percent) ? percent.toFixed(2) : "—"}%
                        </span>
                      </div>
                      <div
                        className="h-2 rounded-full overflow-hidden"
                        style={{ background: "var(--c-border)" }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${width}%`,
                            background: "#2563eb",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              Peeling (VA) percentage comparison could not be loaded safely.
            </div>
          )
        ) : widget.type === "ceo-dummy-carton-usage-by-period" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.rows?.length ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex rounded-lg border p-1" style={{ borderColor: "var(--c-border)" }}>
                  {[
                    ["month", "Month"],
                    ["week", "Week"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setDummyCartonPeriod(value)}
                      className="rounded-md px-3 py-1.5 text-[11px] font-semibold"
                      style={{
                        background: dummyCartonPeriod === value ? "#0f62fe" : "transparent",
                        color: dummyCartonPeriod === value ? "#fff" : "var(--c-text-muted)",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <SearchableYearDropdown
                  value={dummyCartonYear}
                  options={dummyCartonAvailableYears}
                  onChange={setDummyCartonYear}
                />
                <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>
                  {specialData.summary?.plants || 0} plants · {dummyCartonPeriod === "month" ? specialData.summary?.months || 0 : specialData.summary?.weeks || 0} periods
                </div>
              </div>

              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
                  <BarChart
                    data={(() => {
                      const filtered = specialData.rows.filter((row) => row.period_type === dummyCartonPeriod && String(row.period || "").slice(0, 4) === dummyCartonYear);
                      const periods = [...new Set(filtered.map((row) => row.period))].sort().reverse().slice(0, 24).reverse();
                      const plants = [...new Set(filtered.map((row) => row.plant))].sort();
                      return periods.map((period) => {
                        const item = { period };
                        plants.forEach((plant) => {
                          const row = filtered.find((entry) => entry.period === period && entry.plant === plant);
                          item[plant] = row?.dummy_mcs || 0;
                        });
                        return item;
                      });
                    })()}
                    margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                    onClick={() => nav("/ceo-command-center/dummy-carton-usage-by-period")}
                    style={{ cursor: "pointer" }}
                  >
                    <CartesianGrid strokeDasharray="2 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={(value) => dummyCartonPeriod === "month" ? new Date(String(value) + "-01T00:00:00").toLocaleString("en-US", { month: "short" }) : `Week ${String(value).slice(4)}`} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => Number(value || 0).toLocaleString()} />
                    <Legend />
                    {[...new Set(specialData.rows.filter((row) => row.period_type === dummyCartonPeriod && String(row.period || "").slice(0, 4) === dummyCartonYear).map((row) => row.plant))].sort().map((plant, plantIndex) => (
                      <Bar key={plant} dataKey={plant} name={plant} cursor="pointer" onClick={() => nav("/ceo-command-center/dummy-carton-usage-by-period")} fill={["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706", "#db2777", "#4f46e5", "#64748b"][plantIndex % 8]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => nav("/ceo-command-center/dummy-carton-usage-by-period")}
                  className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: "var(--c-border)",
                    color: "var(--c-text)",
                  }}
                >
                  View Detail
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Dated MCS", specialData.summary?.dated_dummy_mcs],
                  ["Undated MCS", specialData.summary?.undated_dummy_mcs],
                  ["Total Dummy MCS", specialData.summary?.total_dummy_mcs],
                  ["Validation", specialData.reconciliation?.pass ? "PASS" : "FAIL"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border p-3" style={{ borderColor: "var(--c-border)" }}>
                    <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>{label}</div>
                    <div className="text-lg font-semibold mt-1">{typeof value === "number" ? value.toLocaleString() : value}</div>
                  </div>
                ))}
              </div>

            </div>
          ) : (
            <div className="text-sm text-slate-500">No dummy carton usage comparison data available.</div>
          )

        ) : widget.type === "ceo-soaking-pickup-item-plant" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.data?.rows?.length ? (
            <div className="space-y-3">
              <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--c-border)" }}>
                <div
                  className="px-4 py-3 border-b"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[12px] font-semibold">
                      Soaking Pickup Item / Plant
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        downloadCsv(
                          "ceo-soaking-pickup-item-plant.csv",
                          ["Item", "Plant", "Records", "Soak In", "Soak Out", "Pickup %"],
                          (specialData.data.rows || []).map((row) => [
                            row.type_product && row.type_product !== "UNMAPPED"
                              ? `${row.type_product}-${row.item}`
                              : row.item || "UNMAPPED",
                            row.plant || "—",
                            Number(row.records || 0).toLocaleString(),
                            Number(row.soak_in || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }),
                            Number(row.soak_out || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }),
                            `${Number(row.pickup_percent || 0).toFixed(2)}%`,
                          ])
                        )
                      }
                      className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                      style={{ borderColor: "var(--c-border)", background: "#fff" }}
                      title="Download all soaking pickup rows as CSV"
                    >
                      Download CSV
                    </button>
                  </div>
                </div>

                <table className="min-w-full text-[11.5px]">
                  <thead>
                    <tr style={{ background: "var(--c-surface-muted)" }}>

                      {["Item", "Plant", "Records", "Soak In", "Soak Out", "Pickup %"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 whitespace-nowrap font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(soakingExpanded
                      ? specialData.data.rows
                      : specialData.data.rows.slice(0, 5)
                    ).map((row, index) => (
                      <tr
                        key={`${row.item}-${row.plant_id}-${index}`}
                        className="border-t"
                        style={{ borderColor: "var(--c-border)" }}
                      >
                        <td className="px-3 py-2 whitespace-nowrap font-medium">
                          {row.type_product && row.type_product !== "UNMAPPED"
                            ? `${row.type_product}-${row.item}`
                            : row.item || "UNMAPPED"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.plant || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {Number(row.records || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {Number(row.soak_in || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {Number(row.soak_out || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap font-semibold">
                          {Number(row.pickup_percent || 0).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {specialData.data.rows.length > 5 && (
                  <div className="flex justify-center py-3">
                    <button
                      type="button"
                      onClick={() => setSoakingExpanded((current) => !current)}
                      className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                      style={{
                        borderColor: "var(--c-border)",
                        background: "#fff",
                      }}
                    >
                      {soakingExpanded
                        ? "Collapse"
                        : `Expand · Show all ${specialData.data.rows.length.toLocaleString()} rows`}
                    </button>
                  </div>
                )}
              </div>

            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No valid soaking pickup comparison data available.
            </div>
          )
        ) : widget.type === "ceo-production-year-comparison" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.data?.rows?.length ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
                <BarChart
                  data={specialData.data.rows.filter(
                    (row) => row.production_year != null
                  )}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                  onClick={(state) => {
                    const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
                    const rows = specialData.data.rows.filter((row) => row.production_year != null);
                    const row = Number.isInteger(index) ? rows[index] : rows.find((item) => String(item.production_year) === String(state?.activeLabel));
                    const year = row?.production_year;
                    if (!year) return;
                    openWidgetDrillDown({
                      widget: { id: `${widget.id}-drill`, type: "bar", title: `Production ${year}${row.plant ? ` — ${row.plant}` : ""}`, dataset_id: DS.freezingType, dimension: "ProductionDate", measure: "ProductionWeight", aggregation: "sum", filters: row.plant ? [{ field: "Plant", op: "in", value: [row.plant] }] : [] },
                      point: { label: String(year), value: row.production_kgs, range: { from: `${year}-01-01`, to: `${year}-12-31` } },
                    });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="2 3" />
                  <XAxis
                    dataKey="production_year"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${Number(value || 0).toLocaleString()} KGS`,
                      "Production",
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="production_kgs"
                    name="Production KGS"
                    fill="#2563eb"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(entry) => {
                      const row = entry?.payload || entry;
                      const year = row?.production_year;
                      if (!year) return;
                      openWidgetDrillDown({
                        widget: {
                          id: `${widget.id}-drill`,
                          type: "bar",
                          title: `Production ${year}${row.plant ? ` — ${row.plant}` : ""}`,
                          dataset_id: DS.freezingType,
                          dimension: "ProductionDate",
                          measure: "ProductionWeight",
                          aggregation: "sum",
                          filters: row.plant ? [{ field: "Plant", op: "in", value: [row.plant] }] : [],
                        },
                        point: {
                          label: String(year),
                          value: row.production_kgs,
                          range: { from: `${year}-01-01`, to: `${year}-12-31` },
                        },
                      });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No capacity / production comparison data available
            </div>
          )
        ) : widget.type === "ceo-buyer-order-analysis" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.rows?.length ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ["Total Orders", specialData.summary?.total_orders],
                  ["Confirmed Orders", specialData.summary?.confirmed_orders],
                  ["Not Confirmed", specialData.summary?.non_confirmed_orders],
                  ["Confirmation %", `${specialData.summary?.confirmation_pct ?? 0}%`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border p-3"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div
                      className="text-[10px] uppercase tracking-wide"
                      style={{ color: "var(--c-text-muted)" }}
                    >
                      {label}
                    </div>
                    <div className="text-lg font-semibold mt-1">
                      {typeof value === "number"
                        ? value.toLocaleString()
                        : value}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
                  <BarChart
                    data={specialData.rows.slice(0, 20)}
                    margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                    onClick={(state) => {
                      const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
                      const row = Number.isInteger(index) ? specialData.rows[index] : specialData.rows.find((item) => String(item.buyer_name) === String(state?.activeLabel));
                      if (!row) return;
                      const params = new URLSearchParams();
                      if (row.buyer_name) params.set("buyer", row.buyer_name);
                      if (filters?.dateFrom) params.set("from", filters.dateFrom);
                      if (filters?.dateTo) params.set("to", filters.dateTo);
                      nav(`/ceo-command-center/buyer-order-analysis?${params.toString()}`);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <CartesianGrid strokeDasharray="2 3" vertical={false} />
                    <XAxis
                      dataKey="buyer_name"
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      height={110}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => { const t = String(v ?? ""); return t.length > 24 ? `${t.slice(0, 23)}…` : t; }}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="total_orders"
                      name="Total Orders"
                      cursor="pointer"
                      onClick={(entry) => {
                        const buyer = (entry?.payload || entry)?.buyer_name;
                        const params = new URLSearchParams();
                        if (buyer) params.set("buyer", buyer);
                        if (filters?.dateFrom) params.set("from", filters.dateFrom);
                        if (filters?.dateTo) params.set("to", filters.dateTo);
                        nav(`/ceo-command-center/buyer-order-analysis?${params.toString()}`);
                      }}
                      fill="#64748b"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="confirmed_orders"
                      name="Confirmed Orders"
                      cursor="pointer"
                      onClick={(entry) => {
                        const buyer = (entry?.payload || entry)?.buyer_name;
                        const params = new URLSearchParams();
                        if (buyer) params.set("buyer", buyer);
                        if (filters?.dateFrom) params.set("from", filters.dateFrom);
                        if (filters?.dateTo) params.set("to", filters.dateTo);
                        nav(`/ceo-command-center/buyer-order-analysis?${params.toString()}`);
                      }}
                      fill="#2563eb"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div
                  className="text-[11px] leading-5"
                  style={{ color: "var(--c-text-muted)" }}
                >
                  {Number(specialData.summary?.identified_buyers || 0).toLocaleString()} buyers.
                  Buyer is the consignee on the order, as on the ERP order list.
                </div>

                <button
                  type="button"
                  onClick={() =>
                    nav("/ceo-command-center/buyer-order-analysis")
                  }
                  className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: "var(--c-border)",
                    color: "var(--c-text)",
                  }}
                >
                  View Details
                </button>
              </div>

            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No Buyer order comparison data available.
            </div>
          )
        ) : widget.type === "ceo-price-comparison-over-years" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.rows?.length ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
                <LineChart
                  data={specialData.rows.filter(
                    (row) => row.order_year != null
                  )}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                  onClick={() => nav("/ceo-command-center/price-comparison-over-years")}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="2 3" />
                  <XAxis
                    dataKey="order_year"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="weighted_avg_price"
                    name="Weighted Avg Price"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="min_price"
                    name="Minimum Price"
                    stroke="#16a34a"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="max_price"
                    name="Maximum Price"
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>

                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    {specialData?.summary?.currency || "USD"} / {specialData?.summary?.qty_unit || "LBS"} · ERP weighted selling price
                  </span>
                  <button
                    type="button"
                    onClick={() => nav("/ceo-command-center/price-comparison-over-years")}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                  >
                    View Detail
                  </button>
                </div>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No price comparison data available
            </div>
          )
        ) : widget.type === "ceo-antidumping" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.data?.rows?.length ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
                <BarChart
                  data={specialData.data.rows.filter(
                    (row) => row?.sales_year != null
                  )}
                  margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                  onClick={(state) => {
                    const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
                    const rows = specialData.data.rows.filter((row) => row?.sales_year != null);
                    const row = Number.isInteger(index) ? rows[index] : rows.find((item) => String(item.sales_year) === String(state?.activeLabel));
                    if (row) openSalesYearDrill(row.sales_year, ANTIDUMPING_COUNTRIES, `Antidumping country sales — ${row.sales_year}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="2 3" />
                  <XAxis
                    dataKey="sales_year"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${Number(value || 0).toFixed(2)}%`,
                      "Antidumping Contribution",
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="contribution_pct"
                    name="Antidumping Contribution"
                    fill="#2563eb"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(entry) => {
                      const year = (entry?.payload || entry)?.sales_year;
                      openSalesYearDrill(year, ANTIDUMPING_COUNTRIES, `Antidumping country sales — ${year}`);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No antidumping contribution data available
            </div>
          )
        ) : widget.type === "ceo-annual-country-contribution" ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData?.data?.rows?.length ? (
            <div>
              {specialData?.year_validation?.some((row) => !row.publishable) ? (
                <div
                  className="mb-3 rounded-lg border px-3 py-2 text-xs"
                  style={{
                    background: "#fffbeb",
                    borderColor: "#fde68a",
                    color: "#92400e",
                  }}
                >
                  Historical years with unresolved ERP exchange rates are shown as
                  Business Validation Pending. No missing value is converted to 0%.
                </div>
              ) : null}

              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
                  <BarChart
                    data={Object.values(
                      specialData.data.rows.reduce((acc, row) => {
                        const year = row.sales_year;
                        if (!acc[year]) {
                          acc[year] = {
                            sales_year: year,
                            publishable: row.publishable,
                            validation_status: row.validation_status,
                          };
                        }

                        acc[year][row.country] =
                          row.contribution_pct == null
                            ? null
                            : Number(row.contribution_pct);

                        return acc;
                      }, {})
                    )}
                    margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
                    onClick={(state) => {
                      const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
                      const rows = Object.values(specialData.data.rows.reduce((acc, row) => {
                        const year = row.sales_year;
                        if (!acc[year]) acc[year] = { sales_year: year };
                        acc[year][row.country] = row.contribution_pct == null ? null : Number(row.contribution_pct);
                        return acc;
                      }, {}));
                      const row = Number.isInteger(index) ? rows[index] : rows.find((item) => String(item.sales_year) === String(state?.activeLabel));
                      if (row) openSalesYearDrill(row.sales_year, Object.keys(COUNTRY_NAMES).map((key) => COUNTRY_NAMES[key]), `Country sales — ${row.sales_year}`);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <CartesianGrid strokeDasharray="2 3" />
                    <XAxis
                      dataKey="sales_year"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        value == null
                          ? "Business Validation Pending"
                          : `${Number(value).toFixed(2)}%`,
                        name,
                      ]}
                    />
                    <Legend />

                    <Bar
                      dataKey="USA"
                      name="USA"
                      fill="#2563eb"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(entry) => {
                        const year = (entry?.payload || entry)?.sales_year;
                        openSalesYearDrill(year, [COUNTRY_NAMES["USA"]], `USA sales — ${year}`);
                      }}
                    />
                    <Bar
                      dataKey="China"
                      name="China"
                      fill="#16a34a"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(entry) => {
                        const year = (entry?.payload || entry)?.sales_year;
                        openSalesYearDrill(year, [COUNTRY_NAMES["China"]], `China sales — ${year}`);
                      }}
                    />
                    <Bar
                      dataKey="Russia"
                      name="Russia"
                      fill="#f59e0b"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(entry) => {
                        const year = (entry?.payload || entry)?.sales_year;
                        openSalesYearDrill(year, [COUNTRY_NAMES["Russia"]], `Russia sales — ${year}`);
                      }}
                    />
                    <Bar
                      dataKey="Belgium"
                      name="Belgium"
                      fill="#dc2626"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={(entry) => {
                        const year = (entry?.payload || entry)?.sales_year;
                        openSalesYearDrill(year, [COUNTRY_NAMES["Belgium"]], `Belgium sales — ${year}`);
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {specialData?.year_validation?.some((row) => !row.publishable) ? (
                <div className="mt-2 text-[11px] text-slate-500">
                  {specialData.year_validation
                    .filter((row) => !row.publishable)
                    .map((row) => `${row.sales_year}: ${row.unresolved_fx_lines} unresolved FX line(s)`)
                    .join(" · ")}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No annual country contribution data available
            </div>
          )
        ) : isSpecial ? (
          specialLoading ? (
            <WidgetLoadingState />
          ) : specialData ? (
            <div
              onClick={shipmentSpecialClickable ? openShipmentSpecialKpi : undefined}
              onKeyDown={
                shipmentSpecialClickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openShipmentSpecialKpi();
                      }
                    }
                  : undefined
              }
              role={shipmentSpecialClickable ? "button" : undefined}
              tabIndex={shipmentSpecialClickable ? 0 : undefined}
              title={
                shipmentSpecialClickable
                  ? `View ${widget.title} records`
                  : undefined
              }
              className={
                shipmentSpecialClickable
                  ? "cursor-pointer rounded-xl transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 -m-2 p-2"
                  : ""
              }
            >
              <div className="text-3xl font-semibold">
                {widget.type === "ceo-fda-release-hold"
                  ? specialData.released_count ?? 0
                  : widget.type === "ceo-commercial-pending-shipments"
                    ? specialData.pending_quantity ?? 0
                    : widget.type === "ceo-payments-received-pending"
                      ? `${specialData.received_count ?? 0} / ${specialData.pending_count ?? 0}`
                  : widget.type === "ceo-early-shipments"
                    ? specialData?.summary?.early_count ?? 0
                    : widget.type === "ceo-delayed-shipments"
                      ? specialData?.summary?.delayed_count ?? 0
                      : widget.type === "ceo-on-water-shipments"
                        ? specialData?.summary?.on_water_count ?? 0
                        : specialData?.summary?.on_time_count ?? 0}
              </div>

              <div className="mt-1 text-sm text-slate-500">
                {widget.type === "ceo-fda-release-hold"
                  ? `${specialData.released_count ?? 0} Released · ${specialData.hold_count ?? 0} Hold · ${specialData.pending_count ?? 0} Pending`
                  : widget.type === "ceo-commercial-pending-shipments"
                    ? "Pending dispatch quantity (MCS)"
                    : widget.type === "ceo-payments-received-pending"
                      ? `${specialData.received_count ?? 0} Received · ${specialData.pending_count ?? 0} Pending`
                  : widget.type === "ceo-early-shipments"
                    ? "Early shipments"
                    : widget.type === "ceo-delayed-shipments"
                      ? "Delayed shipments"
                      : widget.type === "ceo-on-water-shipments"
                        ? "On-water shipments"
                        : "On-time deliveries"}
              </div>

              <div className="mt-3 text-xs text-slate-500">
                {widget.type === "ceo-fda-release-hold"
                  ? `${specialData.total_count ?? 0} shipments`
                  : widget.type === "ceo-commercial-pending-shipments"
                    ? `${specialData.pending_lines ?? 0} PO lines · Pending dispatch`
                    : widget.type === "ceo-payments-received-pending"
                      ? `${specialData.total_count ?? 0} eligible POs`

                  : widget.type === "ceo-early-shipments"
                    ? `${specialData?.summary?.early_count ?? 0} shipments · ${specialData?.summary?.early_pct ?? 0}% of shipped POs`
                    : widget.type === "ceo-delayed-shipments"
                      ? `${specialData?.summary?.delayed_count ?? 0} shipments · ${specialData?.summary?.delayed_pct ?? 0}% of shipped POs`
                      : widget.type === "ceo-on-water-shipments"
                        ? `${specialData?.summary?.on_water_count ?? 0} shipments currently on water`
                        : `${specialData?.summary?.on_time_count ?? 0} shipments · ${specialData?.summary?.on_time_pct ?? 0}% of shipped POs`}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              No data available
            </div>
          )
        ) : widget.type === "sales-annual-contribution" ? (
          <SalesAnnualContribution
            widget={widget}
            filters={filters}
            onDrillDown={openWidgetDrillDown}
          />
        ) : (
          <WidgetRenderer
            widget={widget}
            dashboardFilters={createCeoFilters(widget, filters || {})}
            onDrillDown={(stage, row) => {
              if (typeof stage === "string" && row) {
                openProcessStage(stage, row);
              } else {
                openWidgetDrillDown(stage);
              }
            }}
            onTableData={setTableExportData}
          />
        )}
      </div>

      {drill && (
        <CeoDrillDownModal
          drill={drill}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          reportColumnSelections={reportColumnSelections}
          setReportColumnSelections={setReportColumnSelections}
          onClose={() => {
            setDrill(null);
            setDrillData(null);
            setDrillError("");
          }}
        />
      )}
    </div>
  );
}

export function CeoDrillDownModal({ drill, data, loading, error, onClose, reportColumnSelections, setReportColumnSelections, inline = false }) {
  const salesScope = React.useContext(CeoSalesFilterContext);
  const [localColumnSelections, setLocalColumnSelections] = useState({});
  const activeColumnSelections = reportColumnSelections || localColumnSelections;
  const updateColumnSelections = setReportColumnSelections || setLocalColumnSelections;
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(95);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortState, setSortState] = useState({
    column: "",
    direction: "asc",
  });
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(50);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const tableWrapRef = useRef(null);

  // Direct Process drill-downs already contain their final rows in `data`.
  // Other drill-downs continue using the server-side /report/query result.
  const isDirectData = drill?.payload?.directData === true;
  const isSpecialPaged = Boolean(drill?.payload?.specialPagedEndpoint);
  const metricField = String(drill?.metricField || "").toLowerCase();
  const isSalesSupportingReport = isSpecialPaged && metricField === "total_sales_usd";
  const [salesExporter, setSalesExporter] = useState(drill?.payload?.specialDetailPayload?.exporter || "");
  const effectiveSalesExporter = salesScope?.filters?.exporter ?? salesExporter;
  const salesDetailFilters = React.useMemo(() => {
    if (!salesScope) return { exporter: effectiveSalesExporter };
    const current = salesScope.filters;
    return {
      exporter: current.exporter, from_date: current.dateFrom, to_date: current.dateTo,
      buyer: current.buyer, consignee: current.consignee, country: current.country,
      grade: current.grade, variety: current.variety, brand: current.brand,
      species: current.species, product_type: current.productType,
      treatment: current.treatment, freezing_type: current.freezingType,
    };
  }, [salesScope, effectiveSalesExporter]);
  useEffect(() => {
    if (isSalesSupportingReport) setReportPage(1);
  }, [isSalesSupportingReport, salesDetailFilters]);
  const isInvoiceReport = metricField === "invoices";
  const availableColumns = data?.columns || [];
  // The invoice supporting report should not expose the PO workflow status.
  // Keep it available to PO reports that use the same ERP field elsewhere.
  const rawColumns = isInvoiceReport
    ? availableColumns.filter((column) => String(column).toLowerCase() !== "postatus")
    : availableColumns;
  const rawRows = isDirectData
    ? (data?.rows || [])
    : (reportData?.rows || (isSpecialPaged ? (data?.rows || []) : []));

  const KPI_METRIC_FIELD_ALIASES = {
    total_sales_gross_kgs: "GrossKGS",
    total_sales_usd: "LineValueUSD",
    sales_mcs: "MCS",
    invoices: "InvoiceNumber",
    rm_purchase_weight: "PurchaseWeight",
    received_qty: "InwardMCS",
    freezing_weight: "ProductionWeight",
    freezing_rejection_weight: "RejectionWeight",
    packed_mcs: "ActualMCS",
    ordered_mcs: "OrderedMCS",
    dispatched_mcs: "ShipmentOutMcs",
    shipment_out_mcs: "ShipmentOutMcs",
    available_stock_mcs: "StockMCS",
    available_stock_kgs: "AvailableKGS",
  };

  const specialMetricField =
    isSpecialPaged && metricField === "available_stock_mcs" ? "MCS" :
    isSpecialPaged && metricField === "available_stock_kgs" ? "KGS" :
    "";

  const resolvedMetricField =
    specialMetricField ||
    KPI_METRIC_FIELD_ALIASES[metricField] ||
    drill?.payload?.widget?.measure ||
    drill?.metricField ||
    "";

  const dimensionField =
    drill?.dimension ||
    drill?.payload?.widget?.group_by ||
    drill?.payload?.widget?.dimension ||
    "";

  const metricValue = drill?.metricValue;
  const aggregation = drill?.aggregation || "sum";

  const BUSINESS_LABELS = {
    LineValueUSD: "Sales Value USD",
    LineValueNative: "Sales Value (Native Currency)",
    PriceNative: "Rate",
    InvoiceDate: "Invoice Date",
    InvoiceNumber: "Invoice Number",
    SaleInvoiceLineNumber: "Sale Invoice Line Number",
    ExporterName: "Exporter",
    ContainerNo: "Container Number",
    InvoiceGrossKGS: "Invoice Gross KGS",
    PoNumber: "PO Number",
    PoNo: "PO Number",
    PoDate: "PO Date",
    ConsigneeName: "Buyer / Consignee",
    BuyerName: "Buyer",
    BrandName: "Brand",
    Brand: "Brand",
    Variety: "Variety",
    Grade: "Grade",
    PackingStyle: "Packing Style",
    PackingType: "Packing Type",
    Packing: "Packing",
    ProductType: "Product Type",
    FreezingType: "Freezing Type",
    Species: "Species",
    ProductCategory: "Product Category",
    ABStatus: "AB Status",
    Glaze: "Glaze",
    GlazeType: "Glaze Type",
    Soaking: "Soaking",
    Slabs: "Slabs",
    KGS: "KGs",
    MCS: "MCS",
    OrderedMCS: "Ordered MCS",
    DispatchedMCS: "Dispatched MCS",
    AvailableMCS: "Remaining Stock MCS",
    AvailableKGS: "Remaining Stock KGS",
    ActualMCS: "Original Stock MCS",
    ActualKGS: "Original Stock KGS",
    StockMCS: "Calculated Stock MCS Equivalent",
    ProductionDate: "Production Date",
    ColdStore: "Cold Store",
    RackLocation: "Rack Location",
    Barcode: "Barcode",
    ShipmentDate: "Shipment Date",
    PhysicalOutDate: "Physical Stock Out Date",
    ERPOutDate: "ERP Out Date",
    StockOutID: "Stock Out ID",
    SockingType: "Soaking Type",
    DestinationCountry: "Destination Country",
    BatchCode: "Batch Code",
    OutMCS: "Shipment Out MCS",
    ShipmentOutMcs: "Shipment Out MCS (incl. Loose Bags)",
    OutActualKGS: "Shipment Out KGS",
    LooseBags: "Loose Bags",
    ReceivedQty: "Production Received Qty",
    ReceivedMCS: "Production Received Qty (Actual MCS)",
    InwardMCS: "Production Received Qty (Actual MCS)",
    ArrivalDate: "Arrival Date",
    ArrivalCenter: "Arrival Centre",
    ProductionPlant: "Production Plant",
    PurchaseDate: "Purchase Date",
    PurchaseWeight: "RM Purchase Weight",
    ProductionWeight: "RM Production Weight",
    SupplierName: "Supplier",
    Centre: "Centre",
    PurchaseStation: "Purchase Station",
    LotNumber: "Lot Number",
    LotStatus: "Lot Status",
    QcRecordID: "QC Test Record",
    AnalysisDate: "Analysis Date",
    ReportDate: "Report Date",
    LotNo: "Lot Number",
    Plant: "Plant",
    Result: "QC Result",
    Parameter: "Test Parameter",
    Sulphite: "Sulphite",
    ExternalLab: "External Lab",
    ExternalResult: "External Lab Result",
    AbStatus: "Antibiotic Status",
    TestedBy: "Tested By",
    AntibioticDetected: "Antibiotic Detected / Failed",
    TestClear: "Clear Tests",
    QCStatus: "QC Status",
    LabCoverage: "Lab Coverage",
    AvailabilityBucket: "Production Readiness",
    DayCode: "Day Code",
    PsiID: "PSI ID",
    AvailabilityPct: "Availability %",
    AssignedMcs: "Packed / Assigned MCS",
    OrderedMcs: "Ordered MCS",
  };

  const businessAggregationLabel = (value) => {
    const key = String(value || "").toLowerCase();

    if (key === "count_distinct") return "Unique Count";
    if (key === "count") return "Count";
    if (key === "avg") return "Average";
    if (key === "sum") return "Total";
    if (key === "min") return "Minimum";
    if (key === "max") return "Maximum";

    return String(value || "");
  };

  const businessLabel = (column) => {
    if (isSpecialPaged && metricField === "ordered_mcs" && column === "Price") {
      return "Unit Price";
    }
    return BUSINESS_LABELS[column] ||
      String(column || "")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/\bUsd\b/g, "USD")
        .replace(/\bKgs\b/g, "KGS")
        .replace(/\bMcs\b/g, "MCS");
  };

  const isTechnicalColumn = (column) => {
    const name = String(column || "").toLowerCase();
    return (
      name === "id" ||
      name.endsWith("id") ||
      name.includes("salelineid") ||
      name.includes("invoiceid") ||
      name.includes("poid") ||
      name.includes("buyerid") ||
      name.includes("consigneeid") ||
      name.includes("processorid") ||
      name.includes("exporterid") ||
      name.includes("countryid") ||
      name.includes("clientid")
    );
  };

  const getDefaultColumnsForMetric = () => {
    const metric = String(drill?.metricField || "").toLowerCase();

    if (drill?.payload?.specialPagedEndpoint) return [...rawColumns];

    if (drill?.payload?.directData) {
      if (drill?.payload?.qcDirectData) return [...rawColumns];
      if (drill?.payload?.processStage === "va") {
        return [
          "ReceivingID",
          "VLID",
          "GradingBreakupID",
          "VarietyID",
          "Variety",
          "Species",
          "TypeProduct",
          "SentWeight",
          "WtAftrVA",
          "ProcessingDate",
          "NoofCrates",
        ];
      }

      if (drill?.payload?.processStage === "finished") {
        return [
          "ReceivingID",
          "GradingBreakupID",
          "VarietyID",
          "Variety",
          "Grade",
          "FreezingType",
          "ProductionWeight",
        ];
      }

      if (drill?.payload?.processStage === "conversion") {
        return [
          "ReceivingID",
          "GradingBreakupID",
          "HeadOnVariety",
          "HeadOnPackingGrade",
          "HeadOnHLCount",
          "FinishedVariety",
          "FinishedGrade",
          "FreezingType",
          "ProductionWeight",
        ];
      }

      return [
        "ReceivingID",
        "LotID",
        "GradingDate",
        "Variety",
        "Species",
        "TypeProduct",
        "PackingGrade",
        "Treatment",
        "HLWeight",
        "HLCount",
        "NoCrates",
      ];
    }

    const DATASET_DEFAULT_COLUMNS = {
      [DS.stock]: [
        "ProductionDate",
        "Plant",
        "ColdStore",
        "RackLocation",
        "Variety",
        "Grade",
        "PackingStyle",
        "StockMCS",
        "AvailableMCS",
        "AvailableKGS",
        "Barcode",
      ],
      [DS.rmPurchase]: ["LotNumber", "PurchaseDate", "SupplierLabel", "PurchaseStationLabel", "PurchaseCount", "PurchaseWeight", "ProductionWeight", "LotStatus"],
      [DS.vaInput]: ["ArrivalDate", "Plant", "Variety", "HonCount", "VAInputWeight"],
      [DS.vaPlant]: ["ProcessingDate", "Plant", "Grade", "Variety", "VAInputWeight", "VAOutputWeight", "VAReturnWeight"],
      [DS.deheading]: ["ProcessingDate", "Plant", "Centre", "CentreName", "ReceivedWeight", "WeightAfterDeheading", "LooseShellWeight", "CountAfterDeheading"],
      [DS.freezingType]: ["ProductionDate", "Plant", "FreezingType", "Grade", "Variety", "Species", "TypeOfMaterial", "ProductionWeight", "RejectionWeight"],
      [DS.dailyProduction]: ["ProductionDate", "Plant", "Grade", "Variety", "Species", "TypeProduct", "FreezingType", "ActualMCS", "ActualKGS"],
      [DS.inwardReceived]: ["ProductionRecordID", "ProductionDate", "Plant", "ColdStore", "PackingType", "Grade", "Variety", "PackingStyle", "ReceivedMCS"],
      [DS.packingType]: ["ProductionDate", "Plant", "PackingType", "Grade", "Variety", "FreezingType", "ActualMCS", "DummyMCS", "MasterMCS"],
      [DS.payments]: ["InvNumFull", "InvoiceDate", "PoNo", "BuyerName", "PaymentStatus", "CollectionRate", "UtilizedValue", "BankCharges"],
      [DS.soakingPlant]: ["SoakingDate", "Plant", "Grade", "Variety", "SoakingTreatment", "SoakInQty", "SoakOutQty"],
      [DS.qc]: ["AnalysisDate", "LotNo", "Centre", "Plant", "Result", "Parameter", "AbStatus", "TestedBy"],
      [DS.shipmentOut]: ["ShipmentDate", "PoNo", "ConsigneeName", "DestinationCountry", "Grade", "ColdStore", "ShipmentOutMcs", "OutActualKGS"],
    };
    const datasetDefaults = DATASET_DEFAULT_COLUMNS[drill?.payload?.widget?.dataset_id];
    if (datasetDefaults) return datasetDefaults;

    if (drill?.payload?.widget?.dataset_id === DS.dummyStock) {
      return [
        "ProductionDate",
        "PackingType",
        "Plant",
        "Grade",
        "PoNo",
        "ConsigneeName",
        "ContractState",
        "AvailableMCS",
        "AvailableKGS",
        "PoPendingMCS",
      ];
    }

    if (metric === "received_qty") {
      return [
        "ProductionDate",
        "Plant",
        "PackingType",
        "Grade",
        "Variety",
        "PackingStyle",
        "InwardMCS",
      ];
    }

    if (metric === "receivedqty") {
      return [
        "ArrivalDate",
        "ArrivalCenter",
        "CenterCode",
        "ProductionPlant",
        "PlantCode",
        "TrackCode",
        "GradingVarieties",
        "VAVarieties",
        "FinishedVarieties",
        "ReceivedQty",
      ];
    }

    if (metric === "availablemcs" || metric === "availablekgs") {
      return [
        "ProductionDate",
        "ColdStore",
        "RackLocation",
        "Variety",
        "Grade",
        "PackingStyle",
        "AvailableMCS",
        "AvailableKGS",
        "ActualMCS",
        "ActualKGS",
        "Barcode",
      ];
    }

    if (metric === "orderedmcs" || metric === "dispatchedmcs") {
      return [
        "PoDate",
        "PoNumber",
        "ConsigneeName",
        "BuyerName",
        "Brand",
        "Variety",
        "Grade",
        "PackingStyle",
        "OrderedMCS",
        "DispatchedMCS",
        "Price",
      ];
    }

    if (
      metric === "outmcs" ||
      metric === "outavailablekgs" ||
      metric === "outactualkgs"
    ) {
      return [
        "ShipmentDate",
        "PoNo",
        "ConsigneeName",
        "DestinationCountry",
        "Grade",
        "BatchCode",
        "ColdStore",
        "RackLocation",
        "ShipmentOutMcs",
        "OutActualKGS",
        "LooseBags",
      ];
    }

    return [
      "InvoiceDate",
      "PoNumber",
      "ConsigneeName",
      "BuyerName",
      "BrandName",
      "Brand",
      "Variety",
      "Grade",
      "PackingStyle",
      "MCS",
      "InvoiceGrossKGS",
      "PriceNative",
      "Currency",
      "LineValueUSD",
      "LineValueNative",
    ];
  };

  const defaultBusinessColumns = getDefaultColumnsForMetric().filter(Boolean);
  const requestedColumns = Array.isArray(
    drill?.payload?.widget?.selected_columns
  )
    ? drill.payload.widget.selected_columns.filter(Boolean)
    : [];

  // The KPI's own measure and the chart's grouping field are always part of
  // the default view, so the report shows the figure the card was built from.
  const preferredDefaults = [
    resolvedMetricField,
    dimensionField,
    ...(requestedColumns.length ? requestedColumns : defaultBusinessColumns),
  ].filter(Boolean);
  const matchedDefaults = rawColumns.filter((column) => {
    const explicitlyRequested = requestedColumns.some(
      (c) =>
        String(c).toLowerCase() ===
        String(column).toLowerCase()
    );

    if (
      !drill?.payload?.directData &&
      !drill?.payload?.specialPagedEndpoint &&
      isTechnicalColumn(column) &&
      !explicitlyRequested
    ) {
      return false;
    }
    return preferredDefaults.some((c) => String(c).toLowerCase() === String(column).toLowerCase());
  });

  // A default view of only one or two fields is too thin to read; top it up
  // with the next business (non-ID) columns of the dataset.
  const MIN_DEFAULT_COLUMNS = 6;
  const defaultSelectedColumns =
    matchedDefaults.length >= MIN_DEFAULT_COLUMNS || drill?.payload?.directData
      ? matchedDefaults
      : [
          ...matchedDefaults,
          ...rawColumns
            .filter((column) => !matchedDefaults.includes(column) && !isTechnicalColumn(column))
            .slice(0, MIN_DEFAULT_COLUMNS - matchedDefaults.length),
        ];

  const reportSelectionKey = drill?.payload?.widget?.id || drill?.title || "default";

  const storedSelectedColumns =
    Object.prototype.hasOwnProperty.call(activeColumnSelections, reportSelectionKey)
      ? activeColumnSelections[reportSelectionKey]
      : null;

  const selectedColumns = storedSelectedColumns;

  useEffect(() => {
    if (!rawColumns.length) return;

    updateColumnSelections((current) => {
      const state = current || {};

      // Existing key means the user already customized this report.
      // Preserve [] because it intentionally means all columns are deselected.
      if (Object.prototype.hasOwnProperty.call(state, reportSelectionKey)) {
        const existing = state[reportSelectionKey];
        return {
          ...state,
          [reportSelectionKey]: Array.isArray(existing)
            ? existing.filter((column) => rawColumns.includes(column))
            : existing,
        };
      }

      // Keep technical identifiers available in the column picker, but hide
      // them from a report's initial table view unless the user selects them.
      const initial = rawColumns.filter((column) => !isTechnicalColumn(column));

      return {
        ...state,
        [reportSelectionKey]: initial,
      };
    });
  }, [rawColumns, reportSelectionKey, updateColumnSelections]);

  // Open the report sorted by the KPI's own measure, largest first, so the
  // records that make up the figure are on page one (not rows of zeros).
  const initialSortApplied = useRef(false);
  useEffect(() => {
    if (initialSortApplied.current || isDirectData || !resolvedMetricField || !rawColumns.length) return;
    const metricColumn = rawColumns.find((c) => String(c).toLowerCase() === String(resolvedMetricField).toLowerCase());
    initialSortApplied.current = true;
    if (metricColumn && !isTechnicalColumn(metricColumn)) {
      setSortState({ column: metricColumn, direction: "desc" });
    }
  }, [rawColumns, resolvedMetricField, isDirectData]);

  const effectiveSelectedColumns = selectedColumns || [];
  const hasSelectedColumns = effectiveSelectedColumns.length > 0;
  const selectedColumnList = selectedColumns || [];

  const orderedIndexes = rawColumns
    .map((column, index) => ({ column, index }))
    .filter((item) => effectiveSelectedColumns.includes(item.column))
    .sort((a, b) => {
      const aMetric =
        resolvedMetricField &&
        String(a.column).toLowerCase() ===
          String(resolvedMetricField).toLowerCase()
          ? -10000
          : 0;
      const bMetric =
        resolvedMetricField &&
        String(b.column).toLowerCase() ===
          String(resolvedMetricField).toLowerCase()
          ? -10000
          : 0;

      const ap = preferredDefaults.findIndex((word) => String(a.column).toLowerCase() === String(word).toLowerCase());
      const bp = preferredDefaults.findIndex((word) => String(b.column).toLowerCase() === String(word).toLowerCase());

      const ar = ap >= 0 ? ap : 999;
      const br = bp >= 0 ? bp : 999;

      return (aMetric + ar) - (bMetric + br);
    });

  const orderedSelectedColumns = orderedIndexes.map((item) => item.column);

  // Server rows are positionally aligned to the column list of the SAME
  // response (reportData.columns), never to the initial widget payload's
  // rawColumns. Map by name; a requested column the active database cannot
  // supply is kept as a heading and rendered "Field not available".
  const reportSourceColumns = reportData?.columns || [];
  const reportResponseColumns = isInvoiceReport
    ? reportSourceColumns.filter((column) => String(column).toLowerCase() !== "postatus")
    : reportSourceColumns;
  const unavailableColumns = isDirectData
    ? []
    : (reportData?.unavailable_columns || []).filter((column) =>
        !isInvoiceReport || String(column).toLowerCase() !== "postatus"
      );
  const columns = !hasSelectedColumns
    ? []
    : (isDirectData
        ? effectiveSelectedColumns
        : (reportResponseColumns.length
            ? [...reportResponseColumns, ...unavailableColumns.filter((c) => !reportResponseColumns.includes(c))]
            : orderedSelectedColumns));
  const rowColumns = isDirectData
    ? rawColumns
    : (reportSourceColumns.length ? reportSourceColumns : rawColumns);

  const projectedRows = projectRows(rowColumns, rawRows, columns);
  const directFilteredRows = isDirectData
    ? projectedRows.filter((row, rowIndex) => {
        const searchTerms = String(search || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
        const rawRow = rawRows[rowIndex];
        const rawValues = Array.isArray(rawRow)
          ? rawRow
          : rawRow && typeof rawRow === "object"
            ? Object.values(rawRow)
            : [];
        const searchableRow = rawValues.map((value) => String(value ?? "").toLowerCase());
        const matchesSearch = searchTerms.every((term) =>
          searchableRow.some((value) => value.includes(term))
        );
        const matchesColumns = columns.every((column, index) => {
          const filter = String(columnFilters[column] || "").trim().toLowerCase();
          return !filter || String(row[index] ?? "").toLowerCase().includes(filter);
        });
        return matchesSearch && matchesColumns;
      }).sort((a, b) => {
        if (!sortState.column) return 0;
        const index = columns.indexOf(sortState.column);
        if (index < 0) return 0;
        return String(a[index] ?? "").localeCompare(String(b[index] ?? ""), undefined, { numeric: true }) * (sortState.direction === "asc" ? 1 : -1);
      })
    : projectedRows;

  const activeColumnFilterCount = Object.values(columnFilters)
    .filter((value) => String(value || "").trim() !== "")
    .length;

  const totalCount = isDirectData
    ? directFilteredRows.length
    : Number(reportData?.total_count ?? (isSpecialPaged ? data?.total_count : 0) ?? 0);
  const totalPages = Math.max(
    1,
    Math.ceil(
      Number(totalCount || 0) / Math.max(1, Number(reportPageSize || 50))
    )
  );
  const currentPage = isDirectData
    ? Math.min(reportPage, totalPages)
    : Number(reportData?.page || reportPage);
  const visibleRows = isDirectData
    ? directFilteredRows.slice((currentPage - 1) * reportPageSize, currentPage * reportPageSize)
    : projectedRows;

  useEffect(() => {
    if (isDirectData) setReportPage(1);
  }, [isDirectData, search, columnFilters, reportPageSize]);

  const changeSort = (column) => {
    setReportPage(1);
    setSortState((current) => {
      if (current.column === column) {
        return {
          column,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        column,
        direction: "asc",
      };
    });
  };

  const clearReportFilters = () => {
    setSearch("");
    setColumnFilters({});
    setSortState({ column: "", direction: "asc" });
    setReportPage(1);
  };

  const resolvedMetricFieldLower = String(
    resolvedMetricField || ""
  ).toLowerCase();

  const metricColumnIndex = columns.findIndex(
    (column) =>
      resolvedMetricFieldLower &&
      String(column).toLowerCase() === resolvedMetricFieldLower
  );

  const filteredAggregate = reportData?.filtered_aggregate ?? (isSpecialPaged ? data?.filtered_aggregate : null);

  const hasParityMetricValue =
    metricValue !== null &&
    metricValue !== undefined &&
    metricValue !== "";

  const reportTotalLabel = String(
    drill?.widgetTitle || drill?.payload?.widget?.title || drill?.title || "Report Total"
  ).replace(/\s+—\s+(Supporting ERP Records|ERP Supporting Records|Detailed Report).*$/i, "");

  const reportMetricTotal =
    hasParityMetricValue
      ? metricValue
      : filteredAggregate !== null &&
        filteredAggregate !== undefined &&
        filteredAggregate !== ""
        ? filteredAggregate
        : metricValue;

  const hasKpiFooter =
    !drill?.payload?.disableKpiFooter &&
    metricColumnIndex >= 0 &&
    reportMetricTotal !== null &&
    reportMetricTotal !== undefined &&
    reportMetricTotal !== "";

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(String(value).replace(/,/g, ""));
    if (!Number.isFinite(num)) return String(value);
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const scrollTable = (mode) => {
    const el = tableWrapRef.current;
    if (!el) return;
    if (mode === "start") el.scrollLeft = 0;
    if (mode === "left") el.scrollBy({ left: -700, behavior: "smooth" });
    if (mode === "right") el.scrollBy({ left: 700, behavior: "smooth" });
    if (mode === "end") el.scrollLeft = el.scrollWidth;
  };

  const updateSelectedColumns = (nextColumns) => {
    setReportPage(1);
    updateColumnSelections((current) => ({
      ...(current || {}),
      [reportSelectionKey]: nextColumns,
    }));
  };

  const toggleColumn = (column) => {
    const current = Array.isArray(selectedColumns) ? selectedColumns : [];

    if (current.includes(column)) {
      updateSelectedColumns(current.filter((c) => c !== column));
    } else {
      updateSelectedColumns([...current, column]);
    }
  };

  const selectAllColumns = () => {
    updateSelectedColumns([...rawColumns]);
  };

  const deselectAllColumns = () => {
    updateSelectedColumns([]);
  };

  useEffect(() => {
    if (
      drill?.payload?.directData ||
      !drill?.payload?.widget ||
      !effectiveSelectedColumns.length
    ) {
      return undefined;
    }

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setReportLoading(true);
      setReportError("");

      try {
        const response = isSpecialPaged
          ? await api.post(
              drill.payload.specialPagedEndpoint,
              {
                ...(drill.payload.specialDetailPayload || {}),
                ...(isSalesSupportingReport ? salesDetailFilters : {}),
                page: reportPage,
                page_size: reportPageSize,
                search: String(search || "").trim() || null,
                column_filters: columnFilters,
                sort_by: sortState.column || null,
                sort_dir: sortState.direction || "asc",
                selected_columns: orderedSelectedColumns,
              },
              { timeout: 60000 }
            )
          : await api.post(
              "/report/query",
              {
                widget: drill.payload.widget, filters: drill.payload.filters || [],
                page: reportPage, page_size: reportPageSize,
                search: String(search || "").trim() || null, column_filters: columnFilters,
                sort_by: sortState.column || null, sort_dir: sortState.direction || "asc",
                selected_columns: orderedSelectedColumns, aggregate_field: resolvedMetricField || null,
                aggregate_func: aggregation || "sum",
              },
              { timeout: 60000 }
            );

        if (!cancelled) {
          setReportData(response.data);
        }
      } catch (e) {
        if (!cancelled) {
          setReportError(
            e?.response?.data?.detail ||
            e?.message ||
            "Unable to load detailed report."
          );
        }
      } finally {
        if (!cancelled) {
          setReportLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    drill,
    effectiveSelectedColumns,
    reportPage,
    reportPageSize,
    search,
    columnFilters,
    sortState,
    isSalesSupportingReport,
    salesDetailFilters,
  ]);

  const metricLabel = businessLabel(drill?.metricField || "Selected KPI");

  const buildReportExportPayload = () => {
    if (isSpecialPaged) {
      return {
        ...(drill.payload.specialDetailPayload || {}),
        ...(isSalesSupportingReport ? salesDetailFilters : {}),
        search: search || null, column_filters: columnFilters || {},
        sort_by: sortState?.column || null, sort_dir: sortState?.direction || "asc",
        selected_columns: effectiveSelectedColumns.length ? effectiveSelectedColumns : undefined,
      };
    }
    const exportWidget = {
      ...drill.payload.widget,
      selected_columns: effectiveSelectedColumns.length
        ? effectiveSelectedColumns
        : undefined,
    };

    return {
      widget: exportWidget,
      filters: drill.payload.filters || [],
      page: 1,
      page_size: reportPageSize,
      search: search || null,
      column_filters: columnFilters || {},
      sort_by: sortState?.column || null,
      sort_dir: sortState?.direction || "asc",
      selected_columns: effectiveSelectedColumns.length
        ? effectiveSelectedColumns
        : undefined,
      aggregate_field: drill?.metricField || null,
      aggregate_func: aggregation || "sum",
    };
  };

  const downloadReportBlob = (response, extension, mimeType) => {
    const blob = new Blob([response.data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `${String(drill.title || "detailed_report")
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_+|_+$/g, "")}_details.${extension}`;

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    if (isDirectData) {
      if (!columns.length || !directFilteredRows.length) return;
      downloadCsv(`${String(drill.title || "detailed_report").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "")}_details.csv`, columns, directFilteredRows);
      return;
    }
    if (!drill?.payload?.widget) return;

    try {
      const response = await api.post(
        isSpecialPaged ? "/ai/ceo-chat/erp-parity-kpi-v2/detail/export.csv" : "/report/export.csv",
        buildReportExportPayload(),
        {
          responseType: "blob",
          timeout: 120000,
        }
      );

      downloadReportBlob(
        response,
        "csv",
        "text/csv;charset=utf-8"
      );
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Unable to export detailed report as CSV."
      );
    }
  };

  const exportExcel = async () => {
    if (isDirectData) {
      if (!columns.length || !directFilteredRows.length) return;
      try {
        const response = await api.post("/report/direct-export.xlsx", {
          title: drill.title || "Detailed Report",
          columns,
          rows: directFilteredRows,
        }, { responseType: "blob", timeout: 120000 });
        downloadReportBlob(response, "xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      } catch (e) {
        alert(e?.message || "Unable to export detailed report as Excel.");
      }
      return;
    }
    if (!drill?.payload?.widget) return;

    try {
      const response = await api.post(
        isSpecialPaged ? "/ai/ceo-chat/erp-parity-kpi-v2/detail/export.xlsx" : "/report/export.xlsx",
        buildReportExportPayload(),
        {
          responseType: "blob",
          timeout: 120000,
        }
      );

      downloadReportBlob(
        response,
        "xlsx",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.message ||
        "Unable to export detailed report as Excel."
      );
    }
  };

  return (
    <div
      className={inline ? "flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm" : "fixed inset-0 z-50 bg-white flex flex-col"}
      style={inline ? { height: "calc(100vh - 120px)", minHeight: 640, borderColor: "var(--c-border)" } : undefined}
      data-testid={inline ? "ms-report" : "ceo-drilldown-fullscreen"}
    >
      <div className="px-6 py-4 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--c-border)" }}>
        <div className="min-w-0">
          <div className="text-[18px] font-semibold truncate" style={{ color: "var(--c-text)" }}>
            {drill.title} — Detailed Report
          </div>
          {drill?.metricField ? (
            <div className="text-[12px] mt-1" style={{ color: "var(--c-text-muted)" }}>
              {metricLabel}
            </div>
          ) : null}
        </div>

        {onClose ? (
          <button type="button" className="btn btn-ghost" onClick={onClose} title="Close">
            <X size={17} strokeWidth={1.8} />
          </button>
        ) : null}
      </div>

      <div
        className="px-6 py-4 border-b space-y-4"
        style={{
          borderColor: "var(--c-border)",
          background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
        }}
      >
        {isSalesSupportingReport && (
          <div className="flex items-center gap-3">
            <label htmlFor="sales-supporting-exporter" className="text-[12px] font-semibold">Exporter</label>
            <select
              id="sales-supporting-exporter"
              className="select"
              value={effectiveSalesExporter}
              onChange={(event) => {
                if (salesScope) salesScope.setExporter(event.target.value);
                else setSalesExporter(event.target.value);
                setReportPage(1);
              }}
            >
              <option value="">All exporters</option>
              {(data?.exporter_options || []).map((exporter) => (
                <option key={exporter.id} value={exporter.id}>{exporter.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-12 gap-3 items-stretch">
          <div
            className="col-span-12 md:col-span-6 lg:col-span-3 rounded-xl border bg-white px-5 py-4 shadow-sm"
            style={{ borderColor: "#dbe3ef" }}
          >
            <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>
              {reportTotalLabel}
            </div>
            <div className="text-[22px] font-semibold numeric mt-1" style={{ color: "var(--c-text)" }}>
              {formatValue(reportMetricTotal)}
            </div>
          </div>

          <div
            className="col-span-12 md:col-span-6 lg:col-span-2 rounded-xl border bg-white px-5 py-4 shadow-sm"
            style={{ borderColor: "#dbe3ef" }}
          >
            <div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>
              Total Records
            </div>
            <div className="text-[22px] font-semibold numeric mt-1" style={{ color: "var(--c-text)" }}>
              {totalCount.toLocaleString()}
            </div>
            <div className="text-[11.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
              {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : "No matching records"}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-3 flex items-center">
            <div className="w-full">
              <div
                className="text-[10.5px] font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: "#64748b" }}
              >
                Search Report
              </div>
              <input
                className="input w-full"
                style={{
                  minHeight: 42,
                  borderRadius: 10,
                  background: "#fff",
                  borderColor: "#cbd5e1",
                }}
                placeholder="Search buyer, PO, grade, variety..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-wrap items-end justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={exportCsv}
              disabled={!hasSelectedColumns || reportLoading}
              title="Download the current filtered detailed report as CSV"
            >
              <Download size={14} strokeWidth={1.8} />
              Download CSV
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={exportExcel}
              disabled={!hasSelectedColumns || reportLoading}
              title="Download the current filtered detailed report as Excel"
            >
              <Download size={14} strokeWidth={1.8} />
              Download Excel
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowFilters((value) => !value)}
              title="Show or hide column filters"
            >
              {showFilters ? "Hide Filters" : "Filter Columns"}
              {activeColumnFilterCount > 0
                ? ` (${activeColumnFilterCount})`
                : ""}
            </button>

            <button
              type="button"
              className="btn btn-outline"
              onClick={clearReportFilters}
              disabled={!search && activeColumnFilterCount === 0 && !sortState.column}
            >
              Clear Filters
            </button>

            {/* Inline (MS page) columns are chosen by the page's own picker. */}
            {!inline && (
              <>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowColumnPicker((v) => !v)}
                >
                  Select Columns
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={selectAllColumns}
                  disabled={!rawColumns.length || selectedColumnList.length === rawColumns.length}
                  title="Select all available columns"
                >
                  Select All
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={deselectAllColumns}
                  disabled={!selectedColumnList.length}
                  title="Deselect all columns"
                >
                  Deselect All
                </button>
              </>
            )}




          </div>
        </div>

        {!inline && showColumnPicker && (
          <div className="rounded-xl border bg-white p-3" style={{ borderColor: "var(--c-border)" }}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="text-[13px] font-semibold">Select columns to display in this report</div>
              <label className="flex items-center gap-1.5 text-[12px] font-semibold cursor-pointer">
                <input
                  type="checkbox"
                  checked={rawColumns.length > 0 && selectedColumnList.length === rawColumns.length}
                  onChange={(e) => (e.target.checked ? selectAllColumns() : deselectAllColumns())}
                />
                Select all
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {rawColumns.map((column) => (
                <label
                  key={column}
                  className="px-2.5 py-1.5 rounded-lg border text-[11.5px] flex items-center gap-1.5"
                  style={{
                    borderColor: selectedColumnList.includes(column) ? "#2563eb" : "var(--c-border)",
                    background: selectedColumnList.includes(column) ? "#eff6ff" : "#fff",
                    color: isTechnicalColumn(column) ? "#64748b" : "var(--c-text)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedColumnList.includes(column)}
                    onChange={() => toggleColumn(column)}
                  />
                  {businessLabel(column)}
                  {isTechnicalColumn(column) && <span style={{ color: "#94a3b8" }}>(ID)</span>}
                </label>
              ))}
            </div>
          </div>
        )}

        {hasSelectedColumns && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3"
            style={{ borderColor: "#dbe3ef" }}
          >
            <div className="flex flex-wrap items-center gap-2">
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: "#eff6ff", color: "#1d4ed8" }}
            >
              {visibleRows.length.toLocaleString()} visible
            </span>

            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: "#f8fafc", color: "#475569" }}
            >
              {totalCount.toLocaleString()} total records
            </span>

            {activeColumnFilterCount > 0 && (
              <span
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: "#fff7ed", color: "#c2410c" }}
              >
                {activeColumnFilterCount} column filter(s)
              </span>
            )}

            {totalPages > 0 && (
              <span
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: "#f0fdf4", color: "#15803d" }}
              >
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setZoom((z) => Math.max(70, z - 10))}
            >
              −
            </button>
            <span className="text-[12px] numeric" style={{ color: "#475569" }}>
              {zoom}%
            </span>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setZoom((z) => Math.min(130, z + 10))}
            >
              +
            </button>

            <span
              className="mx-1 h-5 w-px"
              style={{ background: "#cbd5e1" }}
            />

            <select
              className="input"
              value={reportPageSize}
              onChange={(event) => {
                setReportPageSize(Number(event.target.value));
                setReportPage(1);
              }}
              style={{
                width: 92,
                minHeight: 36,
                borderRadius: 8,
                background: "#fff",
              }}
              title="Rows per page"
            >
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={250}>250 / page</option>
              <option value={500}>500 / page</option>
            </select>

            <button
              type="button"
              className="btn btn-outline"
              disabled={currentPage <= 1 || reportLoading}
              onClick={() => setReportPage(1)}
            >
              First
            </button>

            <button
              type="button"
              className="btn btn-outline"
              disabled={currentPage <= 1 || reportLoading}
              onClick={() => setReportPage((page) => Math.max(1, page - 1))}
            >
              Previous
            </button>

            <span
              className="px-3 py-2 rounded-lg border text-[12px] font-semibold numeric"
              style={{
                borderColor: "#cbd5e1",
                background: "#fff",
                color: "#334155",
              }}
            >
              Page {currentPage} of {totalPages || 0}
            </span>

            <button
              type="button"
              className="btn btn-outline"
              disabled={!totalPages || currentPage >= totalPages || reportLoading}
              onClick={() =>
                setReportPage((page) =>
                  Math.min(totalPages || page, page + 1)
                )
              }
            >
              Next
            </button>

            <button
              type="button"
              className="btn btn-outline"
              disabled={!totalPages || currentPage >= totalPages || reportLoading}
              onClick={() => setReportPage(totalPages)}
            >
              Last
            </button>
            </div>
          </div>
        )}

      </div>

      <div
        ref={tableWrapRef}
        className="flex-1 overflow-auto"
        style={{
          background: "#f8fafc",
          padding: "14px 16px 20px",
        }}
      >
        {loading && <div className="p-5 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>Loading detailed data…</div>}

        {error && <div className="p-5 text-[12.5px]" style={{ color: "#991b1b" }}>{error}</div>}

        {!loading && !error && columns.length === 0 && (
          <div className="p-5 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
            Please select columns to display the data.
          </div>
        )}

        {!loading && !error && columns.length > 0 && (
          <table
            className="tbl"
            style={{
              fontSize: `${Math.max(10, Math.min(14, zoom / 9))}px`,
              lineHeight: "1.35",
              minWidth: "max-content",
              background: "#fff",
              border: "1px solid #dbe3ef",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
            }}
          >
            <thead className="sticky top-0 z-20 bg-white">
              <tr>
                <th
                  scope="col"
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 40,
                    width: 56,
                    minWidth: 56,
                    padding: "6px 8px",
                    whiteSpace: "nowrap",
                    background: "#eef2f7",
                    color: "#1e293b",
                    fontWeight: 700,
                    borderBottom: "1px solid var(--c-border)",
                    textAlign: "right",
                  }}
                >
                  S.No
                </th>
                {columns.map((column) => {
                  const isMetric =
                    metricField &&
                    String(column).toLowerCase() === metricField;

                  const isSorted = sortState.column === column;

                  return (
                    <th
                      key={column}
                      title={`Sort by ${businessLabel(column)}`}
                      onClick={() => changeSort(column)}
                      style={{
                        whiteSpace: "nowrap",
                        padding: "6px 8px",
                        background: isMetric ? "#dbeafe" : "#eef2f7",
                        color: isMetric ? "#1d4ed8" : "#1e293b",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none",
                        position: isMetric ? "sticky" : undefined,
                        left: isMetric ? 56 : undefined,
                        zIndex: isMetric ? 32 : undefined,
                        borderBottom: "1px solid var(--c-border)",
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span>{businessLabel(column)}</span>
                        <span
                          className="text-[10px]"
                          style={{ color: isSorted ? "#2563eb" : "#94a3b8" }}
                        >
                          {isSorted
                            ? sortState.direction === "asc"
                              ? "▲"
                              : "▼"
                            : "↕"}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>

              {showFilters && (
                <tr>
                  <th
                    aria-hidden="true"
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 39,
                      width: 56,
                      minWidth: 56,
                      padding: "6px 8px",
                      background: "#ffffff",
                      borderBottom: "1px solid var(--c-border)",
                    }}
                  />
                  {columns.map((column) => {
                    const isMetric =
                      metricField &&
                      String(column).toLowerCase() === metricField;

                    return (
                      <th
                        key={`${column}-filter`}
                        style={{
                          padding: "6px 8px",
                          background: isMetric ? "#eff6ff" : "#ffffff",
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 56 : undefined,
                          zIndex: isMetric ? 31 : undefined,
                          borderBottom: "1px solid var(--c-border)",
                        }}
                      >
                        <input
                          value={columnFilters[column] || ""}
                          onChange={(event) =>
                            setColumnFilters((current) => ({
                              ...current,
                              [column]: event.target.value,
                            }))
                          }
                          onClick={(event) => event.stopPropagation()}
                          placeholder={`Filter ${businessLabel(column)}`}
                          style={{
                            width: "100%",
                            minWidth: 120,
                            padding: "6px 8px",
                            border: "1px solid #cbd5e1",
                            borderRadius: 7,
                            fontSize: 11,
                            fontWeight: 500,
                            background: "#fff",
                            outline: "none",
                          }}
                        />
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>

            <tbody>
              {visibleRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    background: rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                    transition: "background 120ms ease",
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.background = "#eef6ff";
                    if (event.currentTarget.firstElementChild) {
                      event.currentTarget.firstElementChild.style.background = "#eef6ff";
                    }
                  }}
                  onMouseLeave={(event) => {
                    const rowBackground = rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc";
                    event.currentTarget.style.background = rowBackground;
                    if (event.currentTarget.firstElementChild) {
                      event.currentTarget.firstElementChild.style.background = rowBackground;
                    }
                  }}
                >
                  <td
                    aria-label={`Serial number ${(currentPage - 1) * reportPageSize + rowIndex + 1}`}
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 12,
                      width: 56,
                      minWidth: 56,
                      padding: "5px 8px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      background: rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      color: "#64748b",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {(currentPage - 1) * reportPageSize + rowIndex + 1}
                  </td>
                  {row.map((value, columnIndex) => {
                    const columnName = String(columns[columnIndex]);
                    const isMetric = metricField && columnName.toLowerCase() === metricField;
                    const isOrderStatus = columnName.toLowerCase() === "orderstatus";
                    const processStage =
                      columnName === "GradingVarieties" ? "grading" :
                      columnName === "VAVarieties" ? "va" :
                      columnName === "FinishedVarieties" ? "finished" :
                      columnName === "SizeConversionPaths" ? "conversion" :
                      null;
                    const isProcessTable = drill?.payload?.directData === true;
                    const isSizeConversion = columnName === "SizeConversionPaths";
                    const displayValue = isSizeConversion && value
                      ? <span style={{ display: "inline-block", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }} title={String(value)}>
                          {String(value).split(", ").map(path => {
                            const [fromPart, toPart] = path.split(" -> ");
                            const from = (fromPart || "").split(" / ");
                            const to = (toPart || "").split(" / ");
                            return [from[0], from[1]].filter(Boolean).join(" ") + " -> " +
                              [to[0], to[1]].filter(Boolean).join(" ");
                          }).join("; ")}
                        </span>
                      : isFieldNotAvailable(value)
                        ? <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Field not available</span>
                        : (value === null || value === undefined || value === ""
                          ? "—"
                          : (
                              columnName.toLowerCase().includes("year") && !columnName.toLowerCase().includes("date")
                                ? String(value).replace(/,/g, "")
                                : (isOrderStatus ? String(value).toUpperCase() : formatReportValue(value, columnName))
                            ));
                    return (
                      <td
                        key={columnIndex}
                        title={value === null || value === undefined ? "" : String(value)}
                        style={{
                          padding: "5px 8px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth:
                            String(columns[columnIndex]).toLowerCase().includes("variety")
                              ? 180
                              : 100,
                          maxWidth:
                            String(columns[columnIndex]).toLowerCase().includes("variety")
                              ? 240
                              : 220,
                          verticalAlign: "middle",
                          background: isMetric ? "#eff6ff" : undefined,
                          fontWeight: isMetric ? 700 : 500,
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 56 : undefined,
                          zIndex: isMetric ? 20 : undefined,
                          borderBottom: "1px solid #e5e7eb",
                          color: "#1e293b",
                        }}
                      >
                        {isProcessTable && processStage ? (
                          <button
                            type="button"
                            onClick={() => {
                              openProcessStage(
                                processStage,
                                Object.fromEntries(
                                  columns.map((column, index) => [column, row[index]])
                                )
                              );
                            }}
                            className="font-semibold underline underline-offset-2"
                            style={{ color: "#2563eb" }}
                          >
                            View
                          </button>
                        ) : displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {hasKpiFooter && (
              <tfoot className="sticky bottom-0 z-20 bg-white shadow-[0_-1px_0_var(--c-border)]">
                <tr>
                <td
                  aria-hidden="true"
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 31,
                    width: 56,
                    minWidth: 56,
                    background: "#fff",
                    borderTop: "2px solid #cbd5e1",
                  }}
                />
                  {columns.map((column, columnIndex) => {
                    const isMetric = resolvedMetricFieldLower && String(column).toLowerCase() === resolvedMetricFieldLower;
                    return (
                      <td
                        key={column}
                        className="font-semibold numeric"
                        style={{
                          padding: "12px",
                          borderTop: "2px solid #cbd5e1",
                          whiteSpace: "nowrap",
                          background: isMetric ? "#dbeafe" : "#fff",
                          color: isMetric ? "#1d4ed8" : "#334155",
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 56 : undefined,
                          zIndex: isMetric ? 30 : undefined,
                        }}
                      >
                        {isMetric
                          ? formatValue(reportMetricTotal)
                          : columnIndex === 0
                            ? reportTotalLabel.toUpperCase()
                            : ""}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}



function ShipmentPerformanceMatrix({ filters, onOpenDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [shipmentTableExpanded, setShipmentTableExpanded] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [drillCustomerFilter, setDrillCustomerFilter] = useState("");
  const [drillStatus, setDrillStatus] = useState("all");
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post(
          "/ai/ceo-chat/shipment-performance",
          {
            from_date: filters?.dateFrom || undefined,
            to_date: filters?.dateTo || undefined,
            buyer: filters?.buyer || undefined,
            consignee: filters?.consignee || undefined,
            country: filters?.country || undefined,
          },
          { timeout: 35000 }
        );

        if (!cancelled) {
          setData(response?.data ?? response);
        }
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Shipment performance could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [filters?.dateFrom, filters?.dateTo, filters?.buyer, filters?.consignee, filters?.country]);

  const rows = Array.isArray(data?.customer_rows) ? data.customer_rows : [];

  const filteredRows = rows.filter((row) => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return true;

    return String(row?.CustomerName || "")
      .toLowerCase()
      .includes(q);
  });

  const total = Number(data?.summary?.eligible_pos || 0);
  const shipped = Number(data?.summary?.classified_shipped_pos || 0);
  const pending = Number(data?.summary?.pending_shipments || 0);

  const loadCustomerDrillDown = async (
    customerName = "",
    status = drillStatus
  ) => {
    try {
      const actualCustomer = String(customerName || "").trim();

      setSelectedCustomer(actualCustomer || "All Customers");
      setDrillCustomerFilter(actualCustomer);
      setDrillStatus(status);
      setDrillLoading(true);
      setDrillError("");
      setDrillData(null);

      const response = await api.post(
        "/ai/ceo-chat/shipment-performance/detail",
        {
          from_date: filters?.dateFrom || undefined,
          to_date: filters?.dateTo || undefined,
          customer_name: actualCustomer || undefined,
          buyer: filters?.buyer || undefined,
          consignee: filters?.consignee || undefined,
          country: filters?.country || undefined,
          promptness_status: status,
        },
        { timeout: 35000 }
      );

      setDrillData(response?.data ?? response);
    } catch (e) {
      setDrillData(null);
      setDrillError(
        e?.response?.data?.detail ||
          e?.message ||
          "Customer PO drill-down could not be loaded safely."
      );
    } finally {
      setDrillLoading(false);
    }
  };

  useEffect(() => {
    const handlePromptnessKpiDrill = (event) => {
      const status = event?.detail?.status;

      if (!["early", "on time", "delayed"].includes(status)) {
        return;
      }

      onOpenDetail?.({
        scope: "po",
        status,
        customer: "",
      });
    };

    const handleOnWaterKpiDrill = () => {
      onOpenDetail?.({
        scope: "on-water",
        status: "all",
        customer: "",
      });
    };

    const handleFdaKpiDrill = () => {
      onOpenDetail?.({
        scope: "fda",
        status: "all",
        customer: "",
      });
    };

    const handlePaymentsKpiDrill = () => {
      onOpenDetail?.({
        scope: "payments",
        status: "all",
        customer: "",
      });
    };

    window.addEventListener(
      "ceo-shipment-promptness-drill",
      handlePromptnessKpiDrill
    );

    window.addEventListener(
      "ceo-shipment-on-water-drill",
      handleOnWaterKpiDrill
    );

    window.addEventListener(
      "ceo-fda-release-hold-drill",
      handleFdaKpiDrill
    );

    window.addEventListener(
      "ceo-payments-received-pending-drill",
      handlePaymentsKpiDrill
    );

    return () => {
      window.removeEventListener(
        "ceo-shipment-promptness-drill",
        handlePromptnessKpiDrill
      );

      window.removeEventListener(
        "ceo-shipment-on-water-drill",
        handleOnWaterKpiDrill
      );

      window.removeEventListener(
        "ceo-fda-release-hold-drill",
        handleFdaKpiDrill
      );

      window.removeEventListener(
        "ceo-payments-received-pending-drill",
        handlePaymentsKpiDrill
      );
    };
  }, [onOpenDetail]);

  const closeCustomerDrillDown = () => {
    setSelectedCustomer("");
    setDrillCustomerFilter("");
    setDrillStatus("all");
    setDrillData(null);
    setDrillError("");
  };

  const exportCustomerDrillCsv = () => {
    const exportRows = Array.isArray(drillData?.rows)
      ? drillData.rows
      : [];

    if (!exportRows.length) return;

    if (drillData?.truncated) {
      setDrillError(
        "Export blocked because the drill-down result is truncated. Narrow the filter before downloading."
      );
      return;
    }

    const exportColumns = [
      "S.No",
      "PoNo",
      "PoDate",
      "ShipByDate",
      "StockOutDate",
      "CustomerName",
      "BuyerName",
      "DestinationCountry",
      "PromptnessStatus",
      "VarianceDays",
    ];

    const csvEscape = (value) => {
      const s = value === null || value === undefined ? "" : String(value);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csv = [
      exportColumns.map(csvEscape).join(","),
      ...exportRows.map((row, index) =>
        [index + 1, ...exportColumns.slice(1).map((column) => row?.[column])].map(csvEscape).join(",")
      ),
    ].join("\n");

    const blob = new Blob([addSerialNumberToCsv(csv)], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeCustomer = String(selectedCustomer || "customer")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    a.href = url;
    a.download =
      `shipment-performance-${safeCustomer || "customer"}-${drillStatus.replace(/\s+/g, "-")}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ceo-block">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="ceo-block-title">
            Customer / Buyer-wise Shipment Performance
          </div>
          <div className="ceo-block-subtitle">
            Customer-wise shipment promptness: stock-out date compared with the order's ship-by date.
          </div>
        </div>

        {data?.ok && (
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: "#dcfce7", color: "#166534" }}
            >
              Technically Mapped
            </span>
            <span
              className="text-[11px]"
              style={{ color: "var(--c-text-muted)" }}
            >
              {formatDateValue(data?.date_range?.from_date) || "—"} →{" "}
              {formatDateValue(data?.date_range?.to_date) || "—"}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-sm text-slate-500">
          Loading customer shipment performance...
        </div>
      ) : error ? (
        <div
          className="rounded-xl border p-4 mt-4 text-sm"
          style={{
            background: "#fef2f2",
            borderColor: "#fecaca",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      ) : data?.ok ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
            {[
              {
                label: "Eligible POs",
                value: total,
                description: "POs eligible for shipment promptness analysis",
                footer: "View all eligible POs",
                scope: "po",
                status: "all",
              },
              {
                label: "Shipped / Classified",
                value: shipped,
                description: "POs classified as Early, On Time or Delayed",
                footer: "View classified shipped POs",
                scope: "po",
                status: "shipped",
              },
              {
                label: "Pending Shipments",
                value: pending,
                description: "Eligible POs without an actual StockOutDate",
                footer: "View pending shipment POs",
                scope: "po",
                status: "pending",
              },
              {
                label: "Customers",
                value: rows.length,
                description: "Operational customers in the selected ShipByDate period",
                footer: "View customer performance",
                scope: "customers",
                status: "all",
              },
            ].map((card) => (
              <button
                key={card.label}
                type="button"
                className="bg-white border rounded-2xl text-left overflow-hidden transition hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                style={{
                  borderColor: "var(--c-border)",
                  minHeight: "172px",
                }}
                onClick={() =>
                  onOpenDetail?.({
                    scope: card.scope,
                    status: card.status,
                    customer: "",
                  })
                }
              >
                <div
                  className="px-4 py-3 border-b font-semibold text-[13px]"
                  style={{ borderColor: "var(--c-border)" }}
                >
                  {card.label}
                </div>

                <div className="px-4 py-4">
                  <div className="text-[30px] leading-none font-semibold text-slate-950">
                    {Number(card.value || 0).toLocaleString()}
                  </div>

                  <div className="text-[12.5px] text-slate-500 mt-3 leading-5 min-h-[40px]">
                    {card.description}
                  </div>

                  <div className="text-[11.5px] text-blue-600 mt-2">
                    {card.footer} →
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] text-slate-500">
              Rate denominator = POs with an actual StockOutDate in the selected
              ShipByDate period.
            </div>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer / buyer..."
              className="border rounded-lg px-3 py-2 text-sm min-w-[240px]"
            />
          </div>

          <div className="overflow-auto mt-3 border rounded-xl">
            <div className="flex justify-end p-2 border-b" style={{ borderColor: "var(--c-border)" }}>
              <button
                type="button"
                onClick={() =>
                  downloadCsv(
                    "ceo-shipment-performance-by-customer.csv",
                    [
                      "Customer / Buyer",
                      "Total POs",
                      "Early",
                      "On Time",
                      "Delayed",
                      "Pending",
                      "On / Before %",


                    ],
                    filteredRows.map((row) => [
                      row.CustomerName || "Unassigned",
                      Number(row.TotalPOs || 0).toLocaleString(),
                      Number(row.EarlyCount || 0).toLocaleString(),
                      Number(row.OnTimeCount || 0).toLocaleString(),
                      Number(row.DelayedCount || 0).toLocaleString(),
                      Number(row.PendingShipment || 0).toLocaleString(),
                      row.OnOrBeforeRatePct === null || row.OnOrBeforeRatePct === undefined
                        ? "—"
                        : `${Number(row.OnOrBeforeRatePct).toFixed(2)}%`,
                    ])
                  )
                }
                className="rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                style={{ borderColor: "var(--c-border)", background: "#fff" }}
                title="Download filtered shipment performance rows as CSV"
              >
                Download CSV
              </button>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left"
                  style={{ background: "var(--c-surface-soft)" }}
                >
                  <th className="px-3 py-3 whitespace-nowrap">Customer / Buyer</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Total POs</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Early</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">On Time</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Delayed</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">Pending</th>
                  <th className="px-3 py-3 text-right whitespace-nowrap">
                    On / Before %
                  </th>
                </tr>
              </thead>

              <tbody>
                {(shipmentTableExpanded
                  ? filteredRows
                  : filteredRows.slice(0, 5)
                ).map((row, index) => (
                  <tr
                    key={`${row.CustomerName}-${index}`}
                    className="border-t cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      onOpenDetail?.({
                        scope: "po",
                        status: "all",
                        customer: row.CustomerName || "Unassigned",
                      })
                    }
                    title="Open full PO-level shipment performance"
                  >
                    <td className="px-3 py-3 min-w-[300px] font-medium">
                      <div className="flex items-center justify-between gap-3">
                        <span>{row.CustomerName || "Unassigned"}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenDetail?.({
                              scope: "po",
                              status: "all",
                              customer: row.CustomerName || "Unassigned",
                            });
                          }}
                          className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                          aria-label={`View PO records for ${row.CustomerName || "Unassigned"}`}
                        >
                          View POs →
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.TotalPOs || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.EarlyCount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.OnTimeCount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.DelayedCount || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {Number(row.PendingShipment || 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-right font-medium">
                      {row.OnOrBeforeRatePct === null ||
                      row.OnOrBeforeRatePct === undefined
                        ? "—"
                        : `${Number(row.OnOrBeforeRatePct).toFixed(2)}%`}
                    </td>
                  </tr>
                ))}

                {filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No customer shipment performance rows match the current
                      search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {filteredRows.length > 5 && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  onClick={() =>
                    setShipmentTableExpanded((current) => !current)
                  }
                  className="rounded-lg border px-4 py-2 text-xs font-semibold hover:bg-slate-50"
                  style={{
                    borderColor: "var(--c-border)",
                    background: "#fff",
                  }}
                >
                  {shipmentTableExpanded
                    ? "Collapse"
                    : `Expand · Show all ${filteredRows.length.toLocaleString()} rows`}
                </button>
              </div>
            )}
          </div>

          <div
            className="mt-3 text-[11px] leading-5"
            style={{ color: "var(--c-text-muted)" }}
          >
            Early = StockOutDate before ShipByDate · On Time = same date ·
            Delayed = after ShipByDate · Pending = StockOutDate not recorded.
            On-water is maintained separately as a shipment lifecycle state.
          </div>
        </>
      ) : (
        <div className="py-8 text-sm text-slate-500">
          Shipment performance data is unavailable.
        </div>
      )}

    </div>
  );
}


function SectionNote({ children, tone = "info" }) {
  return (
    <div className={`ceo-section-note ${tone}`}>
      <AlertTriangle size={16} strokeWidth={1.8} />
      <div>{children}</div>
    </div>
  );
}

// R26 - Buyer total orders vs confirmed orders. Self-contained so it can follow
// the global buyer / country / plant filters as well as the dates, and hand the
// same context to its page; the shared special-widget loader only tracks dates.
function BuyerOrderCountsCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const plant = filters?.plant || "";
  const buyer = filters?.buyer || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/buyer-order-counts", { from_date: dateFrom, to_date: dateTo, consignee, country, plant, buyer, limit: 500 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load buyer order counts."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, consignee, country, plant, buyer]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    if (plant) params.set("plant", plant);
    if (buyer) params.set("buyer", buyer);
    const qs = params.toString();
    nav(`/ceo-command-center/buyer-order-analysis${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const top = (data?.buyers || []).slice(0, 10);
  const maxTotal = Math.max(0, ...top.map((b) => b.total));
  const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
  const confirmedName = data?.confirmed_name || "Po Received";
  const scope = [consignee, buyer, country, plant].filter(Boolean).join(" · ");
  const withheld = data && !data.publishable && (t.orders || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Buyer Total Orders vs Confirmed Orders</div>
          <div className="mt-1 text-sm text-slate-600">
            How many orders each buyer raised and how many were confirmed.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          View Details
        </button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading buyer order counts…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          These figures are not available for the current selection.
        </div>
      ) : null}
      {!loading && data && !withheld && !(t.orders || 0) ? (
        <div className="mt-4 text-sm text-slate-500">No orders match the selected filters.</div>
      ) : null}

      {!loading && data && !withheld && (t.orders || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Total Orders", fmt(t.orders), "Every order raised, any status", "#0f172a"],
              ["Confirmed Orders", fmt(t.confirmed), "Purchase order received", "#2563eb"],
              ["Not Confirmed", fmt(t.not_confirmed), "Pending, approved, cancelled or rejected", "#0f172a"],
              ["Confirmation Rate", t.confirmation_pct == null ? "—" : `${t.confirmation_pct}%`, "Confirmed ÷ total orders", "#0f172a"],
              ["Buyers", fmt(t.buyers), `${fmt(t.buyers_with_confirmed)} with a confirmed order`, "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Top buyers by orders</div>
            <div className="mt-0.5 text-[11px] text-slate-500">Blue = confirmed · grey = not confirmed. Bar length is the buyer's total orders.</div>
            <div className="mt-2 space-y-1.5">
              {top.map((b) => (
                <div key={b.consignee_id} className="flex items-center gap-3">
                  <div className="w-56 shrink-0 truncate text-xs text-slate-700" title={b.buyers ? `${b.consignee} (${b.buyers})` : b.consignee}>{b.consignee}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${maxTotal ? (b.confirmed / maxTotal) * 100 : 0}%`, backgroundColor: "#2563eb" }} />
                    <div style={{ width: `${maxTotal ? (b.not_confirmed / maxTotal) * 100 : 0}%`, backgroundColor: "#cbd5e1" }} />
                  </div>
                  <div className="w-44 shrink-0 text-right text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">{fmt(b.confirmed)}</span> of {fmt(b.total)} confirmed
                    {b.confirmation_pct != null ? <span className="text-slate-500"> · {b.confirmation_pct}%</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {dateFrom || dateTo ? `${formatDateValue(dateFrom) || "…"} to ${formatDateValue(dateTo) || "…"}` : "All dates"}{scope ? ` · ${scope}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R54 - Sale price vs actual price (ERP Price Book: Sale Invoice Price vs
// Invoice Price (DDP)). Self-contained so it follows the global date / buyer /
// consignee / country filters and hands the same context to its page.
function SaleVsActualPriceCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const consignee = filters?.consignee || "";
  const buyer = filters?.buyer || "";
  const country = filters?.country || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/sale-vs-actual-price", { from_date: dateFrom, to_date: dateTo, consignee, buyer, country })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the price comparison."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, consignee, buyer, country]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (consignee) params.set("consignee", consignee);
    if (buyer) params.set("buyer", buyer);
    if (country) params.set("country", country);
    const qs = params.toString();
    nav(`/ceo-command-center/sale-vs-actual-price${qs ? `?${qs}` : ""}`);
  };

  const groups = data?.groups || [];
  const g = data?.primary != null ? groups[data.primary] : null;
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN");
  const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const withheld = data && !data.publishable && groups.some((x) => x.lines > 0);
  const terms = g ? g.by_terms.filter((t) => t.comparable_lines > 0).slice(0, 6) : [];
  const maxQty = Math.max(0, ...terms.map((t) => t.quantity));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Sale Price vs Actual Price</div>
          <div className="mt-1 text-sm text-slate-600">
            The price we invoice against the actual price behind it on every order line, as on the ERP Price Book. On DDP orders to the USA the invoice price is grossed up for US duties and tariffs; the actual price is what remains before them.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          View Details
        </button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading price comparison…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          These figures are not available for the current selection.
        </div>
      ) : null}
      {!loading && data && !withheld && !g ? <div className="mt-4 text-sm text-slate-500">No order lines match the selected filters.</div> : null}

      {!loading && data && !withheld && g ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Sale Invoice Price", `${g.currency} ${fmt2(g.weighted_sale_price)}/${g.unit}`, "Weighted average, as invoiced", "#64748b"],
              ["Actual (DDP) Price", `${g.currency} ${fmt2(g.weighted_ddp_price)}/${g.unit}`, "Weighted average, before US duties", "#2563eb"],
              ["Difference per Unit", `${g.currency} ${fmt2(g.price_difference)}`, g.difference_pct == null ? "" : `${g.difference_pct}% of the sale price`, "#0f172a"],
              ["Value Difference", `${g.currency} ${fmt0(g.value_difference)}`, `Sale ${fmt0(g.sale_value)} vs actual ${fmt0(g.ddp_value)}`, "#0f172a"],
              ["Lines Compared", `${fmt0(g.comparable_lines)} of ${fmt0(g.lines)}`, `${fmt0(g.orders)} orders · ${fmt0(g.buyers)} buyers`, "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By shipping terms — {g.currency} per {g.unit}</div>
            <div className="mt-0.5 text-[11px] text-slate-500">Bar length is the weight compared. DDP orders carry the duty gap; CFR / CIF lines should show the two prices equal.</div>
            <div className="mt-2 space-y-1.5">
              {terms.map((t) => (
                <div key={t.key} className="flex items-center gap-3">
                  <div className="w-16 shrink-0 text-xs font-medium text-slate-700">{t.key}</div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${maxQty ? (t.quantity / maxQty) * 100 : 0}%`, backgroundColor: "#2563eb", height: "100%" }} />
                  </div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700">
                    sale <span className="font-semibold text-slate-900">{fmt2(t.weighted_sale_price)}</span> · actual <span className="font-semibold" style={{ color: "#2563eb" }}>{fmt2(t.weighted_ddp_price)}</span>
                    {t.difference_pct != null ? <span className="text-slate-500"> · gap {t.difference_pct}%</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {dateFrom || dateTo ? `${formatDateValue(dateFrom) || "…"} to ${formatDateValue(dateTo) || "…"}` : "All dates"}{[consignee, buyer, country].filter(Boolean).length ? ` · ${[consignee, buyer, country].filter(Boolean).join(" · ")}` : ""}
            {groups.length > 1 ? ` · Showing ${g.currency}/${g.unit}; ${groups.length - 1} other currency/unit group(s) on the detail page.` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R56 - Monthly consumables usage by plant (salt & chemicals, ERP store
// consumption entries). Self-contained so it follows the global date / plant
// filters and hands the same context to its page.
function ConsumablesUsageCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/consumables-expense-approved", { from_date: dateFrom, to_date: dateTo, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load consumables usage."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/consumables-usage-by-plant${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const expense = data?.expense || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const fmtMoney = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const withheld = data && !data.publishable && (t.entries || 0) > 0;
  const months = (data?.months || []).slice(-6);
  const plants = (data?.plants || []).slice(0, 6);
  const cell = {}; (data?.matrix || []).forEach((x) => { cell[`${x.month}|${x.plant}`] = x.kg; });
  const maxCell = Math.max(0, ...months.flatMap((m) => plants.map((p) => cell[`${m}|${p.plant}`] || 0)));
  const ml = (m) => { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "short", year: "2-digit" }); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Consumables Usage by Plant — Salt &amp; Chemicals</div>
          <div className="mt-1 text-sm text-slate-600">
            Monthly plant usage and expense for salt, processing chemicals and approved supplemental consumables such as masks, blades and scissors. ERP quantities remain the source for recorded chemical/salt usage; supplemental costs are shown only after explicit approval in Client Requirements.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          View Details
        </button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading consumables usage…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          These figures are not available for the current selection.
        </div>
      ) : null}
      {!loading && data && !withheld && !(t.entries || 0) ? <div className="mt-4 text-sm text-slate-500">No consumption entries match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.entries || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Consumed", `${fmt0(t.kg)} kg`, `${fmt0(t.bags)} bags`, "#2563eb"],
              ["Per Month", t.avg_kg_per_month == null ? "—" : `${fmt0(t.avg_kg_per_month)} kg`, `Average over ${t.months} month(s)`, "#0f172a"],
              ["Plants", fmt0(t.plants), "With consumption recorded", "#0f172a"],
              ["Chemicals", fmt0(t.chemicals), "Including salt", "#0f172a"],
              ["Expense Value", expense.published ? `INR ${fmtMoney(expense.total_value)}` : "Pending", expense.published ? `ERP chemicals INR ${fmtMoney(expense.erp_chemical_value)} · manual INR ${fmtMoney(expense.manual_consumables_value)}` : "Approve costs/manual usage in Client Requirements", expense.published ? "#059669" : "#b45309"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Kilograms consumed — last {months.length} month(s) in scope, by plant</div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead><tr className="border-b border-slate-200 uppercase tracking-wide text-slate-500">
                  <th className="py-1.5 pr-3">Plant</th>{months.map((m) => <th key={m} className="whitespace-nowrap py-1.5 pr-3 text-right">{ml(m)}</th>)}<th className="py-1.5 pr-3 text-right">Period</th>
                </tr></thead>
                <tbody>{plants.map((p) => (
                  <tr key={p.plant} className="border-b border-slate-100">
                    <td className="whitespace-nowrap py-1.5 pr-3 font-medium text-slate-800">{p.plant}</td>
                    {months.map((m) => { const v = cell[`${m}|${p.plant}`] || 0; const a = maxCell ? 0.08 + (v / maxCell) * 0.55 : 0; return (
                      <td key={m} className="whitespace-nowrap py-1.5 pr-3 text-right text-slate-800" style={{ backgroundColor: v ? `rgba(37, 99, 235, ${a.toFixed(2)})` : undefined }}>{v ? fmt0(v) : "—"}</td>); })}
                    <td className="whitespace-nowrap py-1.5 pr-3 text-right font-semibold text-slate-900">{fmt0(p.kg)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {dateFrom || dateTo ? `${formatDateValue(dateFrom) || "…"} to ${formatDateValue(dateTo) || "…"}` : "All dates"}{plant ? ` · ${plant}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R57 - Raw material arrivals by count at purchase centres ("how much 42-count
// arrived at a centre today?"). Self-contained: follows the global dates (a
// single day when none are set - today), with its own count box, and hands the
// context to its page.
function RmArrivalsByCountCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [count, setCount] = useState("");
  const [countApplied, setCountApplied] = useState("");
  const [dayOverride, setDayOverride] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";
  const todayIso = new Date().toISOString().slice(0, 10);
  const singleDay = dayOverride || (!dateFrom && !dateTo ? todayIso : (dateFrom && dateFrom === dateTo ? dateFrom : ""));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const body = singleDay ? { date: singleDay, count: countApplied, plant } : { from_date: dateFrom, to_date: dateTo, count: countApplied, plant };
    api
      .post("/ai/ceo-chat/rm-arrivals-by-count", body)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load arrivals."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [singleDay, dateFrom, dateTo, countApplied, plant]);
  useEffect(() => { setDayOverride(""); }, [dateFrom, dateTo]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (singleDay) params.set("date", singleDay); else { if (dateFrom) params.set("from", dateFrom); if (dateTo) params.set("to", dateTo); }
    if (countApplied) params.set("count", countApplied);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/rm-arrivals-by-count${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const fmt1 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const withheld = data && !data.publishable && (t.lines || 0) > 0;
  const empty = data && data.publishable && (t.lines || 0) === 0;
  const latest = t.latest_arrival_date_in_scope || t.latest_arrival_date_overall || "";
  const centres = (data?.by_centre || []).slice(0, 8);
  const maxKg = Math.max(0, ...centres.map((c) => c.kg));
  const countTxt = countApplied ? `${countApplied}-count` : "all counts";
  const noneTxt = countApplied ? `No ${countApplied}-count arrival` : "No arrival";
  const lastTxt = countApplied ? `The last ${countApplied}-count arrival` : "The last arrival";
  const scope = data?.filters?.scope || "";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Raw Material Arrivals by Count</div>
          <div className="mt-1 text-sm text-slate-600">
            How much shrimp of a given count arrived at each purchase centre on the day, from the ERP arrival register. Type a count (for example 42) to see just that count; counts are matched exactly, as in the ERP count-wise report.
          </div>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Count
            <input type="number" step="0.5" min="0" value={count} onChange={(e) => setCount(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setCountApplied(count); }} placeholder="e.g. 42"
              className="mt-1 block w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-800" /></label>
          <button type="button" onClick={() => setCountApplied(count)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Apply</button>
          <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
        </div>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading arrivals…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          These figures are not available for the current selection.
        </div>
      ) : null}
      {!loading && empty ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          {noneTxt} is recorded at any centre {scope}.
          {latest ? <> {lastTxt} in the register was on <strong>{formatDateValue(latest)}</strong>. <button type="button" onClick={() => setDayOverride(latest)} className="text-blue-700 underline">Show that day</button></> : null}
        </div>
      ) : null}

      {!loading && data && !withheld && (t.lines || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Arrived", `${fmt1(t.kg)} kg`, `${countTxt} · ${scope}`, "#2563eb"],
              ["Arrivals", fmt0(t.arrivals), `${fmt0(t.lines)} arrival line(s)`, "#0f172a"],
              ["Centres", fmt0(t.centres), "With arrivals in scope", "#0f172a"],
              ["Counts", fmt0(t.counts), "Distinct counts", "#0f172a"],
              ["Suppliers", fmt0(t.suppliers), "Distinct suppliers", "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By purchase centre — {countTxt}</div>
            <div className="mt-2 space-y-1.5">
              {centres.map((c) => (
                <div key={c.centre} className="flex items-center gap-3">
                  <div className="w-48 shrink-0 truncate text-xs text-slate-700" title={c.centre}>{c.centre} <span className="text-slate-400">({c.code})</span></div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxKg ? (c.kg / maxKg) * 100 : 0}%`, backgroundColor: "#2563eb", height: "100%" }} /></div>
                  <div className="w-52 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold text-slate-900">{fmt1(c.kg)} kg</span> · {fmt0(c.arrivals)} arrival(s) · {fmt0(c.counts)} count(s)</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {scope}{plant ? ` · plant ${plant}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R50 - MPEDA farm certificates behind EU orders. Self-contained: open EU
// orders by default, follows the global dates / consignee / buyer / country /
// plant filters and hands the same context to its page.
function EuFarmCertificatesCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const consignee = filters?.consignee || "";
  const buyer = filters?.buyer || "";
  const country = filters?.country || "";
  const plant = filters?.plant || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/eu-farm-certificates", { scope: "open", from_date: dateFrom, to_date: dateTo, consignee, buyer, country, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the EU farm certificate check."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, consignee, buyer, country, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    params.set("scope", "open");
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (consignee) params.set("consignee", consignee);
    if (buyer) params.set("buyer", buyer);
    if (country) params.set("country", country);
    if (plant) params.set("plant", plant);
    nav(`/ceo-command-center/eu-farm-certificates?${params.toString()}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const withheld = data && !data.publishable && (t.eu_orders || 0) > 0;
  const states = (data?.by_state || []).filter((s) => s.orders > 0);
  const countries = (data?.by_country || []).slice(0, 6);
  const maxOrders = Math.max(0, ...countries.map((c) => c.orders));
  const nonEuCountry = country && !(data?.options?.countries || []).includes(country) && data?.options?.countries?.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">MPEDA Farm Certificates for EU Orders</div>
          <div className="mt-1 text-sm text-slate-600">
            For open orders going to an EU country: which farms are allocated in the QC code list and whether each farm's MPEDA/CAA certificate is in place, complete and in date. Orders with no farms allocated yet cannot be certified.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Checking EU orders…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.eu_orders || 0) ? <div className="mt-4 text-sm text-slate-500">{nonEuCountry ? `${country} is not an EU destination — no EU orders to check.` : "No open EU orders match the selected filters."}</div> : null}

      {!loading && data && !withheld && (t.eu_orders || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Open EU Orders", fmt0(t.eu_orders), `${fmt0(t.countries)} countr${t.countries === 1 ? "y" : "ies"}`, "#0f172a"],
              ["Need Attention", fmt0(t.needs_attention), "Farm certificates not fully in place", "#b45309"],
              ["No Farms Allocated", fmt0(t.no_farms), "No QC code list yet", "#0f172a"],
              ["Expired Farm Certificates", fmt0(data.farm_summary?.expired), `farm links · ${fmt0(data.farm_summary?.no_expiry)} without expiry date`, data.farm_summary?.expired ? "#b45309" : "#0f172a"],
              ["Certified & Valid", fmt0(t.certified), "Every farm certified, in date", "#059669"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold text-slate-700">By certificate state</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {states.map((s) => (
                  <span key={s.state} className={`rounded-full border px-3 py-1 text-xs ${s.needs_attention ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>{s.label}: <strong>{fmt0(s.orders)}</strong></span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700">By EU country — orders that need attention / all</div>
              <div className="mt-2 space-y-1.5">
                {countries.map((c) => (
                  <div key={c.key} className="flex items-center gap-3">
                    <div className="w-28 shrink-0 truncate text-xs text-slate-700">{c.key}</div>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxOrders ? (c.needs_attention / maxOrders) * 100 : 0}%`, backgroundColor: "#b45309", height: "100%" }} /></div>
                    <div className="w-24 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold text-slate-900">{fmt0(c.needs_attention)}</span> / {fmt0(c.orders)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {dateFrom || dateTo ? `${formatDateValue(dateFrom) || "…"} to ${formatDateValue(dateTo) || "…"}` : "All open orders"}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R51 - Total volume vs finished product by type (HL / raw / cooked / blanched),
// on the same traced head-on -> finished chain as the head-on vs finished card.
// Self-contained: follows the global dates and plant, hands them to its page.
function FinishedProductVolumeCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = resolveProcessPlant(DS.preprocessing, filters || {});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/finished-product-volume-approved", { from_date: dateFrom, to_date: dateTo, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the finished product comparison."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/finished-product-volume${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct1 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const colors = { hl: "#0891b2", raw: "#2563eb", cooked: "#d97706", blanched: "#7c3aed", headon: "#059669", unclassified: "#94a3b8" };
  const cats = (t.by_category || []).filter((c) => c.finished_kg > 0);
  const withheld = data && !data.publishable && (t.records || 0) > 0;
  const breaded = data?.breaded || null;
  const main = ["hl", "raw", "cooked", "blanched"].map((k) => cats.find((c) => c.category === k) || null);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Total Volume vs Finished Product — HL / Raw / Cooked / Breaded</div>
          <div className="mt-1 text-sm text-slate-600">
            How much head-on raw material went in and how much came out as each finished-product type, traced through ERP processing records. Raw, cooked, HL and blanched remain ERP-native; Breaded is included only when its ERP aliases are explicitly approved in Client Requirements.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Tracing head-on to finished product…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.head_on_kg || 0) ? <div className="mt-4 text-sm text-slate-500">No graded lots match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.head_on_kg || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-7">
            {[
              ["Total Volume (head-on)", `${fmt0(t.head_on_kg)} kg`, `${fmt0(t.lots)} lots graded`, "#0f172a"],
              ["Finished Product", `${fmt0(t.finished_kg)} kg`, `${pct1(t.yield_finished_pct)} of head-on`, "#2563eb"],
              ...main.map((c, i) => [["HL (shell-on)", "Raw (peeled)", "Cooked", "Blanched"][i], c ? `${fmt0(c.finished_kg)} kg` : "0 kg", c ? `${pct1(c.share_pct)} of finished · ${pct1(c.pct_of_head_on)} of head-on` : "none in period", c ? colors[c.category] : "#94a3b8"]),
              ["Breaded", breaded?.published ? `${fmt0(breaded.finished_kg)} kg` : "Pending", breaded?.published ? `Approved ERP alias mapping · ${breaded.aliases?.join(", ") || "configured"}` : "Approve ERP aliases in Client Requirements", breaded?.published ? "#059669" : "#b45309"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Where the head-on volume went</div>
            <div className="mt-2 flex h-4 w-full overflow-hidden rounded-full bg-slate-100">
              {cats.map((c) => <div key={c.category} style={{ width: `${(c.finished_kg / t.head_on_kg) * 100}%`, backgroundColor: colors[c.category] || "#94a3b8", height: "100%" }} title={`${c.label}: ${fmt0(c.finished_kg)} kg`} />)}
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-600">
              {cats.map((c) => <span key={c.category}><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: colors[c.category] }} />{c.label} {pct1(c.pct_of_head_on)}</span>)}
              <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-slate-200 align-middle" />process loss &amp; untraced {pct1(100 - (t.yield_finished_pct || 0))}</span>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {dateFrom || dateTo ? `${formatDateValue(dateFrom) || "…"} to ${formatDateValue(dateTo) || "…"}` : "All dates"}{plant ? ` · ${plant}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

// R52 - Ship-date promptness by plant, on the validated shipment-performance
// rule. Self-contained: follows the global dates / plant / consignee / country
// (defaults to the current financial year when no dates are set) and hands
// the same context to its page.
function ShipPromptnessByPlantCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/ship-promptness-by-plant", { from_date: dateFrom, to_date: dateTo, plant, consignee, country })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load ship-date promptness."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant, consignee, country]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    const qs = params.toString();
    nav(`/ceo-command-center/ship-promptness-by-plant${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct1 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const colors = { early: "#059669", on_time: "#2563eb", delayed: "#dc2626", pending: "#cbd5e1" };
  const plants = data?.by_plant || [];
  const withheld = data && !data.publishable && (t.orders || 0) > 0;
  const period = data?.filters || {};

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Ship-Date Promptness — by Plant</div>
          <div className="mt-1 text-sm text-slate-600">
            For every plant, how many of the orders due to ship in the period went out early, on the ship-by date, or late — the same early / on-time / delayed rule as the Shipment Performance view, compared plant against plant.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading ship-date promptness…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.orders || 0) ? <div className="mt-4 text-sm text-slate-500">No orders were due to ship in this period for the selected filters.</div> : null}

      {!loading && data && !withheld && (t.orders || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Orders Due", fmt0(t.orders), `${fmt0(t.shipped)} shipped · ${fmt0(t.pending)} not yet`, "#0f172a"],
              ["Shipped On / Before", pct1(t.on_or_before_pct), `${fmt0(t.on_or_before)} of ${fmt0(t.shipped)} shipped`, colors.early],
              ["Early", fmt0(t.early), pct1(t.early_pct), colors.early],
              ["On Time", fmt0(t.on_time), pct1(t.on_time_pct), colors.on_time],
              ["Delayed", fmt0(t.delayed), `${pct1(t.delayed_pct)} · avg ${Number(t.avg_delay_days || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 })} days late`, colors.delayed],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Plants compared — green early · blue on time · red delayed · grey not yet shipped</div>
            <div className="mt-2 space-y-1.5">
              {plants.map((p) => (
                <div key={p.plant} className="flex items-center gap-3">
                  <div className="w-44 shrink-0 truncate text-xs text-slate-700" title={p.plant}>{p.plant}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    {["early", "on_time", "delayed", "pending"].map((k) => <div key={k} style={{ width: `${p.orders ? (p[k] / p.orders) * 100 : 0}%`, backgroundColor: colors[k], height: "100%" }} title={`${k.replace("_", " ")}: ${p[k]}`} />)}
                  </div>
                  <div className="w-64 shrink-0 text-right text-xs text-slate-700">
                    <span className="font-semibold" style={{ color: colors.early }}>{pct1(p.on_or_before_pct)}</span> on/before · <span className="font-semibold" style={{ color: colors.delayed }}>{fmt0(p.delayed)}</span> late of {fmt0(p.orders)}{p.avg_delay_days != null ? <span className="text-slate-500"> · avg {Number(p.avg_delay_days).toLocaleString("en-IN", { maximumFractionDigits: 0 })} d late</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {period.period_defaulted_to_current_fy ? `Current financial year ${formatDateValue(period.from_date)} to ${formatDateValue(period.to_date)}` : `${formatDateValue(period.from_date) || "…"} to ${formatDateValue(period.to_date) || "…"}`}{[plant, consignee, country].filter(Boolean).length ? ` · ${[plant, consignee, country].filter(Boolean).join(" · ")}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}


function FreezingLifecycleCard({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showTracks, setShowTracks] = useState(false);
  const fromDate = filters?.dateFrom || "";
  const toDate = filters?.dateTo || "";
  const plant = filters?.plant || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/freezing-lifecycle", { from_date: fromDate, to_date: toDate, plant, limit: 100 }, { timeout: 120000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the freezing lifecycle."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fromDate, toDate, plant]);

  const t = data?.totals || {};
  const kg = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const stateLabel = { completed: "Completed", in_progress: "Balance pending", over_input: "Output above recorded input", input_not_recorded: "No soaking record" };
  const stateCls = { completed: "border-emerald-200 bg-emerald-50 text-emerald-700", in_progress: "border-amber-200 bg-amber-50 text-amber-800", over_input: "border-rose-200 bg-rose-50 text-rose-700", input_not_recorded: "border-slate-200 bg-slate-50 text-slate-600" };
  const withheld = data && !data.publishable && (t.tracks || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Freezing Lifecycle — Input → Frozen → Rejected → Balance</div>
          <div className="mt-1 text-sm text-slate-600">
            Per production track: the weight that left soaking (freezing input), what was frozen, what was rejected, and what is still to be frozen. Follows the CEO date and plant filters.
          </div>
        </div>
        <button type="button" onClick={() => setShowTracks((v) => !v)} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">{showTracks ? "Hide Tracks" : "View Tracks"}</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading freezing lifecycle…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Reconciliation failed — figures withheld. {data.reconciliation?.equation}</div> : null}
      {!loading && data && !(t.tracks || 0) ? <div className="mt-4 text-sm text-slate-500">No freezing records match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.tracks || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Freezing Input", `${kg(t.input_kg)} kg`, `Soaking output · ${kg(t.tracks_with_input)} of ${kg(t.tracks)} tracks have a soaking record`, "#0f172a"],
              ["Frozen (Production)", `${kg(t.frozen_kg)} kg`, `Yield ${pct(t.freezing_yield_pct)} of recorded input`, "#2563eb"],
              ["Rejected", `${kg(t.rejection_kg)} kg`, `${pct(t.rejection_pct)} of freezing output`, "#dc2626"],
              ["Balance (not yet frozen)", `${kg(t.balance_kg)} kg`, `${kg(t.tracks_in_progress)} track(s) still in process`, "#d97706"],
              ["Output above input", `${kg(t.excess_kg)} kg`, `${kg(t.tracks_excess)} track(s) · frozen without a matching soaking record`, "#64748b"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Reconciliation: {data.reconciliation?.equation}. {kg(t.tracks_reconciled)} track(s) fully reconciled. {t.tracks_without_input ? `${kg(t.tracks_without_input)} track(s) (${kg(t.frozen_kg_without_input)} kg frozen) have no soaking record, so no input or balance is assumed for them.` : ""}
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-4">Plant</th><th className="py-2 pr-4 text-right">Tracks</th><th className="py-2 pr-4 text-right">Input kg</th><th className="py-2 pr-4 text-right">Frozen kg</th>
                <th className="py-2 pr-4 text-right">Rejected kg</th><th className="py-2 pr-4 text-right">Balance kg</th><th className="py-2 pr-4 text-right">Above input kg</th></tr></thead>
              <tbody>{(data.by_plant || []).map((p) => (
                <tr key={p.plant} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium text-slate-800">{p.plant}</td><td className="py-2 pr-4 text-right">{kg(p.tracks)}</td><td className="py-2 pr-4 text-right">{kg(p.input_kg)}</td>
                  <td className="py-2 pr-4 text-right">{kg(p.frozen_kg)}</td><td className="py-2 pr-4 text-right text-rose-700">{kg(p.rejection_kg)}</td>
                  <td className="py-2 pr-4 text-right text-amber-700">{kg(p.balance_kg)}</td><td className="py-2 pr-4 text-right text-slate-500">{kg(p.excess_kg)}</td></tr>))}</tbody>
            </table>
          </div>
          {showTracks ? (
            <div className="mt-4 overflow-x-auto">
              <div className="mb-1 text-xs text-slate-500">{data.grain} Sorted: balance pending first.{data.truncated ? " First 100 shown." : ""}</div>
              <table className="min-w-full text-sm">
                <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4">Track</th><th className="py-2 pr-4">Plant</th><th className="py-2 pr-4">Freezing Dates</th><th className="py-2 pr-4 text-right">Input</th><th className="py-2 pr-4 text-right">Frozen</th>
                  <th className="py-2 pr-4 text-right">Rejected</th><th className="py-2 pr-4 text-right">Balance</th><th className="py-2 pr-4">State</th></tr></thead>
                <tbody>{(data.rows || []).map((r) => (
                  <tr key={r.track_id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-slate-800">{r.track_id}</td><td className="py-2 pr-4">{r.plant}</td><td className="py-2 pr-4 text-slate-600 whitespace-nowrap">{formatDateValue(r.first_date)}{r.last_date !== r.first_date ? ` → ${formatDateValue(r.last_date)}` : ""}</td>
                    <td className="py-2 pr-4 text-right">{r.input_kg === null ? "—" : kg(r.input_kg)}</td><td className="py-2 pr-4 text-right">{kg(r.frozen_kg)}</td>
                    <td className="py-2 pr-4 text-right text-rose-700">{kg(r.rejection_kg)}</td><td className="py-2 pr-4 text-right text-amber-700">{r.balance_kg === null ? "—" : kg(r.balance_kg)}</td>
                    <td className="py-2 pr-4"><span className={`rounded-full border px-2 py-0.5 text-xs ${stateCls[r.state] || ""}`}>{stateLabel[r.state] || r.state}</span></td></tr>))}</tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function YearlyDispatchComparisonCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const yearType = filters?.periodMode === "Financial Year" ? "financial" : "calendar";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/yearly-dispatch-comparison", { year_type: yearType, date_basis: "dispatch", exporter: "DFL", plant, consignee, country })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the yearly comparison."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [yearType, plant, consignee, country]);

  const openPage = () => {
    const params = new URLSearchParams();
    params.set("year_type", yearType);
    params.set("exporter", "DFL");
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    nav(`/ceo-command-center/yearly-dispatch-comparison?${params.toString()}`);
  };

  const t = data?.totals || {};
  const years = data?.by_year || [];
  const latest = years.length ? years[years.length - 1] : null;
  const sp = data?.same_period;
  const spLatest = sp ? sp.rows[sp.rows.length - 1] : null;
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const tonnes = (kgs) => Number((kgs || 0) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const change = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const changeColor = (v) => (v === null || v === undefined ? "#64748b" : v >= 0 ? "#059669" : "#dc2626");
  const colors = { volume: "#2563eb", containers: "#059669" };
  const maxKgs = Math.max(0, ...years.map((y) => y.kgs || 0));
  const maxCtr = Math.max(0, ...years.map((y) => y.containers || 0));
  const withheld = data && !data.publishable && (t.invoices || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Yearly DFL Comparison — Volume &amp; Containers</div>
          <div className="mt-1 text-sm text-slate-600">
            Year against year, how much DFL shipped and in how many containers: every customs invoice that has left the cold store, with the ERP's net weight per invoice and the container numbers on it.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading yearly comparison…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.invoices || 0) ? <div className="mt-4 text-sm text-slate-500">No dispatched customs invoices match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.invoices || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`${latest.year} Volume${sp ? " (to date)" : ""}`, `${tonnes(latest.kgs)} t`, latest.prev_year ? `${change(latest.kgs_change_pct)} vs ${latest.prev_year} full year` : "First year on record", colors.volume],
              [`${latest.year} Containers${sp ? " (to date)" : ""}`, fmt0(latest.containers), latest.prev_year ? `${change(latest.containers_change_pct)} vs ${latest.prev_year} full year` : "First year on record", colors.containers],
              [sp ? `Same period ${sp.months}` : "Previous year", sp && spLatest ? `${change(spLatest.kgs_change_pct)} vol` : years.length > 1 ? `${tonnes(years[years.length - 2].kgs)} t` : "—", sp && spLatest ? `${change(spLatest.containers_change_pct)} containers vs ${sp.rows.length > 1 ? sp.rows[sp.rows.length - 2].year : "—"} ${sp.months}` : years.length > 1 ? `${fmt0(years[years.length - 2].containers)} containers` : "", sp && spLatest ? changeColor(spLatest.kgs_change_pct) : "#0f172a"],
              ["All Years", `${tonnes(t.kgs)} t`, `${fmt0(t.containers)} containers · ${fmt0(t.invoices)} shipments`, "#0f172a"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Year by year — blue volume (tonnes) · green containers</div>
            <div className="mt-2 space-y-2">
              {years.map((y) => (
                <div key={y.year} className="flex items-center gap-3">
                  <div className="w-16 shrink-0 text-xs font-medium text-slate-700">{y.year}{y === latest && sp ? <span className="block text-[10px] font-normal text-slate-400">to date</span> : null}</div>
                  <div className="flex-1 space-y-1">
                    <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxKgs ? (y.kgs / maxKgs) * 100 : 0}%`, backgroundColor: colors.volume, height: "100%" }} title={`${tonnes(y.kgs)} t`} /></div>
                    <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxCtr ? (y.containers / maxCtr) * 100 : 0}%`, backgroundColor: colors.containers, height: "100%" }} title={`${fmt0(y.containers)} containers`} /></div>
                  </div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700">
                    <span className="font-semibold" style={{ color: colors.volume }}>{tonnes(y.kgs)} t</span> <span style={{ color: changeColor(y.kgs_change_pct) }}>({change(y.kgs_change_pct)})</span> · <span className="font-semibold" style={{ color: colors.containers }}>{fmt0(y.containers)}</span> ctr <span style={{ color: changeColor(y.containers_change_pct) }}>({change(y.containers_change_pct)})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {yearType === "financial" ? "Financial years (Apr–Mar)" : "Calendar years"}{[plant, consignee, country].filter(Boolean).length ? ` · ${[plant, consignee, country].filter(Boolean).join(" · ")}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

function StockFinanceExposureCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dateFrom = filters?.dateFrom || "";
  const dateTo = filters?.dateTo || "";
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const variety = filters?.variety || "";
  const grade = filters?.grade || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/finance-cost-approved", { from_date: dateFrom, to_date: dateTo, plant, consignee, country, variety, grade })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the finance exposure."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, plant, consignee, country, variety, grade]);

  const openPage = (bucket) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    if (variety) params.set("variety", variety);
    if (grade) params.set("grade", grade);
    if (bucket) params.set("bucket", bucket);
    const qs = params.toString();
    nav(`/ceo-command-center/stock-finance-exposure${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const ccy = data?.currency || "";
  const fx = data?.exchange_rate || null;
  const bk = Object.fromEntries((data?.by_bucket || []).map((b) => [b.bucket, b]));
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const tonnes = (kgs) => Number((kgs || 0) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const dys = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })} d`);
  const colors = { dummy: "#b45309", awaiting_shipment: "#2563eb", value: "#059669" };
  const ages = ["0-30", "31-60", "61-90", "91-180", ">180"];
  const ageMcs = (bucket) => ages.map((a) => (data?.by_age || []).find((x) => x.age === a && x.bucket === bucket)?.mcs || 0);
  const financeCost = data?.finance_cost || {};
  const withheld = data && !data.publishable && (t.stock_rows || 0) > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Finance Cost — Product in Dummy / Awaiting Shipment</div>
          <div className="mt-1 text-sm text-slate-600">
            Finished product waiting in the cold store and tying up working capital: cartons packed as dummy with no order yet, and cartons packed for orders that have not shipped — how much, how long it has waited, and what it is worth where a price exists.
          </div>
        </div>
        <button type="button" onClick={() => openPage("")} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading cold-store exposure…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.stock_rows || 0) ? <div className="mt-4 text-sm text-slate-500">No stock in the cold store matches the selected filters.</div> : null}

      {!loading && data && !withheld && (t.stock_rows || 0) > 0 ? (
        <>
          {financeCost.published ? (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <span className="font-semibold">Finance cost</span> at the approved annual rate of {financeCost.annual_rate_pct}%.
            </div>
          ) : null}
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Dummy Stock", `${fmt0(bk.dummy?.mcs)} ctn`, `${tonnes(bk.dummy?.kgs)} t · avg ${dys(bk.dummy?.avg_days)} in store`, colors.dummy, "dummy"],
              ["Awaiting Shipment", `${fmt0(bk.awaiting_shipment?.mcs)} ctn`, `${tonnes(bk.awaiting_shipment?.kgs)} t · avg ${dys(bk.awaiting_shipment?.avg_days)} · ${fmt0(bk.awaiting_shipment?.overdue_mcs)} past ship-by`, colors.awaiting_shipment, "awaiting_shipment"],
              ["Priced Value", `${ccy} ${fmt0(t.priced_value)}`, `${t.priced_share_pct ?? 0}% of cartons priced${fx ? ` · ≈ INR ${fmt0(t.priced_value * fx)}` : ""}`, colors.value, ""],
              ["Finance Cost", financeCost.published ? `${financeCost.currency || ccy} ${fmt0(financeCost.total)}` : "Pending", financeCost.published ? `${financeCost.annual_rate_pct}% p.a.` : "Annual rate not set", financeCost.published ? "#059669" : "#94a3b8", ""],
            ].map(([label, value, sub, color, bucket]) => (
              <div key={label} className={`rounded-xl border border-slate-200 p-3${bucket ? " cursor-pointer hover:bg-slate-50" : ""}`} onClick={bucket ? () => openPage(bucket) : undefined}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">How long it has waited — cartons by days in store (0-30 · 31-60 · 61-90 · 91-180 · over 180)</div>
            <div className="mt-2 space-y-1.5">
              {[["Dummy", "dummy"], ["Awaiting shipment", "awaiting_shipment"]].map(([label, bucket]) => { const parts = ageMcs(bucket); const total = parts.reduce((a, b) => a + b, 0); return (
                <div key={bucket} className="flex items-center gap-3">
                  <div className="w-36 shrink-0 text-xs text-slate-700">{label}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    {parts.map((v, i) => <div key={ages[i]} style={{ width: `${total ? (v / total) * 100 : 0}%`, backgroundColor: colors[bucket], opacity: 0.35 + i * 0.16, height: "100%" }} title={`${ages[i]} days: ${fmt0(v)} cartons`} />)}
                  </div>
                  <div className="w-64 shrink-0 text-right text-xs text-slate-700">{parts.map((v, i) => <span key={ages[i]} className={i ? "ml-2" : ""} title={`${ages[i]} days`}>{fmt0(v)}</span>)}</div>
                </div>); })}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of today, aged from the production date{dateFrom || dateTo ? ` · produced ${formatDateValue(dateFrom) || "…"} to ${formatDateValue(dateTo) || "…"}` : ""}{[plant, consignee, country, variety, grade].filter(Boolean).length ? ` · ${[plant, consignee, country, variety, grade].filter(Boolean).join(" · ")}` : ""}. {fmt0(t.mcs)} cartons / {tonnes(t.kgs)} t in store in all
          </div>
        </>
      ) : null}
    </div>
  );
}

function PackingMaterialContractsCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const consignee = filters?.consignee || "";
  const country = filters?.country || "";
  const grade = filters?.grade || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/packing-material-availability-approved", { preset: "sysco", plant, consignee, country, grade })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the packing-material view."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [plant, consignee, country, grade]);

  const openPage = () => {
    const params = new URLSearchParams();
    params.set("preset", "sysco");
    if (plant) params.set("plant", plant);
    if (consignee) params.set("consignee", consignee);
    if (country) params.set("country", country);
    if (grade) params.set("grade", grade);
    nav(`/ceo-command-center/packing-material-contracts?${params.toString()}`);
  };

  const t = data?.totals || {};
  const stock = data?.packing_material_stock || {};
  const styles = data?.by_style_with_stock || data?.by_style || [];
  const stockApproved = stock.status === "approved_supplemental";
  const availableStock = styles.reduce((sum, row) => sum + Number(row.available_cartons || 0), 0);
  const shortageStock = styles.reduce((sum, row) => sum + Number(row.shortage_cartons || 0), 0);
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const pct1 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`);
  const colors = { packed: "#059669", balance: "#2563eb" };
  const withheld = data && !data.publishable && (t.lines || 0) > 0;
  const brands = (data?.options?.presets || []).find((p) => p.key === "sysco")?.brands || [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Packing Material for Sysco Contracts</div>
          <div className="mt-1 text-sm text-slate-600">
            What the open Sysco (Portico-brand) contracts still need to be packed in: cartons ordered, cartons already packed and in the cold store, and the balance still to pack — the printed master cartons and inner packs that must be on hand.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading packing-material requirement…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.lines || 0) ? <div className="mt-4 text-sm text-slate-500">No open Sysco contract lines match the selected filters.</div> : null}

      {!loading && data && !withheld && (t.lines || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ["Open Contracts", fmt0(t.contracts), `${fmt0(t.lines)} lines`, "#0f172a"],
              ["Cartons Ordered", fmt0(t.ordered_mcs), "master cartons", "#475569"],
              ["Packed in Store", fmt0(t.packed_mcs), `${pct1(t.packed_pct)} of ordered`, colors.packed],
              ["Still to Pack", fmt0(t.balance_mcs), `printed master cartons needed · ${fmt0(t.inner_packs_needed)} inner packs`, colors.balance],
              ["Material in Stock", stockApproved ? fmt0(availableStock) : "Not recorded", stockApproved ? `${fmt0(shortageStock)} carton shortage against open requirement` : "stock not recorded", stockApproved ? "#059669" : "#94a3b8"],
            ].map(([label, value, sub, color]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By brand and packing style — green packed · blue still to pack</div>
            <div className="mt-2 space-y-1.5">
              {styles.slice(0, 6).map((s) => (
                <div key={`${s.brand}|${s.packing_style}`} className="flex items-center gap-3">
                  <div className="w-64 shrink-0 truncate text-xs text-slate-700" title={`${s.brand} · ${s.packing_style}`}>{s.brand} · {s.packing_style}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${s.ordered_mcs ? (s.packed_mcs / s.ordered_mcs) * 100 : 0}%`, backgroundColor: colors.packed, height: "100%" }} title={`packed ${fmt0(s.packed_mcs)}`} />
                    <div style={{ width: `${s.ordered_mcs ? (s.balance_mcs / s.ordered_mcs) * 100 : 0}%`, backgroundColor: colors.balance, height: "100%" }} title={`still to pack ${fmt0(s.balance_mcs)}`} />
                  </div>
                  <div className="w-80 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: colors.balance }}>{fmt0(s.balance_mcs)}</span> needed · {stockApproved && s.available_cartons != null ? `${fmt0(s.available_cartons)} available · ${fmt0(s.shortage_cartons)} short · ` : ""}{fmt0(s.inner_packs_needed)} inner packs ({fmt0(s.inner_per_carton)}/ctn) · {fmt0(s.contracts)} contracts</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of today, open contracts only{[plant, consignee, country, grade].filter(Boolean).length ? ` · ${[plant, consignee, country, grade].filter(Boolean).join(" · ")}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

function ChemicalStockCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  // Plant = All shows the chemical consolidated across every plant; the API
  // is unit-based, so it is asked for any unit and its all-plant figures
  // (chemical_totals / matrix) are used.
  const selectedPlants = Array.isArray(plant) ? plant : (plant ? [plant] : []);
  const allPlants = selectedPlants.length !== 1;
  const unit = selectedPlants.length === 1 ? selectedPlants[0] : (selectedPlants.length ? selectedPlants : "2");
  const unitKey = JSON.stringify(unit);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/chemical-stock", { unit: JSON.parse(unitKey), chemical: "carfisol" })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the chemical stock."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [unitKey]);

  const openPage = (chemical) => {
    const params = new URLSearchParams();
    if (!allPlants) params.set("unit", unit);
    params.set("chemical", chemical == null ? "carfisol" : chemical);
    nav(`/ceo-command-center/chemical-stock?${params.toString()}`);
  };

  const kgFmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const color = (v) => (v < 0 ? "#dc2626" : v > 0 ? "#2563eb" : "#64748b");
  const maxDate = (list) => list.reduce((m, d) => (d && d > m ? d : m), "");

  const chemName = data?.resolved?.chemical?.name || "Carfisol";
  const scopeName = allPlants ? (selectedPlants.length ? selectedPlants.join(", ") : "all plants") : (data?.resolved?.unit?.name || selectedPlants[0]);

  // Headline chemical: one unit, or summed across all units.
  let h = data?.headline || null;
  let chems = data?.unit_chemicals || [];
  let totals = data?.unit_totals || {};
  if (allPlants && data) {
    const ct = data.chemical_totals;
    const byUnit = data.chemical_by_unit || [];
    h = ct ? {
      chemical: chemName,
      stock_kgs: ct.stock_kgs, stock_bags: ct.stock_bags, received_kgs: ct.received_kgs, used_kgs: ct.used_kgs,
      last_movement: maxDate(byUnit.map((x) => x.last_movement)),
      plants: byUnit.filter((x) => x.stock_kgs || x.received_kgs).length,
    } : null;
    const agg = new Map();
    for (const m of data.matrix || []) {
      const cur = agg.get(m.chemical_id) || { chemical_id: m.chemical_id, chemical: m.chemical, stock_kgs: 0, stock_bags: 0, last_movement: "" };
      cur.stock_kgs += Number(m.stock_kgs || 0);
      cur.stock_bags += Number(m.stock_bags || 0);
      cur.last_movement = maxDate([cur.last_movement, m.last_movement]);
      agg.set(m.chemical_id, cur);
    }
    chems = [...agg.values()].sort((a, b) => b.stock_kgs - a.stock_kgs);
    totals = {
      chemicals: chems.length,
      chemicals_in_stock: chems.filter((c) => c.stock_kgs > 0).length,
      negative_stock_chemicals: chems.filter((c) => c.stock_kgs < 0).length,
      stock_kgs: chems.reduce((a, c) => a + c.stock_kgs, 0),
      stock_bags: chems.reduce((a, c) => a + c.stock_bags, 0),
    };
  }
  const withheld = data && !data.publishable && (totals.chemicals || 0) > 0;
  const maxStock = Math.max(0, ...chems.map((c) => c.stock_kgs || 0));
  const stale = (d) => (d == null ? "" : d > 90 ? ` · no movement for ${fmt0(d)} days` : ` · ${fmt0(d)} days ago`);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Chemical Stock — {chemName} at {scopeName}</div>
          <div className="mt-1 text-sm text-slate-600">
            Treatment chemical and salt on hand: everything received less everything used, chemical by chemical{allPlants ? ", totalled across all plants" : ""}.
          </div>
        </div>
        <button type="button" onClick={() => openPage(null)} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading chemical stock…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(totals.chemicals || 0) ? <div className="mt-4 text-sm text-slate-500">No chemical receipts or usage are recorded for {scopeName}.</div> : null}

      {!loading && data && !withheld && (totals.chemicals || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`${chemName} in stock`, h ? `${kgFmt(h.stock_kgs)} kg` : "—", h ? `${kgFmt(h.stock_bags)} bags${allPlants ? ` · ${fmt0(h.plants)} plant(s)` : (h.kg_per_bag ? ` of ${kgFmt(h.kg_per_bag)} kg` : "")}${h.no_movements ? " · no entries at this unit" : ""}` : "", h ? color(h.stock_kgs) : "#64748b"],
              ["Received / Used", h ? `${kgFmt(h.received_kgs)} / ${kgFmt(h.used_kgs)} kg` : "—", h && !allPlants ? `${kgFmt(h.transfer_in_kgs)} kg came in from other units · ${kgFmt(h.transfer_out_kgs)} kg went out` : (allPlants ? "all plants" : ""), "#0f172a"],
              ["Last Movement", h?.last_movement || "—", h && !allPlants ? `receipt ${h.last_receipt || "—"} · usage ${h.last_usage || "—"}${stale(h.days_since_last_movement)}` : "", "#0f172a"],
              [allPlants ? "All chemicals, all plants" : `All chemicals at ${data.resolved?.unit?.code || "unit"}`, `${kgFmt(totals.stock_kgs)} kg`, `${kgFmt(totals.stock_bags)} bags · ${fmt0(totals.chemicals_in_stock)} of ${fmt0(totals.chemicals)} chemicals in stock${totals.negative_stock_chemicals ? ` · ${totals.negative_stock_chemicals} negative` : ""}`, "#2563eb"],
            ].map(([label, value, sub, c]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color: c }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">Every chemical at {scopeName} — stock in kg (click to open its ledger)</div>
            <div className="mt-2 space-y-1.5">
              {chems.slice(0, 8).map((c) => (
                <div key={c.chemical_id} className="flex cursor-pointer items-center gap-3 hover:bg-slate-50" onClick={() => openPage(c.chemical)}>
                  <div className="w-64 shrink-0 truncate text-xs text-slate-700" title={c.chemical}>{c.chemical}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxStock && c.stock_kgs > 0 ? (c.stock_kgs / maxStock) * 100 : 0}%`, backgroundColor: "#2563eb", height: "100%" }} /></div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: color(c.stock_kgs) }}>{kgFmt(c.stock_kgs)} kg</span> · {kgFmt(c.stock_bags)} bags · last {c.last_movement || "—"}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of today · {scopeName}
          </div>
        </>
      ) : null}
    </div>
  );
}

function FloorBalanceCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = resolveProcessPlant(DS.vaPlant, filters || {}) || filters?.plant || "";
  const asOf = filters?.dateTo || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/floor-balance", { as_of: asOf, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the floor balance."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [asOf, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (asOf) params.set("as_of", asOf);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/floor-balance${qs ? `?${qs}` : ""}`);
  };

  const t = data?.totals || {};
  const f = data?.filters || {};
  const plants = data?.by_plant || [];
  const kgFmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const colors = { floor: "#2563eb", graded: "#059669", sent: "#b45309" };
  const maxPlant = Math.max(0, ...plants.map((p) => p.floor_kgs || 0));
  const withheld = data && !data.publishable && (t.tracks || 0) > 0;
  const older = (data?.by_age || []).filter((a) => a.age === ">30").reduce((s, a) => s + a.floor_kgs, 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Floor Balance on a Date</div>
          <div className="mt-1 text-sm text-slate-600">
            Headless material graded (and soaked) on the processing floor but not yet sent to value addition at the end of the day — the ERP's VA floor balance, per plant. Set the global To Date to look at any past day; otherwise today is shown.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading floor balance…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(t.tracks || 0) ? <div className="mt-4 text-sm text-slate-500">No graded tracks arrived in the {f.lookback_days || 7} days up to {f.as_of}{plant ? ` at ${plant}` : ""}.</div> : null}

      {!loading && data && !withheld && (t.tracks || 0) > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`Floor balance on ${f.as_of}`, `${kgFmt(t.floor_kgs)} kg`, `${fmt0(t.tracks_with_balance)} track(s) with material · ${fmt0(t.crates)} crates`, colors.floor],
              ["Graded / Received", `${kgFmt(t.input_kgs)} kg`, `${fmt0(t.tracks)} tracks arrived in the last ${f.lookback_days} days`, colors.graded],
              ["Sent to VA", `${kgFmt(t.sent_kgs)} kg`, "up to the same date", colors.sent],
              ["Older records", older ? `${kgFmt(older)} kg` : "—", older ? "tracks over 30 days still open" : "no unclosed tracks over 30 days in the window", older ? "#dc2626" : "#64748b"],
            ].map(([label, value, sub, c]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color: c }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By plant — kg on the floor at end of {f.as_of}</div>
            <div className="mt-2 space-y-1.5">
              {plants.map((p) => (
                <div key={p.plant} className="flex items-center gap-3">
                  <div className="w-44 shrink-0 truncate text-xs text-slate-700" title={p.plant}>{p.plant}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxPlant ? (p.floor_kgs / maxPlant) * 100 : 0}%`, backgroundColor: colors.floor, height: "100%" }} /></div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: colors.floor }}>{kgFmt(p.floor_kgs)} kg</span> on {fmt0(p.tracks_with_balance)} of {fmt0(p.tracks)} tracks · {fmt0(p.crates)} crates</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            As of {f.as_of} end of day · arrivals from {f.window_from}{plant ? ` · ${plant}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

function ChemicalUsedOnDateCard({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const plant = filters?.plant || "";
  const day = filters?.dateTo || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/chemical-used-on-date", { date: day, plant })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err?.response?.data?.detail || err?.message || "Could not load the chemical usage."); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [day, plant]);

  const openPage = () => {
    const params = new URLSearchParams();
    if (data?.filters?.date) params.set("date", data.filters.date);
    if (plant) params.set("plant", plant);
    const qs = params.toString();
    nav(`/ceo-command-center/chemical-used-on-date${qs ? `?${qs}` : ""}`);
  };

  const f = data?.filters || {};
  const st = data?.store?.totals || {}; const sk = data?.soaking?.totals || {};
  const kgFmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const colors = { store: "#2563eb", tubs: "#059669", salt: "#0891b2" };
  const chems = (data?.store?.by_chemical || []);
  const maxChem = Math.max(0, ...chems.map((c) => c.kgs || 0));
  const dosed = Object.fromEntries((data?.soaking?.by_chemical || []).map((c) => [c.chemical, c.chemical_kgs]));
  const withheld = data && !data.publishable && ((st.entries || 0) > 0 || (sk.tubs || 0) > 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-lg font-semibold text-slate-900">Chemical Used on a Date</div>
          <div className="mt-1 text-sm text-slate-600">
            Treatment chemical and salt used on one day — what the store issued to each plant and what the floor actually dosed into the soaking tubs. Set the global To Date to pick the day; otherwise the latest day with store entries is shown.
          </div>
        </div>
        <button type="button" onClick={openPage} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">View Details</button>
      </div>

      {loading ? <div className="mt-4 text-sm text-slate-500">Loading chemical usage…</div> : null}
      {!loading && error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}
      {!loading && withheld ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">These figures are not available for the current selection.</div> : null}
      {!loading && data && !withheld && !(st.entries || 0) && !(sk.tubs || 0) ? <div className="mt-4 text-sm text-slate-500">No chemical was issued or dosed on {formatDateValue(f.date)}{plant ? ` at ${f.plant_name || plant}` : ""}.</div> : null}

      {!loading && data && !withheld && ((st.entries || 0) > 0 || (sk.tubs || 0) > 0) ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [`Issued from store on ${formatDateValue(f.date)}`, `${kgFmt(st.kgs)} kg`, `${fmt0(st.bags)} bags · ${fmt0(st.entries)} entries · ${fmt0(st.plants)} plant(s)`, colors.store],
              ["Dosed into tubs", `${kgFmt(sk.chemical_kgs)} kg`, `chemical on ${fmt0(sk.tubs)} soaks · ${fmt0(sk.plants)} plant(s)`, colors.tubs],
              ["Salt dosed", `${kgFmt(sk.salt_kgs)} kg`, "on the same soaks", colors.salt],
              ["Material soaked", `${kgFmt(sk.soak_in_kgs)} kg`, st.transfer_entries ? `${kgFmt(st.transfer_kgs)} kg also moved between units` : "no unit-to-unit transfers that day", "#0f172a"],
            ].map(([label, value, sub, c]) => (
              <div key={label} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-xl font-semibold" style={{ color: c }}>{value}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700">By chemical — issued from store (blue) with the amount dosed into tubs that day</div>
            <div className="mt-2 space-y-1.5">
              {chems.slice(0, 8).map((c) => (
                <div key={c.chemical} className="flex items-center gap-3">
                  <div className="w-64 shrink-0 truncate text-xs text-slate-700" title={c.chemical}>{c.chemical}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100"><div style={{ width: `${maxChem ? (c.kgs / maxChem) * 100 : 0}%`, backgroundColor: colors.store, height: "100%" }} /></div>
                  <div className="w-72 shrink-0 text-right text-xs text-slate-700"><span className="font-semibold" style={{ color: colors.store }}>{kgFmt(c.kgs)} kg</span> issued · {fmt0(c.bags)} bags{dosed[c.chemical] != null ? <span style={{ color: colors.tubs }}> · {kgFmt(dosed[c.chemical])} kg dosed</span> : /salt/i.test(c.chemical) ? <span style={{ color: colors.salt }}> · {kgFmt(sk.salt_kgs)} kg dosed</span> : null}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            {f.date_defaulted ? `No date set — showing ${formatDateValue(f.date)}, the latest day with store entries` : `Day ${formatDateValue(f.date)}`}{plant ? ` · ${f.plant_name || plant}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}


// ===== CAMAROO FINAL ERP PARITY KPI CARD =====
const PARITY_KPI_DRILLDOWN = {
  deheading_input_weight: {
    dataset_id: DS.deheadingOverall,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  weight_after_deheading: {
    dataset_id: DS.deheadingOverall,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  deheading_output_unit1: {
    dataset_id: DS.deheadingOverall,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  total_sales_gross_kgs: {
    dataset_id: DS.salesInvoices,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
    selected_columns: ["InvoiceID","InvoiceNumber","InvoiceDate","PoNo","BuyerName","ConsigneeName","DestinationCountry","GrossKGS"],
  },
  total_sales_usd: {
    dataset_id: DS.sales,
    // Keep the supporting records on the KPI's ERP shipment-line contract.
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
    selected_columns: [
      "LineValueUSD", "InvoiceNumber", "InvoiceDate", "PoNumber", "BuyerName",
      "ConsigneeName", "DestinationCountry", "Grade", "Variety", "MCS",
      "Rate", "Price", "QuantityInUnit", "Quantity", "QtyUnit", "Currency",
      "SaleInvoiceLineNumber", "ExporterName", "ContainerNo", "ShipmentDate"
    ],
  },
  sales_mcs: {
    dataset_id: DS.sales,
  },
  invoices: {
    dataset_id: DS.salesInvoices,
  },
  rm_purchase_weight: {
    dataset_id: DS.rmArrival,
    selected_columns: [
      "PurchaseDate",
      "LotNumber",
      "SupplierName",
      "SupplierType",
      "SupplierCentre",
      "PurchaseStation",
      "Centre",
      "Variety",
      "PurchaseCount",
      "PurchaseWeight",
      "Rate"
    ],
  },
  received_qty: {
    dataset_id: DS.inwardReceived,
    selected_columns: ["ProductionDate", "Plant", "PackingType", "Grade", "Variety", "PackingStyle", "InwardMCS", "LooseBags"],
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  freezing_weight: {
    dataset_id: DS.freezingType,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  freezing_rejection_weight: {
    dataset_id: DS.freezingType,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  packed_mcs: {
    dataset_id: DS.dailyProduction,
  },
  ordered_mcs: {
    dataset_id: DS.po,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  dispatched_mcs: {
    dataset_id: DS.shipmentOut,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  shipment_out_mcs: {
    dataset_id: DS.shipmentOut,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  shipment_out_kgs: {
    dataset_id: DS.shipmentOut,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  available_stock_mcs: {
    dataset_id: DS.stock,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
  available_stock_kgs: {
    dataset_id: DS.stock,
    detail_endpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
  },
};

const PARITY_KPI_FAVORITE_TARGETS = {
  "ceo-parity-total_sales_usd": "ceo-total-sales-usd",
  "ceo-parity-commercial-sales-gross-kgs": "ceo-commercial-sales-kgs",
  "ceo-parity-freezing-rejection-weight": "ceo-freezing-rejection-weight",
  "ceo-parity-ordered_mcs": "ceo-ordered-mcs",
  "ceo-parity-rm_purchase_weight": "ceo-purchase-weight",
  "ceo-parity-received_qty": "ceo-received-qty",
  "ceo-parity-shipment_out_mcs": "ceo-shipment-out-mcs",
  "ceo-parity-shipment_out_kgs": "ceo-shipment-out-kgs",
  "ceo-parity-freezing_weight": "ceo-freezing-production-weight",
  "ceo-parity-packed_mcs": "ceo-daily-actual-mcs",
  "ceo-parity-sales_mcs": "ceo-sales-mcs",
  "ceo-parity-invoices": "ceo-sales-invoice-count",
  "ceo-parity-dispatched_mcs": "ceo-dispatched-mcs",
  "ceo-parity-total-sales-gross-kgs": "ceo-commercial-sales-kgs",
  "ceo-parity-deheading-input-weight": "ceo-deheading-received-weight",
  "ceo-parity-weight-after-deheading": "ceo-deheading-output-weight",
  "ceo-parity-deheading-output-unit1": "ceo-loose-shell-weight",
  "ceo-parity-available-stock-mcs": "ceo-available-mcs",
  "ceo-parity-available-stock-kgs": "ceo-available-kgs",
};

function ParityKpiCard({ widget, filters = {}, compact = false, reportColumnSelections, setReportColumnSelections }) {
  const favoriteTarget =
    PARITY_KPI_FAVORITE_TARGETS[widget?.id] || null;

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [drill, setDrill] = React.useState(null);
  const [drillData, setDrillData] = React.useState(null);
  const [drillLoading, setDrillLoading] = React.useState(false);
  const [drillError, setDrillError] = React.useState("");
  const [liveRefreshTick, setLiveRefreshTick] = React.useState(0);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setLiveRefreshTick((current) => current + 1);
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  // Manual refresh from the CEO Command Center.
  React.useEffect(() => {
    const handleManualRefresh = () => {
      setLiveRefreshTick((current) => current + 1);
    };

    window.addEventListener("camaroo:ceo-refresh-kpis", handleManualRefresh);

    return () => {
      window.removeEventListener("camaroo:ceo-refresh-kpis", handleManualRefresh);
    };
  }, []);

  const drillConfig = PARITY_KPI_DRILLDOWN[widget?.metric];
  const isStockParityMetric = ["available_stock_mcs", "available_stock_kgs"].includes(widget?.metric);
  const stockScopeSignature = isStockParityMetric ? [filters?.plant || "ALL", filters?.coldStore || "ALL", filters?.grade || "ALL", filters?.variety || "ALL", filters?.species || "ALL", filters?.productType || "ALL", filters?.treatment || "ALL", filters?.freezingType || "ALL", filters?.brand || "ALL"].join("|") : "";
  const parityRefreshSignature = [
    widget?.metric || "",
    filters?.dateFrom || "",
    filters?.dateTo || "",
    filters?.plant || "",
    filters?.buyer || "",
    filters?.consignee || "",
    filters?.country || "",
    filters?.grade || "",
    filters?.variety || "",
    filters?.treatment || "",
    filters?.brand || "",
    filters?.coldStore || "",
    filters?.species || "",
    filters?.productType || "",
    filters?.freezingType || "",
    liveRefreshTick,
    widget?.metric === "total_sales_usd" ? filters?.exporter || "" : "",
  ].join("|");
  const parityKpiUrl = `/ai/ceo-chat/erp-parity-kpi-v2?scope=${encodeURIComponent(parityRefreshSignature)}`;

  React.useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");

    const request = api.post(parityKpiUrl, {
        metric: widget.metric,
        ...(widget.metric === "total_sales_usd" ? { exporter: filters?.exporter || "" } : {}),
        from_date: filters?.dateFrom || "",
        to_date: filters?.dateTo || "",
        plant: filters?.plant || "",
        buyer: filters?.buyer || "",
        consignee: filters?.consignee || "",
        country: filters?.country || "",
        grade: filters?.grade || "",
        variety: filters?.variety || "",
        treatment: filters?.treatment || "",
        brand: filters?.brand || "",
        cold_store: filters?.coldStore || "",
        species: filters?.species || "",
        product_type: filters?.productType || "",
        freezing_type: filters?.freezingType || "",
        scope_signature: parityRefreshSignature,
      }, { headers: { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" } });

    request
      .then((r) => {
        if (!cancelled) {
          setData(r.data || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Could not load ERP parity KPI."
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    widget.metric,
    filters?.dateFrom,
    filters?.dateTo,
    filters?.plant,
    filters?.buyer,
    filters?.consignee,
    filters?.country,
    filters?.grade,
    filters?.variety,
    filters?.treatment,
    filters?.brand,
    filters?.coldStore,
    filters?.exporter,
    filters?.species,
    filters?.productType,
    filters?.freezingType,
    stockScopeSignature,
    parityRefreshSignature,
    parityKpiUrl,
    liveRefreshTick,
  ]);

  const independentDeheadingReport = [
    "deheading_input_weight", "weight_after_deheading", "deheading_output_unit1",
  ].includes(widget.metric);

  const openParityDrillDown = async () => {
    if (!drillConfig?.dataset_id || (!independentDeheadingReport && (loading || error))) return;

    const tableWidget = {
      id: `${widget.id}-detail`,
      type: "table",
      title: `${widget.title} — Supporting ERP Records`,
      dataset_id: drillConfig.dataset_id,
      selected_columns:
        drillConfig.selected_columns || undefined,
      limit: 2000,
      filters: [],
    };

    const mergedFilters = [
      ...createCeoFilters(tableWidget, filters || {}),
    ];
    const filteredTableWidget = {
      ...tableWidget,
      filters: mergedFilters,
    };

    setDrill({
      title: `${widget.title} — Supporting ERP Records`,
      dimension: "",
      clickedLabel: "",
      metricField: widget.metric || "",
      metricValue: data?.value,
      aggregation: "",
      payload: {
        widget: filteredTableWidget,
        filters: mergedFilters,
        directData: false,
        specialPagedEndpoint: drillConfig?.detail_endpoint || null,
        specialDetailPayload: {
          metric: widget.metric,
          ...(widget.metric === "total_sales_usd" ? { exporter: filters?.exporter || "" } : {}),
          from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "",
          plant: filters?.plant || "", buyer: filters?.buyer || "",
          consignee: filters?.consignee || "", country: filters?.country || "",
          grade: filters?.grade || "", variety: filters?.variety || "",
          brand: filters?.brand || "", cold_store: filters?.coldStore || "",
          species: filters?.species || "", product_type: filters?.productType || "",
          treatment: filters?.treatment || "", freezing_type: filters?.freezingType || "",
        },
        activeDateRange: {
          from_date: filters?.dateFrom || "",
          to_date: filters?.dateTo || "",
        },
      },
    });

    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);

    try {
      const r = drillConfig?.detail_endpoint
        ? await api.post(drillConfig.detail_endpoint, {
            metric: widget.metric,
            ...(widget.metric === "total_sales_usd" ? { include_exporter_options: true } : {}),
            ...(widget.metric === "total_sales_usd" ? { exporter: filters?.exporter || "" } : {}),
            from_date: filters?.dateFrom || "",
            to_date: filters?.dateTo || "",
            plant: filters?.plant || "",
            buyer: filters?.buyer || "",
            consignee: filters?.consignee || "",
            country: filters?.country || "",
            grade: filters?.grade || "",
            variety: filters?.variety || "",
            brand: filters?.brand || "",
            cold_store: filters?.coldStore || "",
            species: filters?.species || "",
            product_type: filters?.productType || "",
            treatment: filters?.treatment || "",
            freezing_type: filters?.freezingType || "",
          }, { timeout: 60000 })
        : await api.post(
            "/widget/data",
            { widget: filteredTableWidget, filters: mergedFilters },
            { timeout: 35000 }
          );

      setDrillData(r.data);
    } catch (e) {
      setDrillError(
        e?.response?.data?.detail ||
          e?.message ||
          "Unable to load supporting ERP records."
      );
    } finally {
      setDrillLoading(false);
    }
  };

  const clickable = Boolean(
    drillConfig?.dataset_id &&
    (independentDeheadingReport || (!loading && !error))
  );

  return (
    <>
      <div
        className={[
          "ceo-live-widget",
          compact ? "ceo-process-stage-card" : "",
          clickable
            ? "cursor-pointer transition hover:border-slate-400 hover:shadow-md"
            : "",
        ].join(" ")}
        onClick={clickable ? openParityDrillDown : undefined}
        onKeyDown={
          clickable
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openParityDrillDown();
                }
              }
            : undefined
        }
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        title={clickable ? "Click to view supporting ERP records" : undefined}
        data-analytics-target={favoriteTarget || widget.id || widget.target || widget.analyticsTarget}
        data-analytics-target-aliases={[widget.id, widget.target, widget.analyticsTarget].filter(Boolean).join(" ") || undefined}
      >
        <div className={`ceo-live-widget-head ${compact ? "ceo-process-stage-head" : ""}`}>
          <div className="ceo-live-widget-title">{widget.title}</div>
          {favoriteTarget ? (
            <FavoriteButton
              target={favoriteTarget}
              entry={
                analyticsCatalogue.find(
                  (entry) =>
                    entry.favoriteEligible &&
                    entry.target === favoriteTarget &&
                    entry.tab === "overview"
                ) || null
              }
            />
          ) : null}
        </div>

        {/* Same body/value markup as generic "kpi" widgets (WidgetRenderer Kpi) */}
        <div className="ceo-live-widget-body">
          {loading ? (
            <WidgetLoadingState />
          ) : error ? (
            <div className="h-full flex items-center text-sm text-red-600">
              {error}
            </div>
          ) : (
            <div className="h-full flex flex-col justify-center px-1">
              <div
                className="kpi-value"
                style={widget?.metric === "shipment_out_mcs" && compact ? { fontSize: 27 } : undefined}
              >
                {widget?.metric === "shipment_out_mcs" && data?.value !== undefined && data?.value !== null
                  ? Number(data.value).toLocaleString(undefined, { maximumFractionDigits: 2 })
                  : data?.display ?? Number(data?.value || 0).toLocaleString("en-IN")}
              </div>
            </div>
          )}
        </div>
      </div>

      {drill ? (
        <CeoDrillDownModal
          drill={drill}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          reportColumnSelections={reportColumnSelections}
          setReportColumnSelections={setReportColumnSelections}
          onClose={() => {
            setDrill(null);
            setDrillData(null);
            setDrillError("");
          }}
        />
      ) : null}
    </>
  );
}

// One failing widget must not blank the whole page (a render error inside a
// card used to unmount everything). Resets when the filters change.
class CeoErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error("CEO section failed to render:", error);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500 shadow-sm">
        {this.props.title ? <div className="font-semibold text-slate-700">{this.props.title}</div> : null}
        <div className="mt-1">This section could not be displayed for the current selection.</div>
        <button type="button" className="btn btn-outline mt-3" onClick={() => this.setState({ error: null })}>
          Retry
        </button>
      </div>
    );
  }
}

function SmartWidgetCard(props) {
  const resetKey = JSON.stringify(props?.filters || {});
  const widgetKey = `${props?.widget?.id || "widget"}:${resetKey}`;
  const quickAccessTarget =
    props?.widget?.id ||
    props?.widget?.target ||
    props?.widget?.analyticsTarget ||
    "";

  const analyticsTargetAliases = [
    props?.widget?.id,
    props?.widget?.target,
    props?.widget?.analyticsTarget,
  ]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" ");

  return (
    <CeoErrorBoundary resetKey={resetKey} title={props?.widget?.title}>
      <div
        data-analytics-target={quickAccessTarget || undefined}
        data-analytics-target-aliases={analyticsTargetAliases || undefined}
        className="contents"
      >
        {props?.widget?.type === "ceo-parity-kpi"
          ? <ParityKpiCard key={props.widget.metric === "total_sales_usd" ? props.widget.id : widgetKey} {...props} />
          : <WidgetCard key={widgetKey} {...props} />}
      </div>
    </CeoErrorBoundary>
  );
}
// ===== END CAMAROO FINAL ERP PARITY KPI CARD =====



function StockAsOfPanel({ filters }) {
  const nav = useNavigate();
  const [data,setData]=useState(null); const [error,setError]=useState(""); const [page,setPage]=useState(1);
  const signature=JSON.stringify({to:filters?.dateTo||"",grade:filters?.grade||"",variety:filters?.variety||"",coldStore:filters?.coldStore||"",plant:filters?.plant||""});
  useEffect(()=>{ setPage(1); },[signature]);
  useEffect(()=>{ let cancelled=false; setData(null); setError("");
    api.post("/ai/ceo-chat/stock-as-of",{from_date:filters?.dateFrom||"",to_date:filters?.dateTo||"",grade:filters?.grade||"",variety:filters?.variety||"",cold_store:filters?.coldStore||"",plant:filters?.plant||"",page,page_size:50},{timeout:120000})
      .then(r=>{if(!cancelled)setData(r.data);}).catch(e=>{if(!cancelled)setError(e?.response?.data?.detail||e?.message||"Unable to load stock as-of data.");});
    return()=>{cancelled=true;};
  },[signature,page]);
  const fmt=n=>Number(n||0).toLocaleString(undefined,{maximumFractionDigits:2});
  const openStockAsOfDrill=(kind,label,title,mcs,kgs)=>{
    if(!label)return;
    const params=new URLSearchParams({kind,label,title});
    if(mcs!==undefined&&mcs!==null)params.set("mcs",String(mcs));
    if(kgs!==undefined&&kgs!==null)params.set("kgs",String(kgs));
    const values={from:filters?.dateFrom,to:filters?.dateTo,grade:filters?.grade,variety:filters?.variety,cold_store:filters?.coldStore,plant:filters?.plant};
    Object.entries(values).forEach(([key,value])=>{if(value)params.set(key,value);});
    if(kind==="cold_store")params.set("cold_store",label);
    if(kind==="grade")params.set("grade",label);
    if(kind==="plant")params.set("plant",label);
    nav(`/ceo-command-center/physical-barcode-stock?${params.toString()}`);
  };
  const Chart=({title,rows,kind})=>{
    const favoriteTarget = {
      cold_store: "ceo-coldstore-stock",
      grade: "ceo-grade-stock",
      plant: "ceo-stock-by-plant",
      production_date: "ceo-stock-ageing",
    }[kind];

    return <div className="ceo-block">
    <div className="flex items-center justify-between gap-2">
      <div className="ceo-block-title">{title}</div>
      {favoriteTarget ? (
        <FavoriteButton
          target={favoriteTarget}
          entry={
            analyticsCatalogue.find(
              (entry) =>
                entry.favoriteEligible &&
                entry.target === favoriteTarget &&
                entry.tab === "store"
            ) || null
          }
        />
      ) : null}
    </div>
    <div className="ceo-block-subtitle">{data?.basis||""} · total {fmt(data?.total_mcs)} MCS</div>
    <div style={{width:"100%",height:300}}><ResponsiveContainer width="100%" height="100%" initialDimension={{width:320,height:280}}>
      <BarChart data={(rows||[]).slice(0,20)} margin={{top:10,right:20,left:10,bottom:55}} onClick={(state)=>{const label=state?.activeLabel;const row=(rows||[]).find((item)=>String(item.label)===String(label));if(row)openStockAsOfDrill(kind,row.label,title,row.mcs,row.kgs);}}>
        <CartesianGrid strokeDasharray="2 3" stroke="#eef2f7" vertical={false}/>
        <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={80} tick={{fontSize:10}}/>
        <YAxis tick={{fontSize:10}} tickFormatter={v=>Number(v).toLocaleString()}/>
        <Tooltip formatter={(v)=>[fmt(v),"MCS"]}/>
        <Bar dataKey="mcs" name="MCS" fill="#2563eb" radius={[4,4,0,0]} cursor="pointer" />
      </BarChart>
    </ResponsiveContainer></div>
    </div>;
  };
  if(error)return <div className="ceo-block text-sm text-red-600">{error}</div>;
  if(!data)return <div className="ceo-block text-sm text-slate-500">Loading stock as-of data…</div>;
  const reg=data.register||{};
  return <div className="space-y-6">
    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
      <b>{data.basis}</b>. Stock is an as-of metric: the selected <b>To Date</b> is the closing-stock date. From Date does not change the closing balance.
      <span className="ml-2">Reconciled total: <b>{fmt(data.total_mcs)} MCS</b> / <b>{fmt(data.total_kgs)} KGS</b>.</span>
    </div>
    <Grid><Chart title="Cold Store-wise Available Stock" rows={data.by_cold_store} kind="cold_store"/><Chart title="Grade-wise Available Stock" rows={data.by_grade} kind="grade"/></Grid>
    <Grid><Chart title="Plant-wise Available Stock" rows={data.by_plant} kind="plant"/></Grid>
    <div className="ceo-block">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div><div className="ceo-block-title">Stock Register — {data.basis}</div><div className="ceo-block-subtitle">{fmt(reg.total_records)} physical stock rows · page {reg.page} of {reg.total_pages}</div></div>
        <div className="flex gap-2"><button className="px-3 py-1.5 rounded border" disabled={reg.page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>Previous</button><button className="px-3 py-1.5 rounded border" disabled={reg.page>=reg.total_pages} onClick={()=>setPage(p=>p+1)}>Next</button></div>
      </div>
      <div className="overflow-auto"><table className="min-w-full text-xs"><thead><tr>{(reg.columns||[]).map(c=><th key={c} className="text-left p-2 border-b bg-slate-50 whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{(reg.rows||[]).map((r,i)=><tr key={r.StockID||i}>{(reg.columns||[]).map(c=><td key={c} className="p-2 border-b whitespace-nowrap">{typeof r[c]==="number"?fmt(r[c]):String(r[c]??"")}</td>)}</tr>)}</tbody></table></div>
    </div>
  </div>;
}

// This chart shares the backend's canonical Available Stock MCS contract.
// Keeping it on the same endpoint as StockAsOfPanel avoids the generic
// dashboard dataset's separate aggregation drifting from the KPI.
function ColdStoreAvailableStockChart({ filters }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError("");
    api.post("/ai/ceo-chat/stock-as-of", {
      from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "",
      plant: filters?.plant || "", cold_store: filters?.coldStore || "",
      grade: filters?.grade || "", variety: filters?.variety || "",
      brand: filters?.brand || "", page: 1, page_size: 10,
    }, { timeout: 120000 })
      .then((response) => { if (!cancelled) setData(response.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || "Unable to load cold store stock.");
      });
    return () => { cancelled = true; };
  }, [filters?.dateFrom, filters?.dateTo, filters?.plant, filters?.coldStore, filters?.grade, filters?.variety, filters?.brand]);

  const rows = data?.by_cold_store || [];
  const fmt = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const openColdStoreReport = (label, row) => {
    if (!label) return;
    const params = new URLSearchParams({ kind: "cold_store", label, title: "Cold Store-wise Available Stock" });
    const values = { from: filters?.dateFrom, to: filters?.dateTo, grade: filters?.grade, variety: filters?.variety, cold_store: label, plant: filters?.plant };
    Object.entries(values).forEach(([key, value]) => { if (value) params.set(key, value); });
    if (row?.mcs !== undefined && row?.mcs !== null) params.set("mcs", String(row.mcs));
    if (row?.kgs !== undefined && row?.kgs !== null) params.set("kgs", String(row.kgs));
    nav(`/ceo-command-center/physical-barcode-stock?${params.toString()}`);
  };
  return (
    <div className="ceo-block">
      <div className="flex items-center justify-between gap-2">
        <div className="ceo-block-title">Cold Store-wise Available Stock</div>
        <FavoriteButton
          target="ceo-coldstore-stock"
          entry={analyticsCatalogue.find((entry) => entry.favoriteEligible && entry.target === "ceo-coldstore-stock" && entry.tab === "store") || null}
        />
      </div>
      <div className="ceo-block-subtitle">Stock MCS · total {fmt(data?.total_mcs)} MCS</div>
      {error ? <div className="py-8 text-center text-sm text-red-600">{error}</div>
        : !data ? <div className="py-8 text-center text-sm text-slate-500">Loading available stock…</div>
          : <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 280 }}>
              <BarChart data={rows.filter((row) => !row.adjustment)} margin={{ top: 10, right: 20, left: 10, bottom: 55 }} onClick={(state) => { const label = state?.activeLabel; const row = rows.find((item) => !item.adjustment && String(item.label) === String(label)); if (row) openColdStoreReport(row.label, row); }}>
                <CartesianGrid strokeDasharray="2 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" angle={-35} textAnchor="end" interval={0} height={80} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(value) => Number(value).toLocaleString()} />
                <Tooltip formatter={(value) => [fmt(value), "MCS"]} />
                <Bar dataKey="mcs" name="MCS" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>}
    </div>
  );
}


function Grid({ children }) {
  return <div className="ceo-live-grid">{children}</div>;
}

// ERP dashboard tiles, computed by the backend with the ERP's own SQL for the
// selected period (endpoints /ai/ceo-chat/erp-*-dashboard). Reference for ERP parity.
const ERP_TILE_SETS = {
  sales: {
    title: "Sales Dashboard",
    note: "Exact ERP Sales dashboard contract: Pending Contracts is an undated open-order population; On Water, Delivered and payment tiles use the selected customs-invoice period.",
    items: [
      ["Pending Contracts", "pending_contracts"], ["On Water", "on_water"], ["Delivered", "delivered"],
      ["Orders", "orders"], ["Pending Payments", "pending_payments"], ["Payments Received", "payments_received"],
    ],
  },
  coldstore: {
    title: "Cold Store Dashboard",
    note: "Same formulas as the ERP Cold Store dashboard (v3/ColdStore/index.php). Stock and order-status tiles are undated snapshots; the others use the selected period. ERP default period: current calendar month.",
    items: [
      ["Current Stock MCS", "current_stock_mcs"], ["Current Stock KGS", "current_stock_kgs"], ["Available Dummy MCS", "available_dummy_mcs"],
      ["Daily Production Pending MCS", "daily_production_pending_mcs"], ["Inward MCS", "inward_mcs"], ["OutWard MCS", "outward_mcs"],
      ["Shipments", "shipments"], ["Pending Shipments", "pending_shipments"], ["Stock out pending", "stockout_pending"],
      ["Open Orders", "order_status_total"], ["Orders Completed", "order_status_completed"], ["Orders Pending", "order_status_pending"], ["Orders Partial", "order_status_partial"],
    ],
  },
  purchase: {
    title: "RM Purchase Dashboard",
    note: "Same formulas as the ERP RM Purchase home (v3/Purchase/index.php), scoped by bill date. ERP default period: current calendar month.",
    items: [
      ["Total Lots", "total_lots"], ["AB+ Lots", "ab_lots"], ["Return Lots", "return_lots"], ["Border Counts", "border_counts"],
      ["Grading Pendings", "grading_pending"], ["Bill Pendings", "bill_pending"], ["Purchase Quantity (T)", "purchase_qty_t"],
      ["Headless (T)", "headless_t"], ["Hon Packed (T)", "hon_packed_t"], ["Avg Yield %", "avg_yield_pct"], ["Suppliers", "suppliers"],
    ],
  },
  qc: {
    title: "QC Dashboard",
    note: "Antibiotic Report counts distinct lots by analysis date (ERP default: today); the QC preview tiles are undated open orders; the code-status pair follows the ERP monthly chart (invoice date).",
    items: [
      ["Tested Samples (lots)", "antibiotic_tested_lots"], ["Detected Samples (lots)", "antibiotic_detected_lots"],
      ["Total POs (open)", "preview_total_pos"], ["QC Completed", "preview_qc_completed"], ["QC Pending", "preview_qc_pending"],
      ["Internal Samples", "preview_internal_samples"], ["External Samples", "preview_external_samples"], ["Avg Availability %", "preview_avg_availability_pct"],
    ],
  },
};


function QcManagementCharts({ charts, onDrill }) {
  const COLORS = ["#2563eb", "#f59e0b", "#ef4444", "#8b5cf6", "#10b981", "#0ea5e9"];
  const pieCard = (title, rows, clicker) => (
    <div className="ceo-block" key={title}>
      <div className="ceo-block-title">{title}</div>
      <div className="h-64 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            onClick={(state) => {
              const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
              const row = Number.isInteger(index) && rows?.[index]
                ? rows[index]
                : rows?.find((item) => String(item.label) === String(state?.activeLabel));
              if (row) clicker(row.label);
            }}
            style={{ cursor: "pointer" }}
          >
            <Pie data={rows || []} dataKey="value" nameKey="label" innerRadius={58} outerRadius={88}
              paddingAngle={2} cursor="pointer">
              {(rows || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [Number(v).toLocaleString(), n]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {(rows || []).map((r, i) => <button key={r.label} type="button" onClick={() => clicker(r.label)}
          className="text-xs px-2 py-1 rounded border hover:bg-slate-50" title={`Open ${r.label} supporting rows`}>
          <span className="inline-block w-2 h-2 rounded-full mr-1" style={{backgroundColor: COLORS[i % COLORS.length]}} />
          {r.label}: {Number(r.value || 0).toLocaleString()}
        </button>)}
      </div>
    </div>
  );
  const barCard = (title, rows, clicker) => (
    <div className="ceo-block" key={title}>
      <div className="ceo-block-title">{title}</div>
      <div className="h-72 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows || []} margin={{top:10,right:10,left:0,bottom:70}}
            onClick={(state) => {
              const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
              const row = Number.isInteger(index) && rows?.[index]
                ? rows[index]
                : rows?.find((item) => String(item.label) === String(state?.activeLabel));
              if (row) clicker(row.label);
            }}
            style={{ cursor: "pointer" }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" angle={-32} textAnchor="end" interval={0} height={80} tick={{fontSize:10}} />
            <YAxis allowDecimals={false} tick={{fontSize:10}} />
            <Tooltip formatter={(v) => Number(v).toLocaleString()} />
            <Bar dataKey="value" fill="#2563eb" radius={[4,4,0,0]} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
  return <div className="space-y-5 mt-5">
    <Grid>
      {pieCard("QC Code Status", charts?.qc_code_status, (label) => onDrill(`QC ${label}`, label === "Completed" ? "preview_qc_completed" : "preview_qc_pending", {status: label.toLowerCase()}))}
      {pieCard("Lab Coverage", charts?.lab_coverage, (label) => onDrill(`Lab Coverage — ${label}`, "preview_total_pos", {lab_scope: label}))}
      {barCard("Production Readiness", charts?.production_readiness, (label) => onDrill(`Production Readiness — ${label}`, "preview_total_pos", {bucket: label}))}
    </Grid>
    <Grid>
      {barCard("Buyer Pending Risk", charts?.buyer_pending_risk, (label) => onDrill(`Buyer Pending Risk — ${label}`, "preview_qc_pending", {customer: label, status: "pending"}))}
      {barCard("Destination Pending Risk", charts?.destination_pending_risk, (label) => onDrill(`Destination Pending Risk — ${label}`, "preview_qc_pending", {country: label, status: "pending"}))}
    </Grid>
  </div>;
}

function ErpTilesCard({ filters, set, reportColumnSelections, setReportColumnSelections, chartsOnly = false }) {
  const cfg = ERP_TILE_SETS[set];
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [coldStoreParity, setColdStoreParity] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");
  const [localReportColumnSelections, setLocalReportColumnSelections] = useState({});
  const activeReportColumnSelections = reportColumnSelections || localReportColumnSelections;
  const activeSetReportColumnSelections = setReportColumnSelections || setLocalReportColumnSelections;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    const body = { from_date: filters?.dateFrom || undefined, to_date: filters?.dateTo || undefined };
    if (set === "sales" || set === "qc") {
      body.buyer = filters?.buyer || undefined;
      body.consignee = filters?.consignee || undefined;
      body.country = filters?.country || undefined;
    }
    if (set === "sales") body.brand = filters?.brand || undefined;
    if (set === "qc") body.plant = filters?.plant || undefined;

    api.post(`/ai/ceo-chat/erp-${set}-dashboard`, body, { timeout: 60000 })
      .then((r) => { if (!cancelled) { setData(r?.data ?? r); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || "ERP tiles could not be loaded."); });
    return () => { cancelled = true; };
  }, [set, filters?.dateFrom, filters?.dateTo, filters?.buyer, filters?.consignee, filters?.country, filters?.brand, filters?.plant]);

  useEffect(() => {
    if (set !== "coldstore") { setColdStoreParity(null); return undefined; }
    let cancelled = false;
    const base = {
      from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "",
      plant: filters?.plant || "", grade: filters?.grade || "",
      variety: filters?.variety || "", brand: filters?.brand || "",
      cold_store: filters?.coldStore || "",
      species: filters?.species || "", product_type: filters?.productType || "",
      treatment: filters?.treatment || "", freezing_type: filters?.freezingType || "",
    };
    const headers = { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" };
    Promise.all([
      api.post(`/ai/ceo-chat/erp-parity-kpi-v2?scope=${Date.now()}-cold-mcs`, { ...base, metric: "available_stock_mcs" }, { headers }),
      api.post(`/ai/ceo-chat/erp-parity-kpi-v2?scope=${Date.now()}-cold-kgs`, { ...base, metric: "available_stock_kgs" }, { headers }),
    ]).then(([mcs, kgs]) => {
      if (!cancelled) setColdStoreParity({ mcs: mcs?.data?.value, kgs: kgs?.data?.value });
    }).catch(() => { if (!cancelled) setColdStoreParity(null); });
    return () => { cancelled = true; };
  }, [set, filters?.dateFrom, filters?.dateTo, filters?.plant, filters?.coldStore, filters?.grade, filters?.variety, filters?.brand, filters?.species, filters?.productType, filters?.treatment, filters?.freezingType]);

  const t = { ...(data?.tiles || {}) };
  if (set === "coldstore" && coldStoreParity) {
    if (coldStoreParity.mcs !== undefined && coldStoreParity.mcs !== null) t.current_stock_mcs = coldStoreParity.mcs;
    if (coldStoreParity.kgs !== undefined && coldStoreParity.kgs !== null) t.current_stock_kgs = coldStoreParity.kgs;
  }
  const openErpTile = async (label, key, extra = {}) => {
    if (set === "coldstore" && ["current_stock_mcs", "current_stock_kgs"].includes(key)) {
      const metric = key === "current_stock_mcs" ? "available_stock_mcs" : "available_stock_kgs";
      setDrill({
        title: `${label} — ERP Supporting Records`, dimension: "", clickedLabel: "",
        metricField: metric, metricValue: Number(t[key]), aggregation: "",
        payload: {
          directData: false, specialPagedEndpoint: "/ai/ceo-chat/erp-parity-kpi-v2/detail",
          specialDetailPayload: { metric, from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "", plant: filters?.plant || "", grade: filters?.grade || "", variety: filters?.variety || "", brand: filters?.brand || "", cold_store: filters?.coldStore || "" },
          activeDateRange: { from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "" },
        },
      });
      setDrillData(null); setDrillError(""); setDrillLoading(true);
      try {
        const r = await api.post("/ai/ceo-chat/erp-parity-kpi-v2/detail", { metric, from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "", plant: filters?.plant || "", grade: filters?.grade || "", variety: filters?.variety || "", treatment: filters?.treatment || "", brand: filters?.brand || "", cold_store: filters?.coldStore || "" }, { timeout: 60000 });
        setDrillData(r.data);
      } catch (e) { setDrillError(e?.response?.data?.detail || e?.message || "Unable to load ERP supporting records."); }
      finally { setDrillLoading(false); }
      return;
    }
    if (!(set === "sales" || set === "qc") || t[key] === undefined || t[key] === null) return;
    setDrill({
      title: `${label} — ERP Supporting Records`,
      dimension: "",
      clickedLabel: "",
      metricField: set === "qc" ? "PoID" : "PoNumber",
      metricValue: Number(t[key]),
      aggregation: "count_distinct",
      payload: { directData: true, qcDirectData: set === "qc", activeDateRange: { from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "" } },
    });
    setDrillData(null); setDrillError(""); setDrillLoading(true);
    try {
      const endpoint = set === "qc" ? "/ai/ceo-chat/erp-qc-dashboard/detail" : "/ai/ceo-chat/erp-sales-dashboard/detail";
      const body = {
        metric: key, from_date: filters?.dateFrom || "", to_date: filters?.dateTo || "",
        buyer: filters?.buyer || "", consignee: filters?.consignee || "",
        country: filters?.country || "", plant: filters?.plant || "", brand: filters?.brand || "", ...extra,
      };
      const r = await api.post(endpoint, body, { timeout: 60000 });
      setDrillData(r.data);
    } catch (e) {
      setDrillError(e?.response?.data?.detail || e?.message || "Unable to load ERP supporting records.");
    } finally { setDrillLoading(false); }
  };

  if (chartsOnly && set === "qc") {
    return (
      <div className="space-y-5">
        {error ? (
          <div className="ceo-block text-sm text-red-600">{error}</div>
        ) : data?.charts ? (
          <QcManagementCharts charts={data.charts} onDrill={(label, key, extra) => openErpTile(label, key, extra)} />
        ) : (
          <div className="ceo-block text-sm text-slate-500">Loading QC status and risk charts…</div>
        )}
        {drill ? (
          <CeoDrillDownModal
            drill={drill}
            data={drillData}
            loading={drillLoading}
            error={drillError}
            reportColumnSelections={activeReportColumnSelections}
            setReportColumnSelections={activeSetReportColumnSelections}
            onClose={() => { setDrill(null); setDrillData(null); setDrillError(""); }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="ceo-block" data-analytics-target={`erp-${set}-dashboard-tiles`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center justify-between gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="ceo-block-title">{cfg.title}</div>

          <FavoriteButton
            target="ceo-coldstore-dashboard"
            entry={
              analyticsCatalogue.find(
                (entry) =>
                  entry.favoriteEligible &&
                  entry.target === "ceo-coldstore-dashboard" &&
                  entry.tab === "store"
              ) || null
            }
          />
        </div>
        {set === "coldstore" ? (
          <FavoriteButton target="section-store-store-cold-store-control-center" />
        ) : null}
      </div>
        {set === "qc" ? <button type="button" onClick={() => openErpTile("Full QC Preview", "preview_total_pos")}
          className="px-3 py-1.5 rounded-lg border text-sm hover:bg-slate-50" title="Open all ERP QC Management Preview rows">Full ERP QC Preview</button> : null}
      </div>
      {data?.period ? <div className="ceo-block-subtitle">{data.period.from_date || data.period.to_date ? `${formatDateValue(data.period.from_date) || "Beginning"} to ${formatDateValue(data.period.to_date) || "Latest"}` : "All dates"}{set === "qc" ? " · Click any KPI or chart to open its supporting ERP rows" : ""}</div> : null}
      {error ? <div className="text-sm text-red-600 mt-2">{error}</div> : (
        <div className="grid grid-cols-12 gap-4 mt-3">
          {cfg.items.map(([label, key]) => {
            const clickable = set === "sales" || set === "qc" || (set === "coldstore" && ["current_stock_mcs", "current_stock_kgs"].includes(key));
            return (
              <div className="col-span-6 md:col-span-4 xl:col-span-2" key={key}>
                <button type="button" disabled={!clickable}
                  onClick={() => clickable && openErpTile(label, key)}
                  className={`w-full rounded-xl text-left p-2 transition ${clickable ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}
                  title={clickable ? `Open ${label} supporting ERP records` : undefined}>
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-2xl font-semibold mt-1">
                    {t[key] === undefined || t[key] === null ? "…" : Number(t[key]).toLocaleString()}
                  </div>
                  {clickable ? <div className="mt-1 text-[10px] text-slate-400">Click for ERP rows</div> : null}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {set === "qc" && data?.charts ? (
        <QcManagementCharts charts={data.charts} onDrill={(label, key, extra) => openErpTile(label, key, extra)} />
      ) : null}

      {drill ? (
        <CeoDrillDownModal
          drill={drill}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          reportColumnSelections={activeReportColumnSelections}
          setReportColumnSelections={activeSetReportColumnSelections}
          onClose={() => { setDrill(null); setDrillData(null); setDrillError(""); }}
        />
      ) : null}
    </div>
  );
}

function ErpSalesDashboardCard({ filters, reportColumnSelections, setReportColumnSelections }) {
  return <ErpTilesCard filters={filters} set="sales" reportColumnSelections={reportColumnSelections} setReportColumnSelections={setReportColumnSelections} />;
}

function KpiRow({ filters }) {
  return (
    <div className="grid grid-cols-12 gap-4">
      {[W.totalSalesUsd, W.orderedMcs, W.availableMcs, W.receivedQty].map((w) => (
        <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
          <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
        </div>
      ))}
    </div>
  );
}

function PlaceholderBlock({ title, items }) {
  return (
    <div className="ceo-block">
      <div className="ceo-block-title">{title}</div>
      <div className="ceo-block-subtitle">
        This section is prepared for CEO-level analytics. Final calculations require ERP table mapping and business formula confirmation.
      </div>
      <div className="ceo-list">
        {items.map((item) => (
          <div key={item} className="ceo-list-item">
            <CheckCircle2 size={14} strokeWidth={1.8} />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CEOFilterBar({ filters, setFilters, filterOptions }) {
  const set = (patch) => setFilters((current) => ({ ...current, ...patch }));
  const selectedValues = (value) => filterValues(value);

  const applyPreset = (preset) => {
    if (preset === "fy") {
      const r = currentFinancialYearRange();
      set({ dateFrom: r.from, dateTo: r.to, periodMode: "Financial Year" });
    } else if (preset === "cy") {
      const r = currentCalendarYearRange();
      set({ dateFrom: r.from, dateTo: r.to, periodMode: "Calendar Year" });
    } else if (preset === "prev1") {
      const r = previousCalendarYearRange(1);
      set({ dateFrom: r.from, dateTo: r.to, periodMode: "Previous Year" });
    } else if (preset === "clear") {
      setFilters({
        dateFrom: "",
        dateTo: "",
        periodMode: "Custom",
        exporter: "", buyer: "", consignee: "", country: "", grade: "", variety: "", species: "",
        productType: "", treatment: "", freezingType: "", brand: "", coldStore: "", plant: "",
      });
    }
  };

  const activeCount = CEO_GLOBAL_FILTER_KEYS.filter((key) => selectedValues(filters[key]).length > 0).length
    + (filters.dateFrom || filters.dateTo ? 1 : 0);

  const SelectFilter = ({
    label,
    value,
    options = [],
    onChange,
    searchable = true,
  }) => {
    const [search, setSearch] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const wrapperRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const [position, setPosition] = React.useState(null);
    const normalizedOptions = (options || []).map((option) => ({
      value: typeof option === "object" ? option.value : option,
      label: typeof option === "object" ? option.label : option,
    }));

    const selected = selectedValues(value);
    const selectedValue = selected[0] || "";
    const pluralFilterNames = {
      Buyer: "buyers",
      Exporter: "exporters",
      Consignee: "consignees",
      Country: "countries",
      Grade: "grades",
      Variety: "varieties",
      Species: "species",
      "Product Type": "product types",
      Treatment: "treatments",
      "Freezing Type": "freezing types",
      Brand: "brands",
      "Cold Store": "cold stores",
      Plant: "plants",
    };
    const allLabel = `All ${pluralFilterNames[label] || `${label.toLowerCase()}s`}`;

    const searchNeedle = normalizeFilterSearch(search.trim());
    const filteredOptions = normalizedOptions.filter((option) =>
      normalizeFilterSearch(option.label).includes(searchNeedle)
    );

    React.useEffect(() => {
      if (!searchable || !open) {
        setPosition(null);
        return undefined;
      }

      const updatePosition = () => {
        const rect = inputRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPosition({
          top: rect.bottom + 3,
          left: rect.left,
          width: rect.width,
        });
      };

      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      const handleOutsideClick = (event) => {
        if (
          wrapperRef.current?.contains(event.target) ||
          event.target.closest?.(".variety-combobox-options")
        ) return;
        setOpen(false);
        setSearch("");
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }, [searchable, open]);



    if (!searchable) {
      return (
        <div>
          <label>{label}</label>
          <select
            className="select"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">All</option>
            {normalizedOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="variety-combobox" ref={wrapperRef}>
        <label>{label}</label>
        <input
          type="text"
          ref={inputRef}
          className="select variety-combobox-input"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder={allLabel}
      value={open ? search : selectedValue
        ? (normalizedOptions.find((option) => String(option.value ?? "") === selectedValue)?.label ?? selectedValue)
        : ""}
          onClick={() => {
            if (!open) {
              setSearch("");
              setOpen(true);
            }
          }}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setSearch("");
            }
            if (e.key === "Enter" && filteredOptions.length > 0) {
              e.preventDefault();
              const optionValue = String(filteredOptions[0].value ?? "");
              onChange(optionValue);
              setSearch("");
              setOpen(false);
            }
          }}
        />
        {open && position && createPortal(
          <div
            className="variety-combobox-options"
            role="listbox"
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex: 2147483647,
            }}
          >
            <button
              type="button"
              className="variety-combobox-option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange("");
                setSearch("");
                setOpen(false);
              }}
            >
              {allLabel}
            </button>
            {filteredOptions.map((option) => (
              <button
                type="button"
                role="option"
                aria-selected={selectedValue === String(option.value)}
                className="variety-combobox-option"
                key={option.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const optionValue = String(option.value ?? "");
                  onChange(optionValue);
                  setSearch("");
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="variety-combobox-empty">No {pluralFilterNames[label] || label.toLowerCase()} found</div>
            )}
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <div className="ceo-filter-panel">
      <div className="ceo-filter-head">
        <div>
          <div className="ceo-filter-title">CEO Global Filters</div>
        </div>
        <div className="ceo-filter-actions">
          <button type="button" onClick={() => applyPreset("fy")}>Current FY</button>
          <button type="button" onClick={() => applyPreset("cy")}>Current CY</button>
          <button type="button" onClick={() => applyPreset("prev1")}>Previous Year</button>
          <button type="button" onClick={() => applyPreset("clear")}>Clear</button>
        </div>
      </div>

      <div className="ceo-filter-grid">
        <div>
          <label>Date From</label>
          <DateInput className="w-full" value={filters.dateFrom} onChange={(e) => set({ dateFrom: e.target.value, periodMode: "Custom" })} />
        </div>
        <div>
          <label>Date To</label>
          <DateInput className="w-full" value={filters.dateTo} onChange={(e) => set({ dateTo: e.target.value, periodMode: "Custom" })} />
        </div>
        <SelectFilter
          label="Exporter"
          value={filters.exporter}
          options={filterOptions?.salesExporters || []}
          onChange={(value) => set({ exporter: value })}
        />
        <SelectFilter
          label="Buyer"
          value={filters.buyer}
          options={filterOptions?.buyers || []}
          onChange={(value) => set({ buyer: value })}
        />
        <SelectFilter
          label="Consignee"
          value={filters.consignee}
          options={filterOptions?.consignees || []}
          onChange={(value) => set({ consignee: value })}
        />
        <SelectFilter
          label="Country"
          value={filters.country}
          options={filterOptions?.countries || []}
          onChange={(value) => set({ country: value })}
        />
        <SelectFilter
          label="Grade"
          value={filters.grade}
          options={filterOptions?.grades || []}
          onChange={(value) => set({ grade: value })}
        />
        <SelectFilter
          label="Variety"
          value={filters.variety}
          options={filterOptions?.varieties || []}
          onChange={(value) => set({ variety: value })}
          searchable
        />
        <SelectFilter
          label="Species"
          value={filters.species}
          options={filterOptions?.species || []}
          onChange={(value) => set({ species: value })}
        />
        <SelectFilter
          label="Product Type"
          value={filters.productType}
          options={(filterOptions?.productTypes || []).map((value) => ({
            value,
            label: ({ RAW: "Raw", COOKED: "Cooked", BLANCHED: "Blanched" })[String(value).toUpperCase()] || value,
          }))}
          onChange={(value) => set({ productType: value })}
        />
        <SelectFilter
          label="Treatment"
          value={filters.treatment}
          options={filterOptions?.treatments || []}
          onChange={(value) => set({ treatment: value })}
        />
        <SelectFilter
          label="Freezing Type"
          value={filters.freezingType}
          options={filterOptions?.freezingTypes || []}
          onChange={(value) => set({ freezingType: value })}
        />
        <SelectFilter
          label="Brand"
          value={filters.brand}
          options={filterOptions?.brands || []}
          onChange={(value) => set({ brand: value })}
        />
        <SelectFilter
          label="Cold Store"
          value={filters.coldStore}
          options={filterOptions?.coldStores || []}
          onChange={(value) => set({ coldStore: value })}
        />
        <SelectFilter
          label="Plant"
          value={filters.plant}
          options={filterOptions?.plants || []}
          onChange={(value) => set({ plant: value })}
        />
      </div>

      <div className="ceo-filter-footer">
        <span>{filters.periodMode || "Custom"}</span>
        <span>{activeCount} active filter(s)</span>
      </div>
    </div>
  );
}


function DocumentationStatusCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.post("/ai/ceo-chat/documentation-status", {})
      .then((r) => { if (alive) { setData(r.data); setError(""); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e?.message || "Documentation status could not be loaded."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: "var(--c-border)" }} data-analytics-target="po-documentation-status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-900">Entire Documentation — Pending / Completed</div>
          <div className="mt-1 text-sm text-slate-500">PO document checklist from the ERP with explicit completion status where the legacy ERP does not store it.</div>
        </div>
        <button type="button" onClick={() => nav("/ceo-command-center/client-requirements")} className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Manage Business Inputs</button>
      </div>
      {loading ? <div className="mt-4 text-sm text-slate-500">Loading documentation status…</div> : error ? <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["POs Checked", data?.matching_pos ?? 0, "#0f172a"],
            ["Completed", data?.completed_pos ?? 0, "#15803d"],
            ["Pending", data?.pending_pos ?? 0, "#b45309"],
            ["Source", "ERP + Status", "#2563eb"],
          ].map(([label, value, color]) => <div key={label} className="rounded-xl border border-slate-200 p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 text-xl font-semibold" style={{ color }}>{value}</div></div>)}
        </div>
      )}
    </div>
  );
}

export default function CEOCommandCenter() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    return TABS.some((item) => item.id === tab) ? tab : "overview";
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    if (tab && TABS.some((item) => item.id === tab) && tab !== active) {
      setActive(tab);
    }

    if (!tab && active !== "overview") {
      setActive("overview");
    }
  }, [location.search, active]);
  useAnalyticsSearchTarget(active, location);
  const [aiPrompt, setAiPrompt] = useState(CEO_AI_EXAMPLES[0]);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiProvider, setAiProvider] = useState("");
  const [aiError, setAiError] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCreating, setAiCreating] = useState(false);
  const [createdDashboard, setCreatedDashboard] = useState(null);
  // Default period = current financial year: every ERP dashboard opens on the
  // current FY, so an unfiltered CEO page must show the same period, not all-time.
  // Filters survive navigation to detail pages and back (kept per browser tab)
  // until the user changes or clears them.
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(CEO_FILTERS_KEY) || "null");
      if (saved && typeof saved === "object") return normalizeCeoGlobalFilters(saved);
    } catch (e) { /* storage unavailable: fall back to defaults */ }
    const fy = currentFinancialYearRange();
    return {
    dateFrom: fy.from,
    dateTo: fy.to,
    periodMode: "Financial Year",
    exporter: "", buyer: "", consignee: "", country: "", grade: "", variety: "", species: "",
    productType: "", treatment: "", freezingType: "", brand: "", coldStore: "", plant: "",
    };
  });
  const [dummyCartonPeriod, setDummyCartonPeriod] = useState("month");
  const salesFilterScope = React.useMemo(() => ({
    filters,
    setExporter: (exporter) => setFilters((current) => ({ ...current, exporter })),
  }), [filters]);
  const resolvedProcessPlant = resolveProcessPlant(DS.vaPlant, filters);
  const processAliasApplied = Boolean(
    !filterValues(filters.plant).length &&
    filterValues(filters.coldStore).length &&
    filterValues(resolvedProcessPlant).length
  );
  const processFilters = processAliasApplied ? { ...filters, plant: resolvedProcessPlant } : filters;

  const [filterOptions, setFilterOptions] = useState({
    exporters: [],
    buyers: [],
    consignees: [],
    brands: [],
    countries: [],
    grades: [],
    varieties: [],
    species: [],
    productTypes: [],
    treatments: [],
    freezingTypes: [],
    coldStores: [],
    plants: [],
    priority: [],
  });

  // Column selections persist while navigating between CEO modules/drill-downs.
  // null means the report has not been customized yet; [] means the user
  // explicitly deselected every column. Browser refresh resets this state.
  const [reportColumnSelections, setReportColumnSelections] = useState({});

  const openShipmentPerformanceDetail = ({
    scope = "po",
    status = "all",
    customer = "",
  } = {}) => {
    const params = new URLSearchParams();

    params.set("scope", scope);
    params.set("status", status || "all");

    if (customer) {
      params.set("customer", customer);
    }

    if (filters.dateFrom) {
      params.set("from", filters.dateFrom);
    }

    if (filters.dateTo) {
      params.set("to", filters.dateTo);
    }

    nav(
      scope === "fda"
        ? `/ceo-command-center/fda-release-hold?${params.toString()}`
        : scope === "payments"
          ? `/ceo-command-center/payments-received-pending?${params.toString()}`
          : scope === "pending-shipments"
            ? `/ceo-command-center/pending-shipments?${params.toString()}`
            : scope === "variety-analysis"
              ? `/ceo-command-center/variety-analysis?${params.toString()}`
              : scope === "confirmed-orders-production-status"
                ? `/ceo-command-center/confirmed-orders-production-status?${params.toString()}`
                : scope === "production-plant-order-view"
                  ? `/ceo-command-center/production-plant-order-view?${params.toString()}`
                  : scope === "species-analysis"
                    ? `/ceo-command-center/species-analysis?${params.toString()}`
                    : scope === "headon-vs-finished-plant"
                      ? `/ceo-command-center/headon-vs-finished-plant?${params.toString()}`
                      : scope === "po-day-code-status"
                        ? `/ceo-command-center/po-day-code-status?${params.toString()}`
                        : scope === "sales-price-analysis"
                          ? `/ceo-command-center/sales-price-analysis?${params.toString()}`
                          : scope === "order-delivery-status"
                            ? `/ceo-command-center/order-delivery-status?${params.toString()}`
                            : scope === "under-production-by-buyer"
                              ? `/ceo-command-center/under-production-by-buyer?${params.toString()}`
                              : scope === "headon-cy-vs-fy"
                                ? `/ceo-command-center/headon-cy-vs-fy?${params.toString()}`
                                : scope === "dummy-stock-pending-contracts"
                                  ? `/ceo-command-center/dummy-stock-pending-contracts?${params.toString()}`
                                : scope === "rm-purchase-value"
                                  ? `/ceo-command-center/rm-purchase-value?${params.toString()}`
                                : scope === "purchase-quality-by-variety"
                                  ? `/ceo-command-center/purchase-quality-by-variety?${params.toString()}`
                                : scope === "supplier-antibiotic-positive"
                                  ? `/ceo-command-center/supplier-antibiotic-positive?${params.toString()}`
                                : scope === "batch-antibiotic-history"
                                  ? `/ceo-command-center/batch-antibiotic-history?${params.toString()}`
                                  : `/ceo-command-center/shipment-performance?${params.toString()}`
    );
  };

  useEffect(() => {
    let cancelled = false;

    api.get("/ceo/filter-options")
      .then((res) => {
        PLANT_ALIASES = res.data?.plantAliases || {};
        if (cancelled) return;
        const opts = res.data || {};
        setFilterOptions(opts);
        // A remembered value that is no longer offered would show as "All"
        // in its dropdown while still filtering every card to nothing.
        const listFor = {
          exporter: opts.salesExporters,
          buyer: opts.buyers, consignee: opts.consignees, country: opts.countries, grade: opts.grades,
          variety: opts.varieties, species: opts.species, productType: opts.productTypes, treatment: opts.treatments, freezingType: opts.freezingTypes,
          brand: opts.brands, coldStore: opts.coldStores, plant: opts.plants,
        };
        setFilters((current) => {
          let changed = false;
          const next = { ...current };
          Object.entries(listFor).forEach(([key, list]) => {
            const values = (list || []).map((o) => (typeof o === "object" ? o.value : o));
            if (next[key] && values.length && !values.includes(next[key])) {
              next[key] = "";
              changed = true;
            }
          });
          return changed ? next : current;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFilterOptions({
            exporters: [],
            buyers: [],
            consignees: [],
            brands: [],
            countries: [],
            grades: [],
            varieties: [],
            species: [],
            productTypes: [],
            treatments: [],
            freezingTypes: [],
            coldStores: [],
            plants: [],
            priority: [],
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Publish the active filter scope so the globally-mounted Ask AI drawer can
  // answer for the period shown on screen instead of all-time.
  useEffect(() => {
    setAskAiContext(ceoFiltersToAskAiContext(filters));
    return () => setAskAiContext(null);
  }, [filters]);

  useEffect(() => {
    try { sessionStorage.setItem(CEO_FILTERS_KEY, JSON.stringify(filters)); } catch (e) { /* ignore */ }
  }, [filters]);

  const previewAiPlan = async () => {
    const text = aiPrompt.trim();
    if (text.length < 8) {
      setAiError("Please enter a clearer CEO analytics prompt.");
      return;
    }

    setAiError("");
    setCreatedDashboard(null);
    setAiLoading(true);

    try {
      const r = await api.post("/ai/analytics-agent/plan", { prompt: text });
      setAiPlan(r.data.plan);
      setAiProvider(r.data.provider || "");
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || "Failed to generate AI plan.");
    } finally {
      setAiLoading(false);
    }
  };

  const createAiDashboard = async () => {
    const text = aiPrompt.trim();
    if (text.length < 8) {
      setAiError("Please enter a clearer CEO analytics prompt.");
      return;
    }

    setAiError("");
    setAiCreating(true);

    try {
      const r = await api.post("/ai/analytics-agent/create-dashboard", { prompt: text });
      setAiPlan(r.data.plan);
      setAiProvider(r.data.provider || "");
      setCreatedDashboard(r.data.dashboard);
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message || "Failed to create AI dashboard.");
    } finally {
      setAiCreating(false);
    }
  };

  const FAVORITE_WIDGET_TARGET_ALIASES = {
    "ceo-total-sales-usd": "ceo-parity-total_sales_usd",
    "ceo-ordered-mcs": "ceo-parity-ordered_mcs",
    "ceo-purchase-weight": "ceo-parity-rm_purchase_weight",
    "ceo-received-qty": "ceo-parity-received_qty",
    "ceo-shipment-out-mcs": "ceo-parity-shipment_out_mcs",
    "ceo-freezing-production-weight": "ceo-parity-freezing_weight",
    "ceo-daily-actual-mcs": "ceo-parity-packed_mcs",
    "ceo-sales-mcs": "ceo-parity-sales_mcs",
    "ceo-sales-invoice-count": "ceo-parity-invoices",
    "ceo-commercial-sales-usd": "ceo-parity-total_sales_usd",
    "ceo-deheading-output-weight": "ceo-parity-weight-after-deheading",
    "ceo-dispatched-mcs": "ceo-parity-dispatched_mcs",
    "ceo-available-mcs": "ceo-parity-available-stock-mcs",
    "ceo-available-kgs": "ceo-parity-available-stock-kgs",
  };

  const renderFavoriteAnalytics = (entry) => {
    if (!entry) return null;
    return (
      <CeoErrorBoundary resetKey={JSON.stringify(filters)} title={entry.title}>
        {renderFavoriteAnalyticsInner(entry)}
      </CeoErrorBoundary>
    );
  };

  const renderFavoriteAnalyticsInner = (entry) => {

    const resolvedTarget =
      FAVORITE_WIDGET_TARGET_ALIASES[entry.target] || entry.target;

    const widget = Object.values(W).find(
      item => item.id === resolvedTarget
    );
    if (widget) return <SmartWidgetCard widget={widget} filters={filters} dummyCartonPeriod={dummyCartonPeriod} setDummyCartonPeriod={setDummyCartonPeriod} reportColumnSelections={reportColumnSelections} setReportColumnSelections={setReportColumnSelections} />;
    switch (entry.id) {
      case "store:ceo-coldstore-dashboard":
        return (
          <FavoriteAnalyticsShell entry={entry}>
            <ErpTilesCard filters={filters} set="coldstore" />
          </FavoriteAnalyticsShell>
        );

      case "overview:section-overview-erp-operational-flow":
        return <FavoriteAnalyticsShell entry={entry}><div className="space-y-6"><div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <Factory size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-erp-operational-flow">ERP Operational Flow</div>
                  <div className="ceo-block-subtitle">
                    End-to-end operational path from raw material purchase to shipment.
                  </div>
                </div>
              </div>

              <div className="ceo-process-flow-shell">
                <div className="ceo-process-flow-grid">
                  {[
                    { step: "01", label: "RM Purchase", widget: W.purchaseWeight },
                    { step: "02", label: "Receiving", widget: W.receivedQty },
                    { step: "03", label: "Deheading", widget: W.deheadingOutputWeight },
                    { step: "04", label: "Value Addition", widget: W.vaOutputWeight },
                    { step: "05", label: "Soaking", widget: W.soakOutQty },
                    { step: "06", label: "Freezing", widget: W.freezingProductionWeight },
                    { step: "07", label: "Packing", widget: W.dailyActualMcs },
                    { step: "08", label: "Cold Store", widget: W.availableMcs },
                    { step: "09", label: "PO Demand", widget: W.orderedMcs },
                    { step: "10", label: "Shipment", widget: W.shipmentOutMcs },
                  ].map((stage) => (
                    <div className="ceo-process-stage-wrap" key={stage.step}>
                      <SmartWidgetCard
                        widget={stage.widget}
                        filters={filters}
                        compact
                        reportColumnSelections={reportColumnSelections}
                        setReportColumnSelections={setReportColumnSelections}
                      />
                    </div>
                  ))}
                </div>

              </div></div></FavoriteAnalyticsShell>;
      case "overview:section-overview-species-analysis-vannamei-vs-black-tiger":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-overview-species-analysis-vannamei-vs-black-tiger">
                  Species Analysis — Vannamei vs Black Tiger
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  How the business splits between the two shrimp species we process, on one shared definition across shipped sales and the confirmed order book, with a species filter for drill-down.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "species-analysis",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Species Split
                </button>
              </div></FavoriteAnalyticsShell>;
      case "overview:card-eufarmcertificatescard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-eufarmcertificatescard"><EuFarmCertificatesCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "overview:card-executiveattentionqueue":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-executiveattentionqueue"><ExecutiveAttentionQueue filters={filters} /></div></FavoriteAnalyticsShell>;
      case "purchase:card-rmarrivalsbycountcard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-rmarrivalsbycountcard"><RmArrivalsByCountCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "purchase:section-purchase-suppliers-linked-to-antibiotic-positive-batches":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-purchase-suppliers-linked-to-antibiotic-positive-batches">
                  Suppliers Linked to Antibiotic-Positive Batches
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Every screened raw-material lot traced back to the supplier it was bought from, so suppliers whose batches returned a positive antibiotic reading can be identified with their affected lots, antibiotics found, purchase centre and receiving plant.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "supplier-antibiotic-positive",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Flagged Suppliers
                </button>
              </div></FavoriteAnalyticsShell>;
      case "purchase:section-purchase-raw-material-purchase-value-in-lakhs-crores":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-purchase-raw-material-purchase-value-in-lakhs-crores">
                  Raw Material Purchase Value — in Lakhs &amp; Crores
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  What we paid for raw material, in rupees, with totals in crores and individual arrivals in lakhs, by month, plant and purchase variety, reconciled to the bills the ERP recorded.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "rm-purchase-value",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Purchase Value
                </button>
              </div></FavoriteAnalyticsShell>;
      case "commercial:section-commercial-selling-price-invoice-value-by-currency":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-commercial-selling-price-invoice-value-by-currency">
                  Selling Price &amp; Invoice Value — by Currency
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  The price we are selling at per pound or per kilo, and what each invoice is worth, with every figure kept in the currency it was invoiced in so dollars are never mixed with rupees or euros.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "sales-price-analysis",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Prices
                </button>
              </div></FavoriteAnalyticsShell>;
      case "commercial:card-salevsactualpricecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-salevsactualpricecard"><SaleVsActualPriceCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "commercial:card-stockfinanceexposurecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-stockfinanceexposurecard"><StockFinanceExposureCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "commercial:section-commercial-variety-analysis":
        return <FavoriteAnalyticsShell entry={entry}><div
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm cursor-pointer transition hover:border-slate-400 hover:shadow-md"
                  role="button"
                  tabIndex={0}
                  title="Open Variety Analysis"
                  onClick={() => openShipmentPerformanceDetail({ scope: "variety-analysis", status: "all", customer: "" })}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openShipmentPerformanceDetail({ scope: "variety-analysis", status: "all", customer: "" }); } }}
                >
                  <div className="mt-2 text-lg font-semibold text-slate-900" data-analytics-target="section-commercial-variety-analysis">
                    Variety Analysis
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    Commercial variety mix measured by master cartons (MCS).
                  </div>
                  <button
                    type="button"
                    className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                    onClick={(e) => e.stopPropagation() ||
                      openShipmentPerformanceDetail({
                        scope: "variety-analysis",
                        status: "all",
                        customer: "",
                      })
                    }
                  >
                    Open Variety Analysis
                  </button>
                </div></FavoriteAnalyticsShell>;
      case "po:section-po-day-codes-assigned-vs-not-assigned":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-po-day-codes-assigned-vs-not-assigned">
                  Day Codes — Assigned vs Not Assigned
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Which orders already have their carton day code assigned through the quality code list, and which are still waiting, so the orders that cannot be coded yet can be chased.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "po-day-code-status",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Day Codes
                </button>
              </div></FavoriteAnalyticsShell>;
      case "po:card-buyerordercountscard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-buyerordercountscard"><BuyerOrderCountsCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "store:section-store-dummy-cartons-on-active-pending-contracts":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-store-dummy-cartons-on-active-pending-contracts">
                  Dummy Cartons on Active Pending Contracts
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Dummy-packed cartons in cold store that are tied to a contract which still has quantity to deliver, alongside dummy stock on fulfilled contracts and stock with no contract at all.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "dummy-stock-pending-contracts",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Dummy Stock
                </button>
              </div></FavoriteAnalyticsShell>;
      case "store:card-consumablesusagecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-consumablesusagecard"><ConsumablesUsageCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "store:card-packingmaterialcontractscard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-packingmaterialcontractscard"><PackingMaterialContractsCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "store:card-chemicalstockcard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-chemicalstockcard"><ChemicalStockCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:section-production-head-on-vs-finished-product-by-plant":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-production-head-on-vs-finished-product-by-plant">
                  Head-On vs Finished Product — by Plant
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Compares how much whole (head-on) raw material each plant took in against the finished frozen product that came out of that same material, so unit-by-unit conversion can be seen side by side.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "headon-vs-finished-plant",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Conversion
                </button>
              </div></FavoriteAnalyticsShell>;
      case "production:card-finishedproductvolumecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-finishedproductvolumecard"><FinishedProductVolumeCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:card-floorbalancecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-floorbalancecard"><FloorBalanceCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:card-chemicalusedondatecard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-chemicalusedondatecard"><ChemicalUsedOnDateCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "production:section-production-confirmed-orders-under-production-vs-yet-to-start":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mt-2 text-lg font-semibold text-slate-900" data-analytics-target="section-production-confirmed-orders-under-production-vs-yet-to-start">
                  Confirmed Orders — Under Production vs Yet to Start
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Splits the confirmed order book still in hand into orders that
                  have actually started production and orders with no production
                  recorded yet, and ranks the not-started orders by how close
                  they are to their shipment deadline.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "confirmed-orders-production-status",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Orders
                </button>
              </div></FavoriteAnalyticsShell>;
      case "production:section-production-under-production-by-buyer":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-production-under-production-by-buyer">
                  Under Production — by Buyer
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  For each buyer, how many of their confirmed open orders have started production and how many are still waiting to start — the same rule as the Confirmed Orders view, broken out by buyer.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "under-production-by-buyer",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View by Buyer
                </button>
              </div></FavoriteAnalyticsShell>;
      case "production:section-production-orders-by-production-plant":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mt-2 text-lg font-semibold text-slate-900" data-analytics-target="section-production-orders-by-production-plant">
                  Orders by Production Plant
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Shows the orders assigned to each production plant, including
                  buyer, order quantity, production status, and plant-level
                  order drill-down.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "production-plant-order-view",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Orders by Plant
                </button>
              </div></FavoriteAnalyticsShell>;
      case "shipment:card-shippromptnessbyplantcard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-shippromptnessbyplantcard"><ShipPromptnessByPlantCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "shipment:card-yearlydispatchcomparisoncard":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-yearlydispatchcomparisoncard"><YearlyDispatchComparisonCard filters={filters} /></div></FavoriteAnalyticsShell>;
      case "shipment:section-shipment-delivered-orders":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-shipment-delivered-orders">
                  Delivered Orders
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Which orders have reached the customer, which have shipped but are still awaiting delivery, and which have not left yet — using the same delivered rule as the ERP orders-by-buyer screen.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "order-delivery-status",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Deliveries
                </button>
              </div></FavoriteAnalyticsShell>;
      case "shipment:card-shipmentperformancematrix":
        return <FavoriteAnalyticsShell entry={entry}><div data-analytics-target="card-shipmentperformancematrix"><ShipmentPerformanceMatrix
                filters={filters}
                onOpenDetail={openShipmentPerformanceDetail}
              /></div></FavoriteAnalyticsShell>;
      case "qc:section-qc-batch-antibiotic-testing-status-retest-history":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-qc-batch-antibiotic-testing-status-retest-history">
                  Batch Antibiotic Testing — Status &amp; Retest History
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Every screened raw-material batch with its antibiotic status, how many times it was tested, and the full test-by-test history — a retest is added to the record, never written over the earlier result.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "batch-antibiotic-history",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Batch History
                </button>
              </div></FavoriteAnalyticsShell>;
      case "qc:section-qc-suppliers-linked-to-antibiotic-positive-batches":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-qc-suppliers-linked-to-antibiotic-positive-batches">
                  Suppliers Linked to Antibiotic-Positive Batches
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Every screened raw-material lot traced back to the supplier it was bought from, so suppliers whose batches returned a positive antibiotic reading can be identified with their affected lots, antibiotics found, purchase centre and receiving plant.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "supplier-antibiotic-positive",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Flagged Suppliers
                </button>
              </div></FavoriteAnalyticsShell>;
      case "qc:section-qc-quality-at-purchase-by-purchase-variety":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-qc-quality-at-purchase-by-purchase-variety">
                  Quality at Purchase — by Purchase Variety
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Antibiotic screening outcomes on raw material lots, grouped by the variety recorded when the lot was bought, so quality at the point of purchase can be compared variety by variety.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "purchase-quality-by-variety",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Purchase Quality
                </button>
              </div></FavoriteAnalyticsShell>;
      case "comparisons:section-comparisons-head-on-volume-calendar-year-vs-financial-year":
        return <FavoriteAnalyticsShell entry={entry}><div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-lg font-semibold text-slate-900" data-analytics-target="section-comparisons-head-on-volume-calendar-year-vs-financial-year">
                  Head-On Volume — Calendar Year vs Financial Year
                </div>
                <div className="mt-2 max-w-4xl text-sm text-slate-600">
                  Total head-on volume by calendar year and by financial year (April to March), side by side, so the two year definitions can be compared without mixing them.
                </div>
                <button
                  type="button"
                  className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "headon-cy-vs-fy",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  View Comparison
                </button>
              </div></FavoriteAnalyticsShell>;
      default: return null;
    }
  };

  return (
    <CeoSalesFilterContext.Provider value={salesFilterScope}>
    <div className="erp-home-shell ceo-analytics-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content ceo-analytics-content">
        <div className="erp-page-title-row ceo-title-row">
          <div>
            {isRestricted(user) && CEO_TAB_INFO[active] ? (
              <>
                <h1>{CEO_TAB_INFO[active][0]}</h1>
                <p>{CEO_TAB_INFO[active][1]}</p>
              </>
            ) : (
              <>
                <h1>CEO Command Center</h1>
                <p>Executive analytics across sales, purchase, QA, process, store, PO, dispatch, master search, and reports.</p>
              </>
            )}
          </div>

          <button
            type="button"
            className="btn btn-outline whitespace-nowrap"
            title="Fetch the latest KPI, chart and table values from the connected ERP database"
            onClick={() => {
              window.dispatchEvent(
                new Event("camaroo:ceo-refresh-kpis")
              );
            }}
          >
            Refresh Analytics
          </button>
        </div>

        <div className="ceo-erp-content space-y-5">
          <CEOFilterBar filters={filters} setFilters={setFilters} filterOptions={filterOptions} />
          <CeoErrorBoundary resetKey={`${active}|${JSON.stringify(filters)}`}>

          {active === "quick-access" && <QuickAccessPanel renderAnalytics={renderFavoriteAnalytics} />}

          {active === "overview" && (
            <div className="space-y-6" data-analytics-target="tab-overview">

              {renderFavoriteAnalytics(analyticsById["overview:section-overview-species-analysis-vannamei-vs-black-tiger"])}

              {/* ======================================================
                  1. EXECUTIVE PULSE
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <LayoutDashboard size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-pulse">Executive Pulse</div>
                  <div className="ceo-block-subtitle">
                    CEO-level snapshot of commercial demand, procurement, production, inventory and customer dispatch activity.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[
                  W.totalSalesUsd,
                  W.orderedMcs,
                  W.availableMcs,
                  W.availableKgs,
                  W.purchaseWeight,
                  W.receivedQty,
                  W.shipmentOutMcs,
                  W.shipmentOutKgs,
                ].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>


              {renderFavoriteAnalytics(analyticsById["overview:card-eufarmcertificatescard"])}


              {/* ======================================================
                  2. EXECUTIVE RISK & EXCEPTIONS
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <AlertTriangle size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-risk-exceptions">Executive Risk & Exceptions</div>
                  <div className="ceo-block-subtitle">
                    Operational conditions requiring management attention.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[
                  W.qcDetectedTests,
                  W.poCriticalRisk,
                  W.poWarningRisk,
                  W.shipmentPendingRisk,
                ].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>


              {/* ======================================================
                  3. BUSINESS CONTRIBUTION & DISTRIBUTION
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <BarChart3 size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-business-contribution-distribution">Business Contribution & Distribution</div>
                  <div className="ceo-block-subtitle">
                    Buyer, destination, grade, inventory-location and dispatch-location
                    contribution for the selected CEO filter context.
                  </div>
                </div>
              </div>

              <Grid>
                <div className="ceo-buyer-sales-full-row">
                  <SmartWidgetCard widget={W.dashboardCustomerSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
                <SmartWidgetCard widget={W.destinationSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.gradeSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <ColdStoreAvailableStockChart filters={filters} />
                <SmartWidgetCard widget={W.shipmentByStore} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>


              {/* ======================================================
                  4. EXECUTIVE TRENDS
              ====================================================== */}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon">
                  <BarChart3 size={18} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-overview-executive-trends">Executive Trends</div>
                  <div className="ceo-block-subtitle">
                    Time-based movement across sales, procurement, production and
                    customer shipment activity.
                  </div>
                </div>
              </div>

              <Grid>
                <SmartWidgetCard widget={W.salesTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.purchaseTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.productionTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.shipmentTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>


              {/* ======================================================
                  5. ERP OPERATIONAL FLOW
              ====================================================== */}
              {renderFavoriteAnalytics(analyticsById["overview:section-overview-erp-operational-flow"])}

            </div>
          )}

          {active === "buyer" && (
            <div className="space-y-6" data-analytics-target="tab-buyer">
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-sales-customer-performance">Sales & Customer Performance</div>
                  <div className="ceo-block-subtitle">
                    Invoice-line sales analysis by customer, destination, grade, variety, brand and year.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.totalSalesUsd, W.salesMcs, W.salesInvoiceCount, W.salesConsigneeCount].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-1-sales-trend-annual-performance">1. Sales Trend & Annual Performance</div>
                  <div className="ceo-block-subtitle">
                    Sales movement by invoice date, year-wise USD sales value and each year's percentage contribution.
                  </div>
                </div>
              </div>

              <Grid>
                <SmartWidgetCard widget={W.salesTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.salesYearComparison} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.salesAnnualContribution} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-2-customer-market-contribution">2. Customer & Market Contribution</div>
                  <div className="ceo-block-subtitle">
                    Buyer, consignee and destination contribution.
                  </div>
                </div>
              </div>

              <Grid>
                <SmartWidgetCard widget={W.buyerSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.consigneeSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.destinationSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-3-product-mix">3. Product Mix</div>
                  <div className="ceo-block-subtitle">
                    Sales contribution by grade, brand and variety.
                  </div>
                </div>
              </div>

              <Grid>
                <SmartWidgetCard widget={W.gradeSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.brandSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.varietySales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-buyer-4-invoice-sales-drill-down">4. Invoice & Sales Drill-down</div>
                  <div className="ceo-block-subtitle">
                    Invoice, PO, customer, product, shipment and shipping-bill level detail.
                  </div>
                </div>
              </div>

              <SmartWidgetCard widget={W.salesTable} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
            </div>
          )}

          {active === "purchase" && (
            <div className="space-y-5" data-analytics-target="tab-purchase">

              {renderFavoriteAnalytics(analyticsById["purchase:card-rmarrivalsbycountcard"])}

              {renderFavoriteAnalytics(analyticsById["purchase:section-purchase-suppliers-linked-to-antibiotic-positive-batches"])}

              {renderFavoriteAnalytics(analyticsById["purchase:section-purchase-raw-material-purchase-value-in-lakhs-crores"])}
              <div className="grid grid-cols-12 gap-4">
                {[W.purchaseWeight, W.purchaseLotCount].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-4" key={w.id}>
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>

              <Grid>
                <SmartWidgetCard widget={W.purchaseSupplierWeight} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.purchaseCentreWeight} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.purchaseStationWeight} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.purchaseLotStatus} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>

              <SmartWidgetCard widget={W.purchaseTable} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
            </div>
          )}

          {active === "commercial" && (
            <div className="space-y-5" data-analytics-target="tab-commercial">

              {renderFavoriteAnalytics(analyticsById["commercial:section-commercial-selling-price-invoice-value-by-currency"])}

              {renderFavoriteAnalytics(analyticsById["commercial:card-salevsactualpricecard"])}

              {renderFavoriteAnalytics(analyticsById["commercial:card-stockfinanceexposurecard"])}

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-commercial-commercial-finance-control-center">
                    Commercial / Finance Control Center
                  </div>
                  <div className="ceo-block-subtitle">
                    Commercial sales, collections, payment realization,
                    pending payments, shipment exposure and buyer-wise
                    commercial analysis.
                  </div>
                </div>
              </div>

              <div
                className="bg-white border rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3"
                style={{ borderColor: "var(--c-border)" }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">
                  Commercial Views
                </span>

                <button
                  type="button"
                  className="px-4 py-2 rounded-xl border bg-slate-900 text-white text-sm font-medium"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "payments",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  Payments Received / Pending
                </button>

                <button
                  type="button"
                  className="px-4 py-2 rounded-xl border bg-slate-900 text-white text-sm font-medium"
                  onClick={() =>
                    openShipmentPerformanceDetail({
                      scope: "pending-shipments",
                      status: "all",
                      customer: "",
                    })
                  }
                >
                  Pending Shipments
                </button>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.commercialSalesUsd, W.commercialUtilizedValue, W.commercialBankCharges].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <SmartWidgetCard widget={W.commercialPendingShipments} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <SmartWidgetCard widget={W.commercialPaymentsReceivedPending} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
              </div>
              <Grid>
                <SmartWidgetCard widget={W.commercialBuyerSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.commercialDestinationSales} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.commercialCollectionRate} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                {renderFavoriteAnalytics(analyticsById["commercial:section-commercial-variety-analysis"])}
                <SmartWidgetCard widget={W.commercialAntidumping} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.commercialTable} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              <SmartWidgetCard widget={W.commercialPaymentTable} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
            </div>
          )}

          {active === "po" && (
            <div className="space-y-6" data-analytics-target="tab-po">

              {renderFavoriteAnalytics(analyticsById["po:section-po-day-codes-assigned-vs-not-assigned"])}
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-po-po-fulfilment-risk-control-center">PO Fulfilment Control Center</div>
                  <div className="ceo-block-subtitle">
                    Order demand and dispatched quantity with supporting PO analysis for the selected period.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.orderedMcs, W.dispatchedMcs].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>

              {renderFavoriteAnalytics(analyticsById["po:card-buyerordercountscard"])}

              <SmartWidgetCard widget={W.poTable} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
            </div>
          )}

          {active === "store" && (
            <div className="space-y-6" data-analytics-target="tab-store">

              {renderFavoriteAnalytics(analyticsById["store:section-store-dummy-cartons-on-active-pending-contracts"])}

              {renderFavoriteAnalytics(analyticsById["store:card-consumablesusagecard"])}
              {renderFavoriteAnalytics(analyticsById["store:card-packingmaterialcontractscard"])}
              {renderFavoriteAnalytics(analyticsById["store:card-chemicalstockcard"])}
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-store-cold-store-control-center">Store / Cold Store Control Center</div>
                  <div className="ceo-block-subtitle">
                    Physical barcode stock, cold-store location, ageing, PO-linked stock, packing state, buyer exposure, and contract-state visibility.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.availableMcs, W.availableKgs].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>

              <Grid>
                <ColdStoreAvailableStockChart filters={filters} />
              </Grid>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-1-physical-barcode-stock">1. Physical Barcode Stock</div>
                  <div className="ceo-block-subtitle">
                    Current available stock by cold store, grade, production date, rack location, barcode, packing style, and PO link.
                  </div>
                </div>
              </div>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-store-2-stock-transfers-by-plant">2. Stock Transfers by Plant</div>
                  <div className="ceo-block-subtitle">
                    Stock transfer volume grouped by source plant and ranked from least to highest MCS.
                  </div>
                </div>
              </div>

              <Grid>
                <SmartWidgetCard widget={W.stockTransfersByPlant} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>

              {false && (
                <>
                  <div className="ceo-module-heading">
                    <div>
                      <div className="ceo-block-title" data-analytics-target="section-store-2-po-contract-linked-stock">2. PO / Contract-linked Stock</div>
                      <div className="ceo-block-subtitle">
                        Stock linked to buyer and PO, with contract state and pending-order quantity.
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4">
                    {[W.dummyStockMcs, W.dummyStockKgs, W.poPendingMcsStore].map((w) => (
                      <div className="col-span-12 md:col-span-4" key={w.id}>
                        <SmartWidgetCard widget={w} filters={filters}
                          reportColumnSelections={reportColumnSelections}
                          setReportColumnSelections={setReportColumnSelections} />
                      </div>
                    ))}
                  </div>

                  <Grid>
                    <SmartWidgetCard widget={W.stockByPackingType} filters={filters}
                      reportColumnSelections={reportColumnSelections}
                      setReportColumnSelections={setReportColumnSelections} />
                    <SmartWidgetCard widget={W.stockByContractState} filters={filters}
                      reportColumnSelections={reportColumnSelections}
                      setReportColumnSelections={setReportColumnSelections} />
                    <SmartWidgetCard widget={W.stockByPlant} filters={filters}
                      reportColumnSelections={reportColumnSelections}
                      setReportColumnSelections={setReportColumnSelections} />
                    <SmartWidgetCard widget={W.stockByBuyer} filters={filters}
                      reportColumnSelections={reportColumnSelections}
                      setReportColumnSelections={setReportColumnSelections} />
                    <SmartWidgetCard widget={W.stockByPo} filters={filters}
                      reportColumnSelections={reportColumnSelections}
                      setReportColumnSelections={setReportColumnSelections} />
                  </Grid>

                  <SmartWidgetCard widget={W.poLinkedStockTable} filters={filters}
                    reportColumnSelections={reportColumnSelections}
                    setReportColumnSelections={setReportColumnSelections} />

                  <SmartWidgetCard widget={W.stockReadyLabAwaiting} filters={filters}
                    reportColumnSelections={reportColumnSelections}
                    setReportColumnSelections={setReportColumnSelections} />
                </>
              )}
            </div>
          )}

          {active === "production" && (
            <div className="space-y-6" data-analytics-target="tab-production">
              {processAliasApplied ? (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  Process filter resolved: Cold Store <strong>{filters.coldStore}</strong> maps to Production Plant <strong>{resolvedProcessPlant}</strong>. Select Plant explicitly to override this mapping.
                </div>
              ) : null}

              {renderFavoriteAnalytics(analyticsById["production:section-production-head-on-vs-finished-product-by-plant"])}

              {renderFavoriteAnalytics(analyticsById["production:card-finishedproductvolumecard"])}
              {renderFavoriteAnalytics(analyticsById["production:card-floorbalancecard"])}
              <div className="ceo-module-heading">
                <div className="ceo-module-icon"><Factory size={18} strokeWidth={1.8} /></div>
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-process-production-control-center">Process / Production Control Center</div>
                  <div className="ceo-block-subtitle">
                    Production flow from receiving through deheading, value addition, soaking, freezing, packing and dummy/master packing.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <SmartWidgetCard widget={W.receivedQty} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                  <SmartWidgetCard widget={W.freezingProductionWeight} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
              </div>

              {renderFavoriteAnalytics(analyticsById["production:section-production-confirmed-orders-under-production-vs-yet-to-start"])}

              {renderFavoriteAnalytics(analyticsById["production:section-production-under-production-by-buyer"])}

              {renderFavoriteAnalytics(analyticsById["production:section-production-orders-by-production-plant"])}

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-1-receiving-preprocessing">1. Receiving & Preprocessing</div>
                  <div className="ceo-block-subtitle">
                    Production inward receiving by plant (Actual MCS). Cold Store takes precedence over Plant when both are selected.
                  </div>
                </div>
              </div>
              <Grid>
                <SmartWidgetCard widget={W.plantProduction} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.productionTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-2-deheading-headless-processing">2. Deheading / Headless Processing</div>
                  <div className="ceo-block-subtitle">
                    Weight received for deheading, weight after deheading and loose shell weight by plant.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[
                  W.deheadingReceivedWeight,
                  W.deheadingOutputWeight,
                  // W.looseShellWeight,
                ].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>
              <Grid>
                <SmartWidgetCard widget={W.deheadingByPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.deheadingTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-3-value-addition">3. Value Addition</div>
                  <div className="ceo-block-subtitle">
                    ERP Value Addition Report: HL input, VA output and yield from PpcValueAddition. Floor balance below is separate WIP and is not VA input.
                  </div>
                </div>
              </div>
              <SmartWidgetCard widget={W.vaPercentByPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              <Grid>
                <SmartWidgetCard widget={W.vaByPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.vaByVariety} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.vaTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />

              <div className="mt-5">
                <FloorBalanceCard filters={processFilters} />
              </div>

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-4-soaking">4. Soaking</div>
                  <div className="ceo-block-subtitle">
                    Soaking input and output quantities by plant, treatment, grade and variety.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[
                  W.soakInQty,
                  // W.soakOutQty, // Temporarily hidden from the Soaking KPI row.
                ].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>
              <Grid>
                <SmartWidgetCard widget={W.soakingByPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.soakingByTreatment} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.soakingPickupByItemPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              <SmartWidgetCard widget={W.soakingTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-5-freezing">5. Freezing</div>
                  <div className="ceo-block-subtitle">
                    Production and rejection weight by plant and freezing type, with record-level production traceability.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.freezingProductionWeight, W.freezingRejectionWeight].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>
              <FreezingLifecycleCard filters={processFilters} />
              <Grid>
                <SmartWidgetCard widget={W.freezingByType} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.freezingByPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.freezingTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />

              {false && <>
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-6-daily-production-packing">6. Daily Production & Packing</div>
                  <div className="ceo-block-subtitle">
                    Finished packed production by plant, grade, variety, product form, freezing type, PO, and production type.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.dailyActualKgs].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>
              <Grid>
                <SmartWidgetCard widget={W.productionByPlantValidated} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.productionByFreezingType} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              </>}
              <SmartWidgetCard widget={W.dailyProductionTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-production-7-dummy-vs-master-packing">6. Dummy vs Master Packing</div>
                  <div className="ceo-block-subtitle">
                    Dummy and master carton production by plant.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {[W.dummyMcs, W.masterMcs].map((w) => (
                  <div className="col-span-12 md:col-span-6 xl:col-span-3" key={w.id}>
                    <SmartWidgetCard widget={w} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                  </div>
                ))}
              </div>
              <Grid>
                <SmartWidgetCard widget={W.packingTypeDistribution} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.packingByPlant} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
              <SmartWidgetCard widget={W.packingTable} filters={processFilters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              <SmartWidgetCard widget={W.dummyCartonUsageByPeriod} filters={processFilters} dummyCartonPeriod={dummyCartonPeriod} setDummyCartonPeriod={setDummyCartonPeriod} />
            </div>
          )}

          {active === "shipment" && (
            <div className="space-y-6" data-analytics-target="tab-shipment">

              {renderFavoriteAnalytics(analyticsById["shipment:card-shippromptnessbyplantcard"])}

              {renderFavoriteAnalytics(analyticsById["shipment:card-yearlydispatchcomparisoncard"])}

              {renderFavoriteAnalytics(analyticsById["shipment:section-shipment-delivered-orders"])}

              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-shipment-dispatch-control-center">
                    Shipment / Dispatch Control Center
                  </div>
                  <div className="ceo-block-subtitle">
                    Shipment progress from the order's ship-by date through stock-out to shipment completion.
                  </div>
                </div>
              </div>


              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-shipment-execution-volume">
                    Shipment Execution Volume
                  </div>
                  <div className="ceo-block-subtitle">
                    Stock-out volume and movement for the selected period.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[W.shipmentOutMcs, W.shipmentOutKgs].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <SmartWidgetCard
                      widget={w}
                      filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections}
                    />
                  </div>
                ))}
              </div>

              <Grid>
                <SmartWidgetCard
                  widget={W.shipmentByStore}
                  filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections}
                />
                <SmartWidgetCard
                  widget={W.shipmentTrend}
                  filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections}
                />
              </Grid>


              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-delivery-regulatory-performance">
                    Delivery & Regulatory Performance
                  </div>
                  <div className="ceo-block-subtitle">
                    Delivery timing, on-water shipments and FDA holds.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                {[
                  W.onTimeDeliveries,
                  W.earlyShipments,
                  W.delayedShipments,
                  W.onWaterShipments,
                  W.fdaReleaseHold,
                ].map((w) => (
                  <div
                    className="col-span-12 md:col-span-6 xl:col-span-3"
                    key={w.id}
                  >
                    <SmartWidgetCard
                      widget={w}
                      filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections}
                    />
                  </div>
                ))}
              </div>

              {renderFavoriteAnalytics(analyticsById["shipment:card-shipmentperformancematrix"])}


              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-shipment-shipment-stock-out-drill-down">
                    Shipment / Stock-Out Drill-down
                  </div>
                  <div className="ceo-block-subtitle">
                    Detailed batch, cold-store, rack, PO, grade and stock-out transaction records.
                  </div>
                </div>
              </div>

              <SmartWidgetCard
                widget={W.shipmentTable}
                filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections}
              />

            </div>
          )}

          {active === "qc" && (
            <div className="space-y-5" data-analytics-target="tab-qc">

              {renderFavoriteAnalytics(analyticsById["qc:section-qc-batch-antibiotic-testing-status-retest-history"])}

              {renderFavoriteAnalytics(analyticsById["qc:section-qc-suppliers-linked-to-antibiotic-positive-batches"])}

              {renderFavoriteAnalytics(analyticsById["qc:section-qc-quality-at-purchase-by-purchase-variety"])}
              <ErpTilesCard filters={filters} set="qc" chartsOnly />

              <div className="ceo-module-heading mt-6">
                <div><div className="ceo-block-title">Antibiotic / Lab Test Evidence</div><div className="ceo-block-subtitle">Test-record and lot-level evidence from QcRmAbReport. These counts are intentionally separate from the open-PO QC Management Preview above.</div></div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-4">
                  <SmartWidgetCard widget={W.qcTotalTests} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <SmartWidgetCard widget={W.qcClearTests} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <SmartWidgetCard widget={W.qcDetectedTests} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                </div>
              </div>

              <Grid>
                <SmartWidgetCard widget={W.qcResultDistribution} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.qcDetectedByPlant} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.qcDetectedByCentre} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.qcParameterVolume} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.qcExternalResult} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>

              <SmartWidgetCard widget={W.qcTable} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
            </div>
          )}


          {active === "comparisons" && (
            <div className="space-y-5" data-analytics-target="tab-comparisons">

              {renderFavoriteAnalytics(analyticsById["comparisons:section-comparisons-head-on-volume-calendar-year-vs-financial-year"])}
              <Grid>
                <SmartWidgetCard widget={W.salesTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.poTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.productionTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.stockTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.shipmentTrend} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
                <SmartWidgetCard widget={W.priceComparisonOverYears} filters={filters}
                reportColumnSelections={reportColumnSelections}
                setReportColumnSelections={setReportColumnSelections} />
              </Grid>
            </div>
          )}

          {active === "master" && (
            <div className="space-y-5" data-analytics-target="tab-master">
              <div className="ceo-module-heading">
                <div>
                  <div className="ceo-block-title" data-analytics-target="section-master-master-search-field-catalogue">Master Search &amp; Field Catalogue</div>
                  <div className="ceo-block-subtitle">
                    Search across buyers, suppliers, lots, orders, invoices, varieties, plants, centres, countries, brands, species, day codes and farms.
                  </div>
                </div>
              </div>
              <MasterSearchPanel embedded />
            </div>
          )}
          {active === "ai" && (
            <div className="space-y-5" data-analytics-target="tab-ai">
              <div className="ceo-block">
                <div className="flex items-start gap-3">
                  <div className="ceo-section-icon"><Brain size={18} strokeWidth={1.8} /></div>
                  <div>
                    <div className="ceo-block-title" data-analytics-target="section-ai-ai-ask-ceo-dashboard">AI Ask CEO Dashboard</div>
                    <div className="ceo-block-subtitle">
                      Ask the AI Agent to create dashboards and reports from the ERP analytics data.
                    </div>
                  </div>
                </div>

                <div className="ceo-ai-builder">
                  <div className="ceo-ai-left">
                    <label className="ceo-ai-label">CEO Prompt</label>
                    <textarea
                      className="ceo-ai-textarea"
                      rows="7"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Example: Create a CEO dashboard for buyer wise sales, stock availability, PO fulfilment, production received quantity, shipment trend, and QC table."
                    />

                    {aiError && (
                      <div className="ceo-ai-error">
                        <AlertTriangle size={15} strokeWidth={1.8} />
                        <span>{aiError}</span>
                      </div>
                    )}

                    <div className="ceo-ai-actions">
                      <button type="button" className="btn btn-outline" onClick={previewAiPlan} disabled={aiLoading || aiCreating}>
                        {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} strokeWidth={1.7} />}
                        Preview Plan
                      </button>
                      <button type="button" className="btn btn-primary" onClick={createAiDashboard} disabled={aiLoading || aiCreating}>
                        {aiCreating ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} strokeWidth={1.7} />}
                        Create Dashboard
                      </button>
                      {createdDashboard && (
                        <button type="button" className="btn btn-outline" onClick={() => nav(`/dashboards/${createdDashboard.id}`)}>
                          <LayoutDashboard size={14} strokeWidth={1.7} />
                          Open Created Dashboard
                        </button>
                      )}
                    </div>

                    {createdDashboard && (
                      <div className="ceo-ai-success">
                        Created: <strong>{createdDashboard.name}</strong>
                      </div>
                    )}
                  </div>

                  <div className="ceo-ai-right">
                    <div className="ceo-ai-panel-title">CEO-ready prompt examples</div>
                    <div className="ceo-ai-prompts compact">
                      {CEO_AI_EXAMPLES.map((prompt) => (
                        <button
                          type="button"
                          key={prompt}
                          className="ceo-ai-prompt"
                          onClick={() => {
                            setAiPrompt(prompt);
                            setAiPlan(null);
                            setCreatedDashboard(null);
                            setAiError("");
                          }}
                        >
                          <BarChart3 size={14} strokeWidth={1.8} />
                          <span>{prompt}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="ceo-block">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="ceo-block-title" data-analytics-target="section-ai-ai-generated-plan">AI Generated Plan</div>
                    <div className="ceo-block-subtitle">
                      Review what AI will create before saving it as a dashboard.
                    </div>
                  </div>
                  {aiProvider && <span className="ceo-provider-pill">{aiProvider === "openai" ? "OpenAI" : "Fallback"}</span>}
                </div>

                {!aiPlan ? (
                  <div className="ceo-ai-empty">
                    <Brain size={24} strokeWidth={1.6} />
                    <div className="mt-2 font-medium">No AI plan generated yet</div>
                    <div className="mt-1 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
                      Type a CEO analytics request and click Preview Plan.
                    </div>
                  </div>
                ) : (
                  <div className="ceo-ai-plan">
                    <div className="ceo-ai-plan-header">
                      <div>
                        <div className="ceo-ai-plan-title">{aiPlan.dashboard_name}</div>
                        <div className="ceo-ai-plan-desc">{aiPlan.description}</div>
                      </div>
                      <div className="ceo-ai-dataset">
                        <span>Dataset</span>
                        <strong>{aiPlan.dataset_name || aiPlan.dataset_id}</strong>
                      </div>
                    </div>

                    {aiPlan.filters?.length > 0 && (
                      <div>
                        <div className="ceo-ai-panel-title mt-4">Filters</div>
                        <div className="ceo-ai-filter-list">
                          {aiPlan.filters.map((f) => (
                            <span key={`${f.field}-${f.type}`}>{f.label || f.field} · {f.type}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="ceo-ai-panel-title mt-4">Widgets</div>
                    <div className="ceo-ai-widget-list">
                      {(aiPlan.widgets || []).map((w, idx) => (
                        <div key={`${w.title}-${idx}`} className="ceo-ai-widget-row">
                          <div className="ceo-ai-widget-type">{w.type}</div>
                          <div>
                            <div className="ceo-ai-widget-title">{w.title}</div>
                            <div className="ceo-ai-widget-meta">
                              {w.dimension && <span>Dimension: {w.dimension}</span>}
                              {w.measure && <span>Measure: {w.measure}</span>}
                              {w.aggregation && <span>Aggregation: {w.aggregation}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {aiPlan.notes?.length > 0 && (
                      <div className="ceo-ai-notes">
                        {aiPlan.notes.map((n, idx) => <div key={idx}>• {n}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          </CeoErrorBoundary>
        </div>
      </main>
    </div>
    </CeoSalesFilterContext.Provider>
  );
}
